//fleetx-security- crud/web-admin/src/pages/Bookings.jsx
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "../api/axios";
import { API_ORIGIN } from "../api/apiConfig";
import AdminLoadingOverlay from "../components/AdminLoadingOverlay";
import AdminShell from "../components/AdminShell";
import { ReturnConditionEditor, ReturnConditionSummary, ReturnSettlementSummary, ReturnChargeConfirmation, getIssueChargeFields, planIssueChargeChange } from "../components/ReturnConditionAssessment";
import ExtensionReviewModal from "../components/ExtensionReviewModal";
import { useAuth } from "../context/AuthContext";
import {
  BOOKING_STATUS_FILTERS,
  getBookingStatusClass,
  getBookingStatusLabel,
  getBookingStatusMeta,
} from "../utils/bookingStatusDisplay";
import {
  buildLocalDateTime,
  calculateRentalPricing,
  getVehicleRate24Hr,
} from "../utils/rentalPricing";
import { buildFinancialSummary } from "../utils/financialSummary";
import {
  getReturnArrangementLabel,
  getReturnPickupFeeStatusLabel,
} from "../utils/returnArrangement";
import {
  getTodayString,
  getMinTimeForDate,
  validateAdminSchedule,
} from "../utils/bookingTimeHelpers";
import GooglePlacesAutocomplete from "../components/GooglePlacesAutocomplete";
import {
  COUNTRY_CODE_OPTIONS,
  combineInternationalContact,
  getContactNumberError,
  getCountryOptionLabel,
  getMaxLocalPhoneDigits,
  inferContactParts,
  normalizeLocalPhoneNumberForCountry,
} from "../utils/phoneValidation";
import {
  LUGGAGE_HELPER_TEXT,
  LUGGAGE_SIZE_OPTIONS,
  formatLuggageDetails,
  getLuggageDetailLines,
  normalizeLuggageSize,
} from "../utils/luggageDetails";
import {
  getCompactVehicleLabel,
  getVehicleDetailLine,
  getVehicleIdentifierText,
  getVehicleName,
  getVehicleOptionLabel,
  getVehicleSeatCount,
} from "../utils/vehicleDisplay";
import {
  getBookingRouteDataReadiness,
  updateBookingRouteField,
} from "../utils/bookingRouteReadiness";
import "../styles/admin.css";

function formatDate(value) {
  if (!value) return "N/A";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "N/A";
  return parsed.toISOString().slice(0, 10);
}

function formatDateTime(value) {
  if (!value) return "N/A";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "N/A";
  return parsed.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: "Asia/Manila",
  });
}

function formatPeso(value) {
  return `PHP ${Math.round(Number(value || 0)).toLocaleString()}`;
}

function getPaymentMethodKind(booking = {}) {
  const category = String(booking.selectedPaymentCategory || "").toLowerCase();
  const method = String(booking.selectedPaymentMethodName || booking.paymentMethod || "").toLowerCase();
  const combined = `${category} ${method}`;
  if (combined.includes("deposit")) return "bank_deposit";
  if (
    combined.includes("e-wallet") ||
    combined.includes("wallet") ||
    combined.includes("gcash") ||
    combined.includes("maya")
  ) {
    return "e_wallet";
  }
  if (
    combined.includes("bank") ||
    combined.includes("transfer") ||
    combined.includes("bpi") ||
    combined.includes("bdo") ||
    combined.includes("china")
  ) {
    return "online_banking";
  }
  return "payment_method";
}

function getPaymentProviderLabel(booking = {}) {
  const raw = String(booking.selectedPaymentMethodName || booking.paymentMethod || "").trim();
  const normalized = raw.toLowerCase();
  if (normalized === "china bank") return "China Banking Corporation";
  if (normalized === "china bank savings") return "China Bank Savings";
  return raw || "Not selected yet";
}

function getPaymentProofTypeLabel(booking = {}) {
  const kind = getPaymentMethodKind(booking);
  if (kind === "bank_deposit") return "Deposit slip proof";
  if (kind === "online_banking") return "Transfer confirmation proof";
  if (kind === "e_wallet") return "E-wallet receipt proof";
  return "Payment proof";
}

const PAYMENT_DEADLINE_STATUSES = new Set([
  "awaiting_payment",
  "pending_payment",
  "payment_rejected",
]);

function getPaymentDeadlineStatus(booking, now) {
  if (!booking?.paymentDueAt) return null;

  const normalizedStatus = String(booking.status || "").toLowerCase();
  if (!PAYMENT_DEADLINE_STATUSES.has(normalizedStatus)) return null;

  const dueDate = new Date(booking.paymentDueAt);
  const dueTime = dueDate.getTime();
  if (Number.isNaN(dueTime)) return null;

  const remainingMs = dueTime - now;
  if (remainingMs <= 0) {
    return { label: "Expired deadline", tone: "danger" };
  }

  const totalSeconds = Math.floor(remainingMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const timeLabel =
    days > 0
      ? `${days}d ${hours}h ${minutes}m`
      : `${hours}h ${minutes}m ${seconds}s`;

  return {
    label: `Expires in ${timeLabel}`,
    tone: remainingMs <= 60 * 60 * 1000 ? "danger" : remainingMs <= 24 * 60 * 60 * 1000 ? "warning" : "ok",
  };
}

function PaymentDeadlineBadge({ booking, now, compact = false }) {
  const deadline = getPaymentDeadlineStatus(booking, now);
  if (!deadline) return null;

  return (
    <span
      className={`payment-deadline-badge payment-deadline-badge--${deadline.tone} ${
        compact ? "payment-deadline-badge--compact" : ""
      }`}
    >
      {deadline.label}
    </span>
  );
}

function paymentBadgeClass(status) {
  const normalized = String(status || "").toLowerCase();

  if (["verified", "fully_paid"].includes(normalized)) return "badge badge-approved";
  if (normalized === "downpayment_paid") return "badge badge-pending";
  if (["rejected", "expired"].includes(normalized)) return "badge badge-rejected";
  return "badge badge-pending";
}

function prettyStatus(status) {
  return String(status || "")
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function settlementStatusLabel(status) {
  const labels = {
    unpaid: "Not paid yet",
    paid_to_admin: "Paid to admin / face-to-face",
    paid_online_pending_verification: "Paid online, pending verification",
  };
  return labels[String(status || "").toLowerCase()] || prettyStatus(status);
}

function settlementMethodLabel(method) {
  const labels = {
    cash: "Cash at office / Face-to-face",
    gcash: "GCash",
    bank_transfer: "Bank Transfer",
    online_banking: "Online Banking",
    other: "Other",
  };
  return labels[String(method || "").toLowerCase()] || prettyStatus(method);
}

function getPaymentOptionLabel(value) {
  if (value === "down_payment_50") return "50% Down Payment";
  if (value === "full_payment") return "Full Payment";
  return "Not specified";
}

function hasDetailValue(value) {
  if (value === null || value === undefined) return false;
  const text = String(value).trim();
  return Boolean(text) && !["N/A", "n/a", "not_required", "0", "PHP 0"].includes(text);
}

function hasPositiveAmount(value) {
  return Number(value || 0) > 0;
}

function hasLegacyDocumentPayload(value) {
  if (!value) return false;
  if (typeof value === "string") return Boolean(value.trim());
  if (typeof value === "object") return Object.keys(value).length > 0;
  return true;
}

function safeDisplay(value, fallback = "N/A") {
  if (value === null || value === undefined) return fallback;
  if (typeof value === "object") return fallback;
  const text = String(value).trim();
  return text ? text : fallback;
}

function getArchivedByLabel(booking = {}) {
  const archivedBy = booking.archivedBy;
  if (!archivedBy) return "N/A";
  if (typeof archivedBy === "string") return archivedBy;
  return archivedBy.name || archivedBy.email || archivedBy.username || "N/A";
}

function getAdminVehicleName(booking) {
  if (!booking?.vehicleId) return "N/A";
  return getVehicleName(booking.vehicleId, "N/A");
}

function getVehicleDisplayName(vehicle) {
  if (!vehicle) return "No vehicle selected";
  return getVehicleName(vehicle);
}

function getVehicleImageSrc(vehicle) {
  const image = Array.isArray(vehicle?.images) && vehicle.images.length ? vehicle.images[0] : vehicle?.image;
  return getUploadUrl(image || "");
}

function getVehiclePassengerLimit(vehicle, rentalType) {
  const seats = getVehicleSeatCount(vehicle);
  if (seats <= 0) return 0;
  return Math.max(1, String(rentalType || "").toLowerCase() === "with_driver" ? seats - 1 : seats);
}

function getBookingSourceLabel(booking) {
  return String(booking?.source || "online").toLowerCase() === "manual" ? "Manual" : "Online";
}

function isInvoiceIssued(booking) {
  return Boolean(
    booking?.invoiceReference ||
      booking?.invoiceNumber ||
      booking?.invoiceIssuedAt ||
      booking?.invoiceUrl ||
      hasLegacyDocumentPayload(booking?.invoiceData)
  );
}

function isReceiptIssued(booking) {
  return Boolean(
    booking?.receiptNumber ||
      booking?.receiptIssuedAt ||
      booking?.receiptUrl ||
      hasLegacyDocumentPayload(booking?.receiptData)
  );
}

function isRefundRelevant(booking) {
  const status = String(booking?.refundStatus || "not_required").toLowerCase();
  return status && status !== "not_required";
}

function hasMoreBookingDetails(booking) {
  return Boolean(
    booking ||
      booking?.createdBy ||
      booking?.accountSetupRequired ||
      booking?.userId?.accountSetupRequired ||
      booking?.invoiceReference ||
      hasDetailValue(booking?.paymentReference) ||
      hasDetailValue(booking?.selectedPaymentCategory) ||
      hasPositiveAmount(booking?.amountPaid) ||
      hasPositiveAmount(booking?.remainingBalance) ||
      String(booking?.paymentOption || "").includes("down") ||
      booking?.contractAccepted ||
      isRefundRelevant(booking) ||
      hasDetailValue(booking?.adjustmentReason) ||
      hasDetailValue(booking?.invoiceNotes) ||
      hasDetailValue(booking?.notes)
  );
}

function canApproveBooking(status) {
  return [
    "pending_approval",
    "pending_booking_review",
    "pending_admin_review",
    "pending_review",
    "booking_review",
    "submitted",
    "request_submitted",
    "under_booking_review",
  ].includes(String(status || "").toLowerCase());
}

function canRejectBooking(status) {
  return !["rejected", "cancelled", "completed"].includes(String(status || "").toLowerCase());
}

function canModifyBooking(booking) {
  const status = String(booking?.status || "").toLowerCase();
  if (!["approved", "confirmed", "booked_confirmed"].includes(status)) return false;
  const startDate = formatDate(booking?.startDate);
  const startTime = booking?.startTime || "00:00";
  const startsAt = new Date(`${startDate}T${startTime}`);
  if (!Number.isNaN(startsAt.getTime()) && startsAt <= new Date()) return false;
  const trackingStatus = String(booking?.tracking?.status || "").toLowerCase();
  return !booking?.tracking?.isActive && !["on_route", "out_of_the_way"].includes(trackingStatus);
}

const ACTIVE_RENTAL_STATUSES = ["active", "ongoing"];

function isActiveRental(booking) {
  return ACTIVE_RENTAL_STATUSES.includes(String(booking?.status || "").toLowerCase());
}

function canManageExtension(booking) {
  return ["approved", "confirmed", "booked_confirmed", "active", "ongoing", "payment_verified"].includes(
    String(booking?.status || "").toLowerCase()
  );
}

function canAssessReturn(booking) {
  const status = String(booking?.status || "").toLowerCase();
  return (
    ["active", "ongoing"].includes(status) &&
    !booking?.returnAssessment?.assessedAt
  );
}

function hasPendingExtensionRequest(booking) {
  return (
    String(booking?.extensionStatus || "").toLowerCase() === "pending" ||
    (booking?.extensionRequests || []).some(
      (request) => String(request.status || "").toLowerCase() === "pending"
    )
  );
}

function hasPaymentAdjustmentRequired(booking) {
  if (String(booking?.status || "").toLowerCase() === "completed") return false;
  return String(booking?.paymentAdjustmentStatus || "").toLowerCase() === "additional_payment_required";
}

function hasCompletedOutstandingBalance(booking) {
  return String(booking?.status || "").toLowerCase() === "completed" &&
    getFinancialAmount(
      getFinancialSummaryForBooking(booking),
      "finalBalance",
      booking?.remainingBalance
    ) > 0;
}

function getAdditionalPaymentStatus(booking) {
  return String(booking?.additionalPayment?.status || "").toLowerCase();
}

function hasReviewableAdditionalPayment(booking) {
  if (String(booking?.status || "").toLowerCase() === "completed" &&
      !hasCompletedOutstandingBalance(booking)) return false;
  return getAdditionalPaymentStatus(booking) === "under_review" &&
    Boolean(booking?.additionalPayment?.proofUrl);
}

function hasAdditionalPaymentActionRequired(booking) {
  const payment = booking?.additionalPayment || {};
  if (String(booking?.status || "").toLowerCase() === "completed" &&
      !hasCompletedOutstandingBalance(booking)) return false;
  // Match backend statsRoutes additionalPaymentsQuery:
  // required=true AND status in [awaiting_payment, under_review, rejected].
  return (
    Boolean(payment.required) &&
    ["awaiting_payment", "under_review", "rejected"].includes(
      getAdditionalPaymentStatus(booking)
    )
  );
}

function getAdditionalPaymentReasonLabel(reason) {
  const labels = {
    extension: "Approved extension",
    late_return: "Late return",
    penalty: "Penalty",
    return_assessment: "Return assessment",
    manual_adjustment: "Manual adjustment",
  };
  return labels[String(reason || "").toLowerCase()] || "Additional charge";
}

function getAdditionalPaymentAmounts(booking) {
  const payment = booking?.additionalPayment || {};
  if (booking?.returnAssessment?.assessedAt &&
      (String(booking?.status || "").toLowerCase() === "completed" ||
        ["return_assessment", "penalty"].includes(String(payment.reason || "")))) {
    const summary = getFinancialSummaryForBooking(booking);
    return {
      totalAdditionalAmount: Number(payment.totalAdditionalAmount || 0),
      outstandingBalanceAmount: getFinancialAmount(summary, "remainingBookingBalance", 0),
      totalAmountDue: getFinancialAmount(summary, "finalBalance", 0),
    };
  }
  const totalAdditionalAmount = Number(payment.totalAdditionalAmount || 0);
  const computedOutstandingBalance = Math.max(
    0,
    Number(booking?.totalPrice || 0) -
      totalAdditionalAmount -
      Number(booking?.amountPaid || 0)
  );
  const outstandingBalanceAmount = Math.max(
    Number(payment.outstandingBalanceAmount || 0),
    computedOutstandingBalance
  );
  const totalAmountDue = Math.max(
    Number(payment.totalAmountDue || 0),
    totalAdditionalAmount + outstandingBalanceAmount
  );
  return { totalAdditionalAmount, outstandingBalanceAmount, totalAmountDue };
}

const DOCUMENT_TYPE_LABELS = {
  booking_invoice: "Booking Invoice",
  payment_receipt: "Payment Receipt",
  completion_receipt: "Completion Receipt",
};

const PAYMENT_TYPE_LABELS = {
  down_payment: "Down Payment",
  full_payment: "Full Payment",
  remaining_balance: "Remaining Balance",
  additional_charge: "Additional Charge",
};

function getDocumentTypeLabel(type) {
  return DOCUMENT_TYPE_LABELS[String(type || "").toLowerCase()] || prettyStatus(type || "document");
}

function getDocumentPaymentTypeLabel(type) {
  const normalized = String(type || "").toLowerCase();
  return PAYMENT_TYPE_LABELS[normalized] || (normalized ? prettyStatus(normalized) : "");
}

function getFinancialSummaryForBooking(booking = {}) {
  return booking.financialSummary || buildFinancialSummary(booking);
}

function getFinancialAmount(summary = {}, key, fallback = 0) {
  const value = Number(summary[key]);
  return Number.isFinite(value) ? value : Number(fallback || 0);
}

function getBillingChargeRows(booking = {}, summary = {}) {
  const approvedExtensions = [...(booking.extensionRequests || [])]
    .filter((request) => request.status === "approved")
    .sort((a, b) => new Date(a.reviewedAt || a.requestedAt || 0) - new Date(b.reviewedAt || b.requestedAt || 0));
  const includedExtensionTotal = approvedExtensions.reduce((total, request) =>
    request.pricing?.version === 2 ? total + Math.max(0, Number(request.additionalAmount || 0)) : total, 0);
  const grandTotal = getFinancialAmount(summary, "grandTotal", booking.totalPrice);
  const originalAmount = Number(approvedExtensions[0]?.oldTotal) > 0
    ? Number(approvedExtensions[0].oldTotal)
    : Math.max(0, grandTotal - includedExtensionTotal);
  const rows = [{
    key: "original-booking",
    label: "Original booking",
    description: `${getVehicleName(booking.vehicleId) || "Vehicle rental"} (${formatDate(booking.startDate)} to ${formatDate(booking.endDate)})`,
    amount: originalAmount,
  }];
  approvedExtensions
    .filter((request) => Number(request.additionalAmount || 0) > 0)
    .forEach((request, index) => {
      rows.push({ key: String(request._id || index), label: `Extension #${index + 1}`, description: "Approved extension", amount: Number(request.additionalAmount) });
    });
  const charges = summary.returnCharges || buildFinancialSummary(booking).returnCharges || {};
  const separatelyListedExtensionTotal = approvedExtensions.reduce((total, request) =>
    request.pricing?.version === 2 ? total : total + Math.max(0, Number(request.additionalAmount || 0)), 0);
  const legacyExtensionTotal = Math.max(Number(charges.extensionFee || 0), separatelyListedExtensionTotal);
  if (legacyExtensionTotal > separatelyListedExtensionTotal) {
    rows.push({ key: "legacy-extension-charge", label: "Extension charge", description: "Approved extension", amount: legacyExtensionTotal - separatelyListedExtensionTotal });
  }
  [
    ["Late return", charges.lateFee],
    ["Damage fee", charges.damageFee],
    ["Fuel adjustment", charges.fuelAdjustment],
    ["Cleaning fee", charges.cleaningFee],
    ["Penalty / manual adjustment", charges.otherFee],
  ].forEach(([label, amount]) => {
    if (Number(amount || 0) > 0) rows.push({ key: label, label, description: "Return assessment", amount: Number(amount) });
  });
  if (booking.returnAssessment?.assessedAt && !rows.some((row) => row.description === "Return assessment")) {
    rows.push({ key: "return-assessment", label: "Return assessment", description: "No additional charges", amount: 0 });
  }
  return rows;
}

function getBillingPaymentRows(booking = {}, summary = {}) {
  const rows = [];
  const currentPayment = booking.additionalPayment || {};
  const initialStatus = String(booking.paymentStatus || "not_submitted").toLowerCase();
  const paid = getFinancialAmount(summary, "totalPaid", booking.amountPaid);
  const initialAmount = Number(booking.invoiceAmount || booking.downPayment || (paid > 0 ? Math.min(paid, Number(booking.totalPrice || paid)) : 0));
  if (initialAmount > 0 || booking.paymentProofUrl || booking.paymentStatus) {
    rows.push({ label: "Initial payment", status: prettyStatus(initialStatus), method: getPaymentProviderLabel(booking), date: booking.paymentVerifiedAt || booking.paymentSubmittedAt, amount: initialAmount });
  }
  const approvedRequests = [...(booking.extensionRequests || [])]
    .filter((request) => request.status === "approved")
    .sort((a, b) => new Date(a.reviewedAt || a.requestedAt || 0) - new Date(b.reviewedAt || b.requestedAt || 0));
  approvedRequests.filter((request) => request.payment || String(currentPayment.extensionRequestId || "") === String(request._id)).forEach((request) => {
    const payment = currentPayment.extensionRequestId && String(currentPayment.extensionRequestId) === String(request._id)
      ? { ...(request.payment || {}), ...currentPayment }
      : request.payment || {};
    rows.push({ label: `Extension #${approvedRequests.indexOf(request) + 1} payment`, status: prettyStatus(payment.status || "awaiting_payment"), method: payment.paymentMethod || getPaymentProviderLabel(booking), date: payment.verifiedAt || payment.proofUploadedAt, amount: Number(payment.submittedAmount || payment.extensionAmount || request.additionalAmount || 0) });
  });
  const isCurrentExtensionRepresented = currentPayment.extensionRequestId && approvedRequests.some((request) => String(request._id) === String(currentPayment.extensionRequestId));
  if ((currentPayment.proofUrl || currentPayment.status === "under_review") && !isCurrentExtensionRepresented) {
    rows.push({ label: "Additional payment", status: prettyStatus(currentPayment.status || "awaiting_payment"), method: booking.paymentMethod || getPaymentProviderLabel(booking), date: currentPayment.verifiedAt || currentPayment.proofUploadedAt, amount: Number(currentPayment.submittedAmount || currentPayment.totalAmountDue || 0) });
  }
  return rows;
}

function getDocumentSortValue(document = {}) {
  const type = String(document.type || "").toLowerCase();
  if (type === "booking_invoice") return 1;
  if (type === "payment_receipt") return 2;
  if (type === "completion_receipt") return 3;
  return 9;
}

function getBillingDocumentsForBooking(booking = {}) {
  const documents = Array.isArray(booking.documents) ? booking.documents : [];
  const normalizedDocs = documents
    .filter((document) => DOCUMENT_TYPE_LABELS[String(document?.type || "").toLowerCase()])
    .map((document, index) => ({
      ...document,
      _uiKey:
        document._id ||
        document.documentNumber ||
        `${document.type || "document"}-${document.paymentType || "none"}-${index}`,
      isLegacyFallback: false,
    }));

  const hasType = (type) =>
    normalizedDocs.some((document) => String(document.type || "").toLowerCase() === type);

  if (!hasType("booking_invoice") && isInvoiceIssued(booking)) {
    normalizedDocs.push({
      _uiKey: "legacy-booking-invoice",
      type: "booking_invoice",
      title: "Booking Invoice",
      documentNumber: booking.invoiceNumber || booking.invoiceReference || "",
      issuedAt: booking.invoiceIssuedAt || booking.createdAt || "",
      status: booking.invoiceStatus || "issued",
      paymentType:
        booking.paymentOption === "full_payment"
          ? "full_payment"
          : booking.paymentOption === "down_payment_50"
          ? "down_payment"
          : "",
      amount: booking.invoiceAmount || booking.totalPrice || 0,
      pdfUrl: booking.invoiceUrl || "",
      isLegacyFallback: true,
    });
  }

  if (!hasType("payment_receipt") && isReceiptIssued(booking)) {
    normalizedDocs.push({
      _uiKey: "legacy-payment-receipt",
      type: "payment_receipt",
      title: "Payment Receipt",
      documentNumber: booking.receiptNumber || "",
      issuedAt: booking.receiptIssuedAt || booking.paymentVerifiedAt || "",
      status: booking.receiptStatus || "issued",
      paymentType:
        booking.paymentOption === "full_payment"
          ? "full_payment"
          : Number(booking.remainingBalance || 0) > 0
          ? "down_payment"
          : "remaining_balance",
      amount: booking.amountPaid || booking.invoiceAmount || booking.totalPrice || 0,
      pdfUrl: booking.receiptUrl || "",
      isLegacyFallback: true,
    });
  }

  return normalizedDocs.sort((a, b) => {
    const typeDelta = getDocumentSortValue(a) - getDocumentSortValue(b);
    if (typeDelta) return typeDelta;
    return new Date(a.issuedAt || 0).getTime() - new Date(b.issuedAt || 0).getTime();
  });
}

function getBookingManagementDocument(booking = {}) {
  const invoiceNumber = String(
    booking.invoiceReference || booking.invoiceNumber || ''
  ).trim();

  if (invoiceNumber) {
    return {
      number: invoiceNumber,
      type: 'Invoice',
      amount: Number(booking.invoiceAmount || 0),
    };
  }

  const storedReceipt = (Array.isArray(booking.documents) ? booking.documents : []).find(
    (document) =>
      String(document?.type || '').toLowerCase() === 'payment_receipt' &&
      String(document?.status || 'issued').toLowerCase() !== 'void'
  );
  const receiptNumber = String(
    storedReceipt?.documentNumber || booking.receiptNumber || ''
  ).trim();

  if (receiptNumber) {
    return {
      number: receiptNumber,
      type: 'Payment receipt',
      amount: Number(storedReceipt?.amount || booking.amountPaid || 0),
    };
  }

  return {
    number: 'Not issued',
    type: '',
    amount: Number(booking.invoiceAmount || 0),
  };
}

function canUseLegacyDocumentEndpoint(document = {}, booking = {}) {
  const type = String(document.type || "").toLowerCase();
  if (type === "booking_invoice") return Boolean(booking.invoiceReference || booking.invoiceNumber);
  if (type === "payment_receipt") {
    const legacyReceiptNumber = document.metadata?.legacyReceiptNumber || document.documentNumber;
    return Boolean(booking.receiptNumber && String(legacyReceiptNumber || "") === String(booking.receiptNumber));
  }
  return false;
}

function getStoredDocumentPdfUrl(document = {}) {
  return String(document.pdfUrl || document.documentUrl || document.url || "").trim();
}

function getStoredDocumentId(document = {}) {
  return String(document._id || document.id || "").trim();
}

function canUseGenericDocumentEndpoint(document = {}, booking = {}) {
  return Boolean(booking?._id && getStoredDocumentId(document));
}

function getFilterEmptyMessage(filterKey) {
  const messages = {
    payment_verification: "No payments pending verification.",
    extension_requests: "No extension requests pending.",
    additional_payments: "No additional payments pending.",
    awaiting_payment: "No bookings awaiting payment.",
    confirmed: "No confirmed bookings found.",
    active: "No active rentals right now.",
    completed: "No completed bookings found.",
    cancelled: "No cancelled bookings found.",
    archived: "No archived bookings found.",
    all: "No bookings found.",
  };
  return messages[filterKey] || "No bookings found.";
}

function driverStatusBadgeClass(status) {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "available") return "badge badge-available";
  if (normalized === "assigned" || normalized === "on_trip") return "badge badge-pending";
  if (normalized === "inactive") return "badge badge-rejected";
  if (normalized === "offline") return "badge";
  return "badge";
}

function getUploadUrl(value) {
  if (!value) return "";
  if (String(value).startsWith("http") || String(value).startsWith("data:")) return value;
  return `${API_ORIGIN}${value}`;
}

const PAYMENT_PROOF_FIELDS = [
  "paymentProof",
  "paymentProofUrl",
  "proofOfPayment",
  "paymentProofImage",
  "paymentReceipt",
];

function hasPaymentProofSubmission(booking) {
  const paymentStatus = String(booking?.paymentStatus || "").toLowerCase();
  const bookingStatus = String(booking?.status || "").toLowerCase();

  return Boolean(
      booking?.paymentSubmittedAt ||
      booking?.paymentProofPublicId ||
      ["submitted", "verified", "rejected", "reupload_required"].includes(paymentStatus) ||
      [
        "payment_submitted",
        "payment_verification",
        "payment_under_review",
        "pending_payment_verification",
        "under_review",
        "payment_rejected",
        "confirmed",
      ].includes(bookingStatus)
  );
}

function isPaymentVerificationActionable(booking) {
  const bookingStatus = String(booking?.status || "").toLowerCase();
  const paymentStatus = String(booking?.paymentStatus || "").toLowerCase();
  return ["payment_submitted", "under_review"].includes(bookingStatus) && paymentStatus === "submitted";
}

function buildAdminFilename(prefix, booking, refField) {
  const lastName = String(booking?.fullName || "Customer")
    .trim().split(/\s+/).pop().replace(/[^a-zA-Z0-9]/g, "") || "Customer";
  const ref = String(booking?.[refField] || booking?.bookingReference || booking?._id || "")
    .replace(/[^a-zA-Z0-9-]/g, "").replace(/\s+/g, "-") || "ref";
  return `${prefix}-${lastName}-${ref}.pdf`;
}

function getBookingPaymentProofUrl(booking) {
  if (!booking || !hasPaymentProofSubmission(booking)) return "";

  const rawProof = PAYMENT_PROOF_FIELDS.map((field) => booking[field])
    .find((value) => String(value || "").trim());

  if (!rawProof) return "";

  const proofUrl = getUploadUrl(rawProof);
  const paymentQrUrl = getUploadUrl(booking.selectedPaymentQrCodeUrl || "");

  if (paymentQrUrl && proofUrl === paymentQrUrl) return "";
  return proofUrl;
}

function clearBookingPaymentProofFields(booking) {
  if (!booking) return booking;

  return {
    ...booking,
    paymentProof: "",
    paymentProofUrl: "",
    proofOfPayment: "",
    paymentProofImage: "",
    paymentReceipt: "",
    paymentProofPublicId: "",
    paymentSubmittedAt: null,
    paymentVerifiedAt: null,
    paymentReviewedAt: null,
  };
}

function getDriverLicenseStatus(driver) {
  if (!driver?.licenseExpiryDate) return "expired";
  const expiry = new Date(driver.licenseExpiryDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (Number.isNaN(expiry.getTime()) || expiry <= today) return "expired";
  return "valid";
}

const manualSteps = [
  "Customer",
  "Vehicle",
  "Trip",
  "Pricing",
  "Payment",
  "Review",
];
const FLEETX_PICKUP_FALLBACK = "FleetX vehicle pickup location";

const initialManualForm = {
  customerSearch: "",
  selectedCustomerId: "",
  fullName: "",
  email: "",
  contact: "",
  contactCountryCode: "+63",
  contactNumber: "",
  address: "",
  selectedCustomerVerificationStatus: "",
  selectedCustomerIsVerified: false,
  selectedCustomerAccountSetupRequired: false,
  vehicleSearch: "",
  vehicleId: "",
  startDate: "",
  endDate: "",
  startTime: "00:00",
  endTime: "00:00",
  pickupLocation: "",
  pickupCoords: null,
  pickupPlaceId: "",
  vehicleHandoffOption: "pickup",
  deliveryAddress: "",
  deliveryCoords: null,
  deliveryPlaceId: "",
  destination: "",
  destinationCoords: null,
  destinationPlaceId: "",
  purposeOfTravel: "",
  rentalType: "with_driver",
  numberOfPax: "1",
  luggageBags: "0",
  luggageSize: "",
  luggageWeightKg: "",
  notes: "",
  discount: "0",
  deliveryFee: "",
  extraFee: "0",
  extraChargeRemarks: "",
  adjustmentReason: "",
  paymentMethod: "Cash",
  paymentOption: "down_payment_50",
  paymentStatus: "unpaid",
  amountPaid: "0",
  paymentReference: "",
};

const initialModifyForm = {
  vehicleId: "",
  vehicleSearch: "",
  startDate: "",
  endDate: "",
  startTime: "00:00",
  endTime: "00:00",
  pickupLocation: "",
  pickupCoords: null,
  pickupPlaceId: "",
  vehicleHandoffOption: "pickup",
  deliveryAddress: "",
  deliveryCoords: null,
  deliveryPlaceId: "",
  destination: "",
  destinationCoords: null,
  destinationPlaceId: "",
  purposeOfTravel: "",
  rentalType: "with_driver",
  numberOfPax: "1",
  luggageBags: "0",
  luggageSize: "",
  luggageWeightKg: "",
  driverId: "",
  discount: "0",
  deliveryFee: "",
  extraFee: "0",
  extraChargeRemarks: "",
  reason: "",
};

const initialExtensionForm = {
  newEndDate: "",
  newEndTime: "00:00",
  reason: "",
  remarks: "",
};

const initialReturnAssessmentForm = {
  conditionStatus: "not_recorded",
  issues: [],
  actualReturnDate: "",
  actualReturnTime: "00:00",
  damageFee: "0",
  fuelAdjustment: "0",
  cleaningFee: "0",
  otherFee: "0",
  vehicleReturnNotes: "",
  vehicleConditionNotes: "",
  fuelCleanlinessNotes: "",
  penaltyOverrideType: "apply",
  penaltyOverrideAmount: "",
  penaltyOverrideRemarks: "",
  // inline settlement — shown when finalBalance > 0 after charges are considered
  settlementStatus: "not_now",
  settlementMethod: "cash",
  settlementAmount: "",
  settlementReference: "",
  settlementNotes: "",
};

const initialSettlementForm = {
  settlementStatus: "paid_to_admin",
  settlementMethod: "cash",
  settlementAmount: "",
  settlementReference: "",
  settlementNotes: "",
  settlementRequestId: "",
};

function toDateInput(value) {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

function calculateDays(startDate, endDate) {
  if (!startDate || !endDate) return 0;
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) {
    return 0;
  }
  return Math.ceil((end - start) / (1000 * 60 * 60 * 24));
}

function formatHours(value) {
  const hours = Number(value || 0);
  if (!Number.isFinite(hours) || hours <= 0) return "0 hours";
  const rounded = Math.round(hours * 10) / 10;
  const days = Math.floor(rounded / 24);
  const remainingHours = Math.round((rounded - days * 24) * 10) / 10;
  if (!days) return `${rounded} hour${rounded === 1 ? "" : "s"}`;
  if (!remainingHours) return `${days} day${days === 1 ? "" : "s"}`;
  return `${days} day${days === 1 ? "" : "s"} and ${remainingHours} hour${remainingHours === 1 ? "" : "s"}`;
}

function getManualVehiclePickupLocation(vehicle) {
  return (
    String(
      vehicle?.location ||
        vehicle?.pickupLocation ||
        vehicle?.address ||
        vehicle?.branch ||
        vehicle?.city ||
        ""
    ).trim() || FLEETX_PICKUP_FALLBACK
  );
}

export default function Bookings() {
  const location = useLocation();
  const { user } = useAuth();
  const isAdmin = user?.role === "technical_admin" || user?.role === "admin";
  const isStaff = user?.role === "staff";
  const canAccessPage = isAdmin || isStaff;

  const [bookings, setBookings] = useState([]);
  const [msg, setMsg] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("payment_verification");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [quickFilter, setQuickFilter] = useState("");
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [activeBookingDetailsTab, setActiveBookingDetailsTab] = useState("overview");
  const [highlightExtensionSection, setHighlightExtensionSection] = useState(false);
  const [extensionReviewTarget, setExtensionReviewTarget] = useState(null);
  const extensionReviewInFlightRef = useRef(false);
  const closeExtensionReview = useCallback(() => {
    if (!extensionReviewInFlightRef.current) setExtensionReviewTarget(null);
  }, []);

  const [noteDraft, setNoteDraft] = useState("");
  const [adminRemarksDraft, setAdminRemarksDraft] = useState("");
  const [refundRemarksDraft, setRefundRemarksDraft] = useState("");
  const [isSubmittingAction, setIsSubmittingAction] = useState(false);
  const [viewingDocumentId, setViewingDocumentId] = useState("");
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState("");
  const [downloadingReceiptId, setDownloadingReceiptId] = useState("");
  const [downloadingContractId, setDownloadingContractId] = useState("");
  const [showManualWizard, setShowManualWizard] = useState(false);
  const [manualStep, setManualStep] = useState(0);
  const [manualForm, setManualForm] = useState(initialManualForm);
  const [manualCustomerResults, setManualCustomerResults] = useState([]);
  const [manualVehicles, setManualVehicles] = useState([]);
  const [manualLoadingCustomers, setManualLoadingCustomers] = useState(false);
  const [manualLoadingVehicles, setManualLoadingVehicles] = useState(false);
  const [manualSubmitting, setManualSubmitting] = useState(false);
  const [manualPreview, setManualPreview] = useState(null);
  const [manualPreviewLoading, setManualPreviewLoading] = useState(false);
  const [manualPreviewError, setManualPreviewError] = useState("");
  const [manualScheduleErrors, setManualScheduleErrors] = useState({});
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [assignmentDrivers, setAssignmentDrivers] = useState([]);
  const [driverAssignmentMode, setDriverAssignmentMode] = useState(false);
  const [selectedDriverId, setSelectedDriverId] = useState("");
  const [draggedDriverId, setDraggedDriverId] = useState("");
  const [dropActive, setDropActive] = useState(false);
  const [assignmentConflict, setAssignmentConflict] = useState(null);
  const [assignmentConfirm, setAssignmentConfirm] = useState(null);
  const [showModifyModal, setShowModifyModal] = useState(false);
  const [modifyForm, setModifyForm] = useState(initialModifyForm);
  const [modifyVehicles, setModifyVehicles] = useState([]);
  const [modifyDrivers, setModifyDrivers] = useState([]);
  const [modifyLoadingOptions, setModifyLoadingOptions] = useState(false);
  const [modifySubmitting, setModifySubmitting] = useState(false);
  const [modifyError, setModifyError] = useState("");
  const [modifyReasonError, setModifyReasonError] = useState("");
  const [showChangeVehicleModal, setShowChangeVehicleModal] = useState(false);
  const [vehiclePickerSearch, setVehiclePickerSearch] = useState("");
  const [vehiclePickerLoading, setVehiclePickerLoading] = useState(false);
  const [vehiclePickerError, setVehiclePickerError] = useState("");
  const [modifyVehicleNotice, setModifyVehicleNotice] = useState("");
  const [showExtensionModal, setShowExtensionModal] = useState(false);
  const [extensionForm, setExtensionForm] = useState(initialExtensionForm);
  const [extensionError, setExtensionError] = useState("");
  const [extensionSubmitting, setExtensionSubmitting] = useState(false);
  const [showReturnAssessmentModal, setShowReturnAssessmentModal] = useState(false);
  const [returnAssessmentForm, setReturnAssessmentForm] = useState(initialReturnAssessmentForm);
  const [returnAssessmentError, setReturnAssessmentError] = useState("");
  const [pendingIssueChargeChange, setPendingIssueChargeChange] = useState(null);
  const visibleIssueCharges = getIssueChargeFields(returnAssessmentForm);
  const [returnAssessmentSubmitting, setReturnAssessmentSubmitting] = useState(false);
  const [showReturnCompletionConfirm, setShowReturnCompletionConfirm] = useState(false);
  const [returnPreview, setReturnPreview] = useState({ key: "", data: null, error: "" });
  const returnPreviewKey = JSON.stringify({
    bookingId: selectedBooking?._id,
    actualReturnDate: returnAssessmentForm.actualReturnDate,
    actualReturnTime: returnAssessmentForm.actualReturnTime,
    penaltyOverrideType: returnAssessmentForm.penaltyOverrideType,
    penaltyOverrideAmount: returnAssessmentForm.penaltyOverrideAmount,
    damageFee: returnAssessmentForm.damageFee,
    fuelAdjustment: returnAssessmentForm.fuelAdjustment,
    cleaningFee: returnAssessmentForm.cleaningFee,
    otherFee: returnAssessmentForm.otherFee,
  });
  const returnPreviewLoading = returnPreview.key !== returnPreviewKey;
  const returnPreviewSummary = !returnPreviewLoading ? returnPreview.data?.financialSummary : null;
  useEffect(() => {
    if (!showReturnAssessmentModal || !selectedBooking?._id) return;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const res = await api.post("/bookings/" + selectedBooking._id + "/return-assessment/preview",
          JSON.parse(returnPreviewKey), { signal: controller.signal });
        if (!controller.signal.aborted) setReturnPreview({ key: returnPreviewKey, data: res.data, error: "" });
      } catch (err) {
        if (!controller.signal.aborted) setReturnPreview({ key: returnPreviewKey, data: null,
          error: err?.response?.data?.message || "Unable to preview assessment. Change a return detail to retry." });
      }
    }, 300);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [showReturnAssessmentModal, selectedBooking?._id, returnPreviewKey]);

  const returnAssessmentRequestInFlight = useRef(false);
  const [showSettlementModal, setShowSettlementModal] = useState(false);
  const [settlementForm, setSettlementForm] = useState(initialSettlementForm);
  const [settlementError, setSettlementError] = useState("");
  const [settlementSubmitting, setSettlementSubmitting] = useState(false);
  const bookingStatusFilters = useMemo(() => {
    // Insert additional_payments after extension_requests (index 2 in the ordered list)
    const base = [...BOOKING_STATUS_FILTERS];
    const extIdx = base.findIndex((f) => f.key === "extension_requests");
    const insertAt = extIdx >= 0 ? extIdx + 1 : base.length;
    base.splice(insertAt, 0, { key: "additional_payments", label: "Additional Payments", tone: "warning" });
    return base;
  }, []);
  const [clockNow, setClockNow] = useState(Date.now());
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showMoreActionsMenu, setShowMoreActionsMenu] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [confirmReason, setConfirmReason] = useState("");
  const [confirmError, setConfirmError] = useState("");
  const verificationInFlightRef = useRef(false);
  const confirmationBoxRef = useRef(null);
  const additionalPaymentSectionRef = useRef(null);
  const [focusAdditionalPayment, setFocusAdditionalPayment] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const handledBookingNavigationRef = useRef(null);
  const consumeBookingNavigation = () => {
    handledBookingNavigationRef.current = {
      key: location.key,
      bookingId: location.state?.bookingId,
    };
  };
  const loadAbortRef = useRef(null);
  const loadInFlightRef = useRef(false);
  const modifyReasonRef = useRef(null);

  const load = useCallback(async ({ silent = false, force = false } = {}) => {
    if (loadInFlightRef.current && !force) return;

    if (loadAbortRef.current) {
      loadAbortRef.current.abort();
    }

    const controller = new AbortController();
    loadAbortRef.current = controller;
    loadInFlightRef.current = true;

    if (!silent) setMsg("");
    try {
      const res = await api.get("/bookings", {
        params: {
          limit: 100,
          archived: statusFilter === "archived" ? "true" : "false",
        },
        signal: controller.signal,
      });
      const nextBookings = (res.data.bookings || []).map((booking) => ({
        ...booking,
        financialSummary: booking.financialSummary || buildFinancialSummary(booking),
      }));
      setBookings(nextBookings);
      setSelectedBooking((prev) => {
        if (!prev?._id) return prev;
        return nextBookings.find((item) => item._id === prev._id) || prev;
      });
    } catch (e) {
      if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;
      console.error("Load bookings error:", e?.response?.data?.message || e.message);
      if (!silent) {
        setMsg(e?.response?.data?.message || "Failed to load bookings.");
      }
    } finally {
      if (loadAbortRef.current === controller) {
        loadAbortRef.current = null;
        loadInFlightRef.current = false;
      }
    }
  }, [statusFilter]);

  useEffect(() => {
    if (canAccessPage) {
      load({ force: true });
    }
  }, [canAccessPage, load]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search || "");
    const queryStatus = searchParams.get("status");
    const nextStatusFilter = location.state?.statusFilter || queryStatus;
    if (nextStatusFilter) {
      setStatusFilter(nextStatusFilter);
    }
    setQuickFilter(location.state?.quickFilter || searchParams.get("quickFilter") || "");
  }, [location.search, location.state?.quickFilter, location.state?.statusFilter]);

  useEffect(() => {
    const bookingId = location.state?.bookingId;
    if (!bookingId || !bookings.length) return;
    const handled = handledBookingNavigationRef.current;
    if (handled?.key === location.key && handled?.bookingId === bookingId) return;

    const booking = bookings.find((item) => item._id === bookingId);
    if (!booking) return;

    // A deliberate navigation opens once, even when later fetches replace bookings.
    handledBookingNavigationRef.current = { key: location.key, bookingId };
    setStatusFilter("all");
    setSelectedBooking(booking);
    setShowDetailsModal(true);
  }, [bookings, location.key, location.state?.bookingId]);

  useEffect(() => {
    if (!canAccessPage) return undefined;

    const timer = window.setInterval(() => {
      load({ silent: true });
    }, 60000);

    return () => {
      window.clearInterval(timer);
      loadAbortRef.current?.abort();
    };
  }, [canAccessPage, load]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setClockNow(Date.now());
    }, 1000);

    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!showManualWizard || !manualForm.customerSearch.trim()) {
      setManualCustomerResults([]);
      return undefined;
    }

    const timer = window.setTimeout(async () => {
      try {
        setManualLoadingCustomers(true);
        const res = await api.get("/bookings/manual/customers", {
          params: { search: manualForm.customerSearch },
        });
        setManualCustomerResults(res.data.customers || []);
      } catch (e) {
        console.error("Manual customer search error:", e?.response?.data?.message || e.message);
        setManualCustomerResults([]);
      } finally {
        setManualLoadingCustomers(false);
      }
    }, 300);

    return () => window.clearTimeout(timer);
  }, [showManualWizard, manualForm.customerSearch]);

  useEffect(() => {
    if (!showManualWizard) return undefined;

    const timer = window.setTimeout(async () => {
      try {
        setManualLoadingVehicles(true);
        const res = await api.get("/bookings/manual/vehicles", {
          params: {
            search: manualForm.vehicleSearch,
            startDate: manualForm.startDate,
            endDate: manualForm.endDate,
            startTime: manualForm.startTime,
            endTime: manualForm.endTime,
            rentalType: manualForm.rentalType,
          },
        });
        setManualVehicles(res.data.vehicles || []);
      } catch (e) {
        console.error("Manual vehicle search error:", e?.response?.data?.message || e.message);
        setManualVehicles([]);
      } finally {
        setManualLoadingVehicles(false);
      }
    }, 250);

    return () => window.clearTimeout(timer);
  }, [
    showManualWizard,
    manualForm.vehicleSearch,
    manualForm.startDate,
    manualForm.endDate,
    manualForm.startTime,
    manualForm.endTime,
    manualForm.rentalType,
  ]);

  const replaceBookingInState = (updatedBooking) => {
    if (!updatedBooking?._id) return;

    setBookings((prev) =>
      prev.map((item) => (item._id === updatedBooking._id ? updatedBooking : item))
    );

    setSelectedBooking((prev) =>
      prev && prev._id === updatedBooking._id ? updatedBooking : prev
    );
  };

  const patchBooking = async (path, payload, successMessage) => {
    setMsg("");
    setIsSubmittingAction(true);
    const isIssuingInvoice =
      path.includes("/status") &&
      String(payload?.status || "").toLowerCase() === "awaiting_payment";
    const actionBookingId = path.match(/\/bookings\/([^/]+)/)?.[1] || "";

    if (isIssuingInvoice && actionBookingId) {
      setSelectedBooking((prev) =>
        prev?._id === actionBookingId ? clearBookingPaymentProofFields(prev) : prev
      );
    }

    try {
      const res = await api.patch(path, payload);
      const updatedBooking =
        isIssuingInvoice && res?.data?.booking
          ? clearBookingPaymentProofFields(res.data.booking)
          : res?.data?.booking;

      if (updatedBooking) {
        replaceBookingInState(updatedBooking);
        await load({ silent: true, force: path.endsWith("/additional-payment/verify") });
      } else {
        await load();
      }

      window.dispatchEvent(new Event("notifications-refresh"));
      setMsg(res?.data?.message || successMessage);
      return { ok: true };
    } catch (e) {
      console.error("Booking patch error:", e?.response?.data?.message || e.message);
      const message = e?.response?.data?.message || "Failed to update booking.";
      setMsg(message);
      return { ok: false, message };
    } finally {
      setIsSubmittingAction(false);
    }
  };

  const loadAvailableDrivers = async (bookingArg = selectedBooking) => {
    if (!bookingArg?.startDate || !bookingArg?.endDate) {
      setAvailableDrivers([]);
      setAssignmentDrivers([]);
      return;
    }

    try {
      const params = {
        startDate: formatDate(bookingArg.startDate),
        endDate: formatDate(bookingArg.endDate),
        bookingId: bookingArg._id,
      };
      const [allRes, availableRes] = await Promise.all([
        api.get("/drivers"),
        api.get("/drivers/availability", { params }),
      ]);
      const available = availableRes.data.drivers || [];
      const availableMap = new Map(available.map((driver) => [String(driver._id), driver]));
      setAvailableDrivers(available);
      setAssignmentDrivers(
        (allRes.data.drivers || []).map((driver) => ({
          ...driver,
          isAvailableForRange: availableMap.has(String(driver._id)),
          assignmentCount: availableMap.get(String(driver._id))?.assignmentCount || 0,
        }))
      );
    } catch (e) {
      console.error("Load available drivers error:", e?.response?.data?.message || e.message);
      setAvailableDrivers([]);
      setAssignmentDrivers([]);
    }
  };

  const assignSelectedDriver = async (driverId = selectedDriverId) => {
    if (!selectedBooking?._id) return;
    if (!driverId) {
      setMsg("Select an available driver first.");
      return;
    }

    setMsg("");
    setIsSubmittingAction(true);

    try {
      const res = await api.patch(`/bookings/${selectedBooking._id}/assign-driver`, {
        driverId,
      });
      if (res.data.booking) {
        replaceBookingInState(res.data.booking);
      }
      setDriverAssignmentMode(false);
      setSelectedDriverId("");
      await loadAvailableDrivers();
      await load({ silent: true });
      setMsg(res.data.message || "Driver assigned.");
    } catch (e) {
      console.error("Assign driver error:", e?.response?.data?.message || e.message);
      setMsg(e?.response?.data?.message || "Failed to assign driver.");
    } finally {
      setIsSubmittingAction(false);
    }
  };

  const openModifyBooking = async (booking = selectedBooking) => {
    if (!booking?._id) return;
    const currentVehicleId = booking.vehicleId?._id || booking.vehicleId || "";
    const nextForm = {
      vehicleId: currentVehicleId,
      vehicleSearch: getAdminVehicleName(booking),
      startDate: formatDate(booking.startDate),
      endDate: formatDate(booking.endDate),
      startTime: booking.startTime || "00:00",
      endTime: booking.endTime || "00:00",
      pickupLocation: booking.pickupLocation || "",
      pickupCoords: booking.pickupCoords || null,
      pickupPlaceId: booking.pickupPlaceId || "",
      vehicleHandoffOption: booking.vehicleHandoffOption || "pickup",
      deliveryAddress: booking.deliveryAddress || "",
      deliveryCoords: booking.deliveryCoords || null,
      deliveryPlaceId: booking.deliveryPlaceId || "",
      destination: booking.destination || "",
      destinationCoords: booking.destinationCoords || null,
      destinationPlaceId: booking.destinationPlaceId || "",
      purposeOfTravel: booking.purposeOfTravel || "",
      rentalType: booking.withDriver ? "with_driver" : "self_drive",
      numberOfPax: String(booking.numberOfPax || 1),
      luggageBags: String(booking.luggageBags ?? booking.luggage ?? booking.luggageCount ?? 0),
      luggageSize: normalizeLuggageSize(booking.luggageSize),
      luggageWeightKg:
        (booking.luggageWeightKg ??
          booking.estimatedLuggageWeightKg ??
          booking.luggageWeight) === undefined ||
        (booking.luggageWeightKg ??
          booking.estimatedLuggageWeightKg ??
          booking.luggageWeight) === null
          ? ""
          : String(
              booking.luggageWeightKg ??
                booking.estimatedLuggageWeightKg ??
                booking.luggageWeight
            ),
      driverId: booking.driverId?._id || booking.driverId || "",
      discount: String(booking.discount || 0),
      deliveryFee:
        booking.deliveryFee === undefined || booking.deliveryFee === null
          ? ""
          : String(booking.deliveryFee),
      extraFee: String(booking.extraFee || 0),
      extraChargeRemarks: booking.extraChargeRemarks || "",
      reason: "",
    };
    setModifyForm(nextForm);
    setModifyVehicles(booking.vehicleId ? [booking.vehicleId] : []);
    setModifyDrivers([]);
    setModifyError("");
    setModifyReasonError("");
    setShowChangeVehicleModal(false);
    setVehiclePickerSearch("");
    setVehiclePickerError("");
    setModifyVehicleNotice("");
    setShowModifyModal(true);
    await loadModifyOptions(nextForm);
  };

  const closeModifyBooking = () => {
    if (modifySubmitting) return;
    setShowModifyModal(false);
    setModifyForm(initialModifyForm);
    setModifyVehicles([]);
    setModifyDrivers([]);
    setModifyError("");
    setModifyReasonError("");
    setShowChangeVehicleModal(false);
    setVehiclePickerSearch("");
    setVehiclePickerError("");
    setModifyVehicleNotice("");
  };

  const loadModifyOptions = async (draft = modifyForm) => {
    try {
      setModifyLoadingOptions(true);
      const requests = [];
      if (draft.rentalType === "with_driver") {
        requests.push(
          api.get("/drivers/availability", {
            params: { startDate: draft.startDate, endDate: draft.endDate },
          })
        );
      }
      if (!requests.length) {
        setModifyDrivers([]);
        return;
      }
      const [driversRes] = await Promise.all(requests);
      setModifyDrivers(driversRes?.data?.drivers || []);
    } catch (err) {
      setModifyError(err?.response?.data?.message || "Unable to load modification options.");
    } finally {
      setModifyLoadingOptions(false);
    }
  };

  const loadAvailableModifyVehicles = async (search = vehiclePickerSearch) => {
    if (!selectedBooking?._id) return;
    if (!modifyForm.startDate || !modifyForm.endDate) {
      setVehiclePickerError("Please complete the schedule before changing the vehicle.");
      setModifyVehicles([]);
      return;
    }

    try {
      setVehiclePickerLoading(true);
      setVehiclePickerError("");
      const res = await api.get(`/bookings/${selectedBooking._id}/modify/vehicles`, {
        params: {
          startDate: modifyForm.startDate,
          endDate: modifyForm.endDate,
          startTime: modifyForm.startTime,
          endTime: modifyForm.endTime,
          search,
          rentalType: modifyForm.rentalType,
          numberOfPax: modifyForm.numberOfPax,
        },
      });
      const vehicles = res.data.vehicles || [];
      const currentSelected = selectedModifyVehicle;
      const withSelected =
        currentSelected && !vehicles.some((item) => String(item._id) === String(currentSelected._id))
          ? [currentSelected, ...vehicles]
          : vehicles;
      setModifyVehicles(withSelected);
    } catch (err) {
      setModifyVehicles([]);
      setVehiclePickerError(
        err?.response?.data?.message || "Unable to load available vehicles."
      );
    } finally {
      setVehiclePickerLoading(false);
    }
  };

  const openChangeVehicleModal = async () => {
    setShowChangeVehicleModal(true);
    setVehiclePickerSearch("");
    await loadAvailableModifyVehicles("");
  };

  const selectModifyVehicle = (vehicle) => {
    const maxPassengers = getVehiclePassengerLimit(vehicle, modifyForm.rentalType);
    const currentPax = Math.max(1, Number(modifyForm.numberOfPax || 1));
    const adjustedPax = maxPassengers > 0 && currentPax > maxPassengers ? maxPassengers : currentPax;
    setModifyVehicles((prev) =>
      prev.some((item) => String(item._id) === String(vehicle._id)) ? prev : [vehicle, ...prev]
    );
    setModifyForm((prev) => ({
      ...prev,
      vehicleId: vehicle._id,
      vehicleSearch: getVehicleOptionLabel(vehicle),
      numberOfPax: String(adjustedPax),
    }));
    setModifyVehicleNotice(
      adjustedPax !== currentPax
        ? "Passenger count was adjusted to match the selected vehicle capacity."
        : `Selected vehicle: ${getVehicleOptionLabel(vehicle)}`
    );
    setShowChangeVehicleModal(false);
  };

  const updateModifyForm = (key, value) => {
    setModifyForm((prev) => {
      const next = updateBookingRouteField(prev, key, value);
      if (key === "rentalType" && value === "self_drive") {
        next.driverId = "";
        next.vehicleHandoffOption = next.vehicleHandoffOption || "pickup";
      }
      if (key === "vehicleHandoffOption" && value === "pickup") {
        next.deliveryAddress = "";
        next.deliveryCoords = null;
        next.deliveryPlaceId = "";
      }
      return next;
    });
    if (key === "reason" && String(value || "").trim()) {
      setModifyReasonError("");
    }
  };

  const selectedModifyVehicle = useMemo(
    () =>
      modifyVehicles.find((vehicle) => String(vehicle._id) === String(modifyForm.vehicleId)) ||
      selectedBooking?.vehicleId ||
      null,
    [modifyVehicles, modifyForm.vehicleId, selectedBooking]
  );
  const modifyPassengerMax = useMemo(
    () => getVehiclePassengerLimit(selectedModifyVehicle, modifyForm.rentalType),
    [selectedModifyVehicle, modifyForm.rentalType]
  );

  useEffect(() => {
    if (!showModifyModal || !modifyPassengerMax) return;
    const currentPax = Math.max(1, Number(modifyForm.numberOfPax || 1));
    if (currentPax <= modifyPassengerMax) return;
    setModifyForm((prev) => ({ ...prev, numberOfPax: String(modifyPassengerMax) }));
    setModifyVehicleNotice("Passenger count was adjusted to match the selected vehicle capacity.");
  }, [showModifyModal, modifyPassengerMax, modifyForm.numberOfPax]);

  const modifyPricing = useMemo(() => {
    const pickupDateTime = buildLocalDateTime(modifyForm.startDate, modifyForm.startTime);
    const returnDateTime = buildLocalDateTime(modifyForm.endDate, modifyForm.endTime);
    const pricing = calculateRentalPricing({
      vehicle: selectedModifyVehicle,
      pickupDateTime,
      returnDateTime,
      rentalType: modifyForm.rentalType,
      withDriver: modifyForm.rentalType === "with_driver",
      paymentOption: selectedBooking?.paymentOption,
    });
    const discount = Math.max(0, Number(modifyForm.discount || 0));
    const deliveryFee = Math.max(0, Number(modifyForm.deliveryFee || 0));
    const extraFee = Math.max(0, Number(modifyForm.extraFee || 0));
    const total = Math.max(0, Number(pricing.subtotal || 0) - discount + deliveryFee + extraFee);
    return { ...pricing, total };
  }, [modifyForm, selectedModifyVehicle, selectedBooking]);

  const submitModifyBooking = async (event) => {
    event.preventDefault();
    if (!selectedBooking?._id) return;
    const trimmedReason = modifyForm.reason.trim();
    if (!trimmedReason) {
      setModifyReasonError("Modification reason is required.");
      window.setTimeout(() => modifyReasonRef.current?.focus(), 0);
      return;
    }

    // Validate that the new schedule is in the future and return is after pickup.
    const schedErrs = validateAdminSchedule({
      startDate: modifyForm.startDate,
      startTime: modifyForm.startTime,
      endDate: modifyForm.endDate,
      endTime: modifyForm.endTime,
    });
    if (Object.keys(schedErrs).length > 0) {
      const first = Object.values(schedErrs)[0];
      setModifyError(first);
      return;
    }

    try {
      setModifySubmitting(true);
      setModifyError("");
      const payload = {
        ...modifyForm,
        reason: trimmedReason,
        withDriver: modifyForm.rentalType === "with_driver",
        numberOfPax: Number(modifyForm.numberOfPax || 1),
        luggageBags: Number(modifyForm.luggageBags || 0),
        luggageSize: modifyForm.luggageSize || "",
        luggageWeightKg:
          modifyForm.luggageWeightKg === "" ? "" : Number(modifyForm.luggageWeightKg),
        discount: Number(modifyForm.discount || 0),
        deliveryFee:
          modifyForm.deliveryFee === "" ? "" : Number(modifyForm.deliveryFee || 0),
        extraFee: Number(modifyForm.extraFee || 0),
        extraChargeRemarks: modifyForm.extraChargeRemarks,
      };
      const res = await api.patch(`/bookings/${selectedBooking._id}/modify`, payload);
      const updated = res.data.booking;
      if (updated) replaceBookingInState(updated);
      setAdminRemarksDraft(updated?.adminRemarks || "");
      setMsg(res.data.adjustment?.paymentAdjustmentNote || "Booking modified successfully.");
      closeModifyBooking();
      await load({ silent: true, force: true });
    } catch (err) {
      setModifyError(err?.response?.data?.message || "Failed to modify booking.");
    } finally {
      setModifySubmitting(false);
    }
  };

  const latestExtensionRequest = useMemo(() => {
    const requests = selectedBooking?.extensionRequests || [];
    return requests.length ? requests[requests.length - 1] : null;
  }, [selectedBooking]);

  const pendingExtensionRequest = useMemo(
    () =>
      (selectedBooking?.extensionRequests || []).find(
        (request) => String(request.status || "").toLowerCase() === "pending"
      ) || null,
    [selectedBooking]
  );

  const openExtensionModal = () => {
    if (!selectedBooking) return;
    setExtensionForm({
      newEndDate: toDateInput(selectedBooking.endDate),
      newEndTime: selectedBooking.endTime || "00:00",
      reason: "",
      remarks: "",
    });
    setExtensionError("");
    setShowExtensionModal(true);
  };

  const closeExtensionModal = () => {
    setShowExtensionModal(false);
    setExtensionForm(initialExtensionForm);
    setExtensionError("");
  };

  const createAdminExtensionRequest = async (event) => {
    event.preventDefault();
    if (!selectedBooking?._id) return;
    try {
      setExtensionSubmitting(true);
      setExtensionError("");
      const res = await api.post(`/bookings/${selectedBooking._id}/extensions`, extensionForm);
      if (res.data.booking) replaceBookingInState(res.data.booking);
      setMsg("Extension request created.");
      closeExtensionModal();
      await load({ silent: true, force: true });
    } catch (err) {
      setExtensionError(err?.response?.data?.message || "Failed to create extension request.");
    } finally {
      setExtensionSubmitting(false);
    }
  };

  const openExtensionReview = (booking = selectedBooking) => {
    if (!booking || extensionReviewInFlightRef.current) return;
    consumeBookingNavigation();
    const request = (booking.extensionRequests || []).find(
      (item) => String(item.status || "").toLowerCase() === "pending"
    );
    if (!request?._id) {
      setMsg("No pending extension request is available for this booking. Please refresh the list.");
      return;
    }
    setSelectedBooking(booking);
    setExtensionReviewTarget({ bookingId: booking._id, requestId: request._id });
  };

  const reviewExtensionRequest = async (requestId, action, remarks, acceptedPricingKey) => {
    if (!selectedBooking?._id || !requestId || extensionReviewInFlightRef.current) return { ok: false };
    extensionReviewInFlightRef.current = true;
    try {
      setIsSubmittingAction(true);
      const res = await api.patch(
        "/bookings/" + selectedBooking._id + "/extensions/" + requestId + "/" + action,
        { remarks: remarks ?? (extensionForm.remarks || adminRemarksDraft), acceptedPricingKey }
      );
      if (res.data.booking) replaceBookingInState(res.data.booking);
      setMsg(action === "approve" ? "Extension approved." : "Extension rejected.");
      await load({ silent: true, force: true });
      return { ok: true };
    } catch (err) {
      if (err?.response?.data?.code === "EXTENSION_PRICE_CHANGED") {
        return { ok: false, pricing: err.response.data.pricing, message: err.response.data.message };
      }
      const message = err?.response?.data?.message || "Failed to " + action + " extension.";
      setMsg(message);
      return { ok: false, message };
    } finally {
      extensionReviewInFlightRef.current = false;
      setIsSubmittingAction(false);
    }
  };

  const reviewedExtensionRequest = selectedBooking?._id === extensionReviewTarget?.bookingId
    ? (selectedBooking?.extensionRequests || []).find((request) => request._id === extensionReviewTarget?.requestId)
    : null;

  const getAssessmentExpectedReturn = (booking) => {
    if (!booking) return null;
    const date = booking.extensionApproved && booking.extensionNewEndDate
      ? booking.extensionNewEndDate
      : booking.endDate;
    const time = booking.extensionApproved && booking.extensionNewEndTime
      ? booking.extensionNewEndTime
      : booking.endTime || "00:00";
    return date ? new Date(buildLocalDateTime(toDateInput(date), time)) : null;
  };

  const openReturnAssessmentModal = () => {
    setPendingIssueChargeChange(null);
    setReturnPreview({ key: "", data: null, error: "" });
    if (!selectedBooking) return;
    const manilaNow = new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString();
    setReturnAssessmentForm({
      ...initialReturnAssessmentForm,
      actualReturnDate: manilaNow.slice(0, 10),
      actualReturnTime: manilaNow.slice(11, 16),
      settlementAmount: "",
    });
    setReturnAssessmentError("");
    setShowReturnAssessmentModal(true);
  };

  const closeReturnAssessmentModal = () => {
    if (returnAssessmentSubmitting) return;
    setShowReturnCompletionConfirm(false);
    setShowReturnAssessmentModal(false);
    setReturnAssessmentForm(initialReturnAssessmentForm);
    setReturnAssessmentError("");
  };

  const openSettlementModal = (booking = selectedBooking) => {
    if (!booking) return;
    const existing = booking.returnAssessment || {};
    setSelectedBooking(booking);
    setSettlementForm({
      settlementStatus: "paid_to_admin",
      settlementMethod: existing.settlementMethod || "cash",
      settlementAmount: "",
      settlementReference: "",
      settlementNotes: "",
      settlementRequestId: window.crypto.randomUUID(),
    });
    setSettlementError("");
    setShowSettlementModal(true);
  };

  const closeSettlementModal = () => {
    if (settlementSubmitting) return;
    setShowSettlementModal(false);
    setSettlementForm(initialSettlementForm);
    setSettlementError("");
  };

  const updateSettlementForm = (key, value) => {
    setSettlementForm((prev) => ({ ...prev, [key]: value }));
    if (settlementError) setSettlementError("");
  };

  const submitSettlement = async (event) => {
    event.preventDefault();
    if (!selectedBooking?._id) return;
    try {
      setSettlementSubmitting(true);
      setSettlementError("");
      const payload = {
        ...settlementForm,
        settlementAmount: settlementForm.settlementAmount === ""
          ? null
          : Number(settlementForm.settlementAmount),
      };
      const res = await api.post(`/bookings/${selectedBooking._id}/return-assessment/record-settlement`, payload);
      if (res.data.booking) replaceBookingInState(res.data.booking);
      setMsg(res.data.message || "Settlement recorded.");
      setShowSettlementModal(false);
      setSettlementForm(initialSettlementForm);
      await load({ silent: true, force: true });
    } catch (err) {
      setSettlementError(err?.response?.data?.message || "Failed to record settlement.");
    } finally {
      setSettlementSubmitting(false);
    }
  };

  const updateReturnAssessmentForm = (key, value) => {
    if (pendingIssueChargeChange) return;
    const patch = typeof key === "object" ? key : { [key]: value };
    if ("issues" in patch || "conditionStatus" in patch) {
      const change = planIssueChargeChange(returnAssessmentForm, patch);
      if (change.affected.length) {
        setPendingIssueChargeChange(change);
        return;
      }
      setReturnAssessmentForm(change.next);
    } else {
      setReturnAssessmentForm((prev) => ({ ...prev, ...patch }));
    }
    if (returnAssessmentError) setReturnAssessmentError("");
  };

  const requestReturnAssessmentCompletion = (event) => {
    event.preventDefault();
    if (!selectedBooking?._id || returnAssessmentSubmitting) return;
    if (returnPreviewLoading || !returnPreviewSummary) {
      setReturnAssessmentError("Wait for the assessment totals before reviewing completion.");
      return;
    }
    if (!["no_issue_found", "issue_found"].includes(returnAssessmentForm.conditionStatus)) {
      setReturnAssessmentError("Select a vehicle condition result.");
      return;
    }
    if (returnAssessmentForm.conditionStatus === "issue_found" &&
        (!returnAssessmentForm.issues.length || returnAssessmentForm.issues.some((issue) => !/[\p{L}\p{N}]/u.test(issue.description.trim())))) {
      setReturnAssessmentError("Add a description for each identified issue.");
      return;
    }
    setShowReturnCompletionConfirm(true);
  };

  const submitReturnAssessment = async () => {
    if (!selectedBooking?._id || returnAssessmentRequestInFlight.current) return;
    returnAssessmentRequestInFlight.current = true;

    try {
      setReturnAssessmentSubmitting(true);
      setReturnAssessmentError("");
      setShowReturnCompletionConfirm(false);
      const assessmentPayload = {
        conditionStatus: returnAssessmentForm.conditionStatus,
        issues: returnAssessmentForm.conditionStatus === "issue_found" ? returnAssessmentForm.issues : [],
        actualReturnDate: returnAssessmentForm.actualReturnDate,
        actualReturnTime: returnAssessmentForm.actualReturnTime,
        penaltyOverrideType: returnAssessmentForm.penaltyOverrideType,
        penaltyOverrideAmount: Number(returnAssessmentForm.penaltyOverrideAmount || 0),
        penaltyOverrideRemarks: returnAssessmentForm.penaltyOverrideRemarks,
        damageFee: Number(returnAssessmentForm.damageFee || 0),
        fuelAdjustment: Number(returnAssessmentForm.fuelAdjustment || 0),
        cleaningFee: Number(returnAssessmentForm.cleaningFee || 0),
        otherFee: Number(returnAssessmentForm.otherFee || 0),
        vehicleReturnNotes: returnAssessmentForm.vehicleReturnNotes,
        vehicleConditionNotes: returnAssessmentForm.vehicleConditionNotes,
        fuelCleanlinessNotes: returnAssessmentForm.fuelCleanlinessNotes,
        settlement: returnAssessmentForm.settlementStatus === "not_now"
          ? undefined
          : {
              settlementStatus: returnAssessmentForm.settlementStatus,
              settlementMethod: returnAssessmentForm.settlementMethod,
              settlementAmount: returnAssessmentForm.settlementAmount === ""
                ? null
                : Number(returnAssessmentForm.settlementAmount),
              settlementReference: returnAssessmentForm.settlementReference,
              settlementNotes: returnAssessmentForm.settlementNotes,
            },
      };
      const res = await api.post(`/bookings/${selectedBooking._id}/return-assessment`, assessmentPayload);
      if (res.data.booking) replaceBookingInState(res.data.booking);
      setMsg(res.data.message || "Booking completed.");

      setShowReturnAssessmentModal(false);
      setReturnAssessmentForm(initialReturnAssessmentForm);
      setReturnAssessmentError("");
      try {
        await load({ silent: true, force: true });
      } catch {
        // The saved booking from the successful response is already in local state.
      }
    } catch (err) {
      setReturnAssessmentError(err?.response?.data?.message || "Failed to assess return.");
    } finally {
      returnAssessmentRequestInFlight.current = false;
      setReturnAssessmentSubmitting(false);
    }
  };

  const validateDriverCandidate = (driver) => {
    if (!driver) return "Driver was not found.";
    const status = String(driver.status || "").toLowerCase();
    if (status === "offline" || status === "inactive") {
      return "This driver is offline or inactive and cannot be assigned.";
    }
    if (getDriverLicenseStatus(driver) !== "valid") {
      return "This driver's license is expired or missing.";
    }
    if (!driver.isAvailableForRange) {
      return "This driver has an overlapping booking for the selected dates.";
    }
    return "";
  };

  const requestDriverAssignment = (driver) => {
    const issue = validateDriverCandidate(driver);
    if (issue) {
      setAssignmentConflict({ driver, message: issue });
      return;
    }
    setSelectedDriverId(driver._id);
    setAssignmentConfirm({ driver });
  };

  const smartAssignDriver = () => {
    const candidates = assignmentDrivers
      .filter((driver) => !validateDriverCandidate(driver))
      .sort((a, b) => {
        const workloadDelta = Number(a.assignmentCount || 0) - Number(b.assignmentCount || 0);
        if (workloadDelta !== 0) return workloadDelta;
        return String(a.fullName || "").localeCompare(String(b.fullName || ""));
      });
    if (!candidates.length) {
      setAssignmentConflict({ message: "No valid driver is available for this booking date range." });
      return;
    }
    setSelectedDriverId(candidates[0]._id);
    setAssignmentConfirm({ driver: candidates[0], smart: true });
  };

  const removeAssignedDriver = async () => {
    if (!selectedBooking?._id) return;
    if (!window.confirm("Remove the assigned driver from this booking?")) return;

    setMsg("");
    setIsSubmittingAction(true);

    try {
      const res = await api.patch(`/bookings/${selectedBooking._id}/remove-driver`);
      if (res.data.booking) {
        replaceBookingInState(res.data.booking);
      }
      setDriverAssignmentMode(false);
      setSelectedDriverId("");
      await loadAvailableDrivers();
      await load({ silent: true });
      setMsg(res.data.message || "Driver removed.");
    } catch (e) {
      console.error("Remove driver error:", e?.response?.data?.message || e.message);
      setMsg(e?.response?.data?.message || "Failed to remove driver.");
    } finally {
      setIsSubmittingAction(false);
    }
  };

  const saveNoteOnly = async () => {
    if (!selectedBooking?._id) return;

    await patchBooking(
      `/bookings/${selectedBooking._id}/status`,
      {
        status: selectedBooking.status || "pending_approval",
        notes: noteDraft,
        adminRemarks: adminRemarksDraft,
      },
      "Booking note saved successfully."
    );
  };

  const archiveBooking = async (id, reason) => {
    if (!isAdmin) return;

    setMsg("");
    setIsSubmittingAction(true);

    try {
      const res = await api.patch(`/bookings/${id}/archive`, { reason });
      const archivedBooking = res.data.booking;
      setBookings((prev) =>
        prev.map((item) => (item._id === id ? { ...item, ...archivedBooking } : item))
      );
      if (selectedBooking?._id === id && statusFilter !== "archived") {
        setShowDetailsModal(false);
        setSelectedBooking(null);
      } else if (selectedBooking?._id === id && archivedBooking) {
        setSelectedBooking((prev) => prev?._id === id ? { ...prev, ...archivedBooking } : prev);
      }
      await load({ silent: true, force: true });
      window.dispatchEvent(new Event("notifications-refresh"));
      setMsg("Booking archived successfully.");
    } catch (e) {
      console.error("Archive booking error:", e?.response?.data?.message || e.message);
      setMsg(e?.response?.data?.message || "Failed to archive booking.");
    } finally {
      setIsSubmittingAction(false);
    }
  };

  const restoreBooking = async (id) => {
    if (!isAdmin) return;

    setMsg("");
    setIsSubmittingAction(true);

    try {
      const res = await api.patch(`/bookings/${id}/restore`);
      const restoredBooking = res.data.booking;
      setBookings((prev) =>
        prev.map((item) => (item._id === id ? { ...item, ...restoredBooking } : item))
      );
      if (selectedBooking?._id === id && restoredBooking) {
        setSelectedBooking((prev) => prev?._id === id ? { ...prev, ...restoredBooking } : prev);
      }
      await load({ silent: true, force: true });
      setMsg("Booking restored successfully.");
    } catch (e) {
      console.error("Restore booking error:", e?.response?.data?.message || e.message);
      setMsg(e?.response?.data?.message || "Failed to restore booking.");
    } finally {
      setIsSubmittingAction(false);
    }
  };

  const openDocumentHtml = (html, title = "FleetX Document") => {
    const blob = new Blob([html], { type: "text/html" });
    const url = window.URL.createObjectURL(blob);
    const opened = window.open(url, "_blank", "noopener,noreferrer");
    if (!opened) {
      setMsg(`Pop-up blocked. Please allow pop-ups to view ${title}.`);
      window.URL.revokeObjectURL(url);
      return;
    }
    window.setTimeout(() => window.URL.revokeObjectURL(url), 30000);
  };

  const getDocumentViewPath = (document, bookingId) => {
    const storedDocumentId = getStoredDocumentId(document);
    if (storedDocumentId) {
      return `/bookings/${bookingId}/documents/${encodeURIComponent(storedDocumentId)}/preview`;
    }

    const type = String(document?.type || "").toLowerCase();
    if (type === "booking_invoice") return `/bookings/${bookingId}/invoice`;
    if (type === "payment_receipt") return `/bookings/${bookingId}/receipt`;
    return "";
  };

  const handleViewBillingDocument = async (document, booking) => {
    if (!document || !booking?._id) return;
    const documentKey = document._uiKey || document.documentNumber || document.type;

    if (document.html) {
      openDocumentHtml(document.html, getDocumentTypeLabel(document.type));
      return;
    }

    if (!canUseGenericDocumentEndpoint(document, booking) && !canUseLegacyDocumentEndpoint(document, booking)) return;

    const path = getDocumentViewPath(document, booking._id);
    if (!path) return;

    try {
      setViewingDocumentId(documentKey);
      const res = await api.get(path, { responseType: "text" });
      openDocumentHtml(res.data, getDocumentTypeLabel(document.type));
    } catch (e) {
      console.error("View document error:", e?.response?.data?.message || e.message);
      setMsg(e?.response?.data?.message || "Failed to view document.");
    } finally {
      setViewingDocumentId("");
    }
  };

  const handleDownloadBillingDocument = async (document, booking) => {
    if (!document || !booking?._id) return;
    const storedDocumentId = getStoredDocumentId(document);
    if (!storedDocumentId) return;

    const documentKey = document._uiKey || document.documentNumber || document.type;
    try {
      setDownloadingReceiptId(documentKey);
      const res = await api.get(
        `/bookings/${booking._id}/documents/${encodeURIComponent(storedDocumentId)}/pdf`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(res.data);
      const link = window.document.createElement("a");
      link.href = url;
      link.download = `${getDocumentTypeLabel(document.type).replace(/\s+/g, "-")}-${booking.bookingReference || booking._id}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Download document error:", e?.response?.data?.message || e.message);
      setMsg(e?.response?.data?.message || "Failed to download document.");
    } finally {
      setDownloadingReceiptId("");
    }
  };

  const handleDownloadInvoice = async (bookingId) => {
    const booking = bookings.find((item) => item._id === bookingId) || selectedBooking;
    try {
      setDownloadingInvoiceId(bookingId);
      const res = await api.get(`/bookings/${bookingId}/invoice/pdf`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = buildAdminFilename("FleetX-Invoice", booking, "invoiceReference");
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Download invoice error:", e?.response?.data?.message || e.message);
      setMsg(e?.response?.data?.message || "Failed to download invoice.");
    } finally {
      setDownloadingInvoiceId("");
    }
  };

  const handleDownloadReceipt = async (bookingId) => {
    const booking = bookings.find((item) => item._id === bookingId) || selectedBooking;
    try {
      setDownloadingReceiptId(bookingId);
      const res = await api.get(`/bookings/${bookingId}/receipt/pdf`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = buildAdminFilename("FleetX-Receipt", booking, "receiptNumber");
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Download receipt error:", e?.response?.data?.message || e.message);
      setMsg(e?.response?.data?.message || "Failed to download receipt.");
    } finally {
      setDownloadingReceiptId("");
    }
  };

  const handleDownloadAdditionalInvoice = async (bookingId) => {
    const booking = bookings.find((item) => item._id === bookingId) || selectedBooking;
    try {
      setDownloadingInvoiceId(`additional-${bookingId}`);
      const res = await api.get(`/bookings/${bookingId}/additional-invoice/pdf`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = buildAdminFilename("FleetX-AdditionalInvoice", booking, "invoiceReference");
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Download additional invoice error:", e?.response?.data?.message || e.message);
      setMsg(e?.response?.data?.message || "Failed to download additional invoice.");
    } finally {
      setDownloadingInvoiceId("");
    }
  };

  const handleViewAdditionalInvoice = async (bookingId) => {
    try {
      setViewingDocumentId(`additional-${bookingId}`);
      const res = await api.get(`/bookings/${bookingId}/additional-invoice/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      console.error("View additional invoice error:", e?.response?.data?.message || e.message);
      setMsg(e?.response?.data?.message || "Failed to open additional invoice.");
    } finally {
      setViewingDocumentId("");
    }
  };

  const handleDownloadContract = async (bookingOrId) => {
    const booking =
      typeof bookingOrId === "object"
        ? bookingOrId
        : bookings.find((item) => item._id === bookingOrId);
    const bookingId = booking?._id || bookingOrId;

    try {
      setDownloadingContractId(bookingId);
      const res = await api.get(`/bookings/${bookingId}/contract/pdf`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = buildAdminFilename("FleetX-Agreement", booking, "bookingReference");
      link.click();
      window.URL.revokeObjectURL(url);
      await load({ silent: true, force: true });
    } catch (e) {
      console.error("Download contract error:", e?.response?.data?.message || e.message);
      setMsg(e?.response?.data?.message || "Failed to download rental contract.");
    } finally {
      setDownloadingContractId("");
    }
  };

  const filteredBookings = useMemo(() => {
    return bookings.filter((b) => {
      const keyword = search.trim().toLowerCase();

      const matchesSearch =
        !keyword ||
        String(b.fullName || "").toLowerCase().includes(keyword) ||
        String(b.email || "").toLowerCase().includes(keyword) ||
        String(b.contact || "").toLowerCase().includes(keyword) ||
        String(b.purposeOfTravel || "").toLowerCase().includes(keyword) ||
        String(b.destination || "").toLowerCase().includes(keyword) ||
        String(b.pickupLocation || "").toLowerCase().includes(keyword) ||
        String(b.bookingReference || "").toLowerCase().includes(keyword) ||
        String(b.invoiceReference || "").toLowerCase().includes(keyword) ||
        String(b.vehicleId?.make || "").toLowerCase().includes(keyword) ||
        String(b.vehicleId?.model || "").toLowerCase().includes(keyword) ||
        String(b.vehicleId?.plateNo || "").toLowerCase().includes(keyword) ||
        String(b.vehicleId?.plateNumber || "").toLowerCase().includes(keyword) ||
        String(b.vehicleId?.unitCode || "").toLowerCase().includes(keyword) ||
        String(b.vehicleId?.code || "").toLowerCase().includes(keyword) ||
        String(b.vehicleId?.category || "").toLowerCase().includes(keyword);

      const statusMeta = getBookingStatusMeta(b.status || "pending_approval");
      const isArchived = Boolean(b.isArchived);
      const matchesStatus =
        statusFilter === "archived"
          ? isArchived
          : isArchived
          ? false
          : statusFilter === "all" ||
        (statusFilter === "extension_requests"
          ? hasPendingExtensionRequest(b)
          : statusFilter === "additional_payments"
          ? hasAdditionalPaymentActionRequired(b)
          : statusFilter === "active"
          ? isActiveRental(b)
          : statusMeta.key === statusFilter);

      const source = String(b.source || "online").toLowerCase();
      const matchesSource = sourceFilter === "all" || source === sourceFilter;

      const normalizedStatus = String(b.status || "").toLowerCase();
      const startDate = b.startDate ? new Date(b.startDate) : null;
      const isUpcoming =
        startDate &&
        !Number.isNaN(startDate.getTime()) &&
        startDate.getTime() >= Date.now() &&
        ["approved", "booked_confirmed", "confirmed"].includes(normalizedStatus);
      const needsRouteReview =
        String(b.routeEstimateSource || b.routeTravelTimeSource || "").toLowerCase() ===
          "inter_island_manual" ||
        /special|manual|inter.?island|ferry|barge/i.test(String(b.routeNote || "")) ||
        ["manual_review", "unavailable"].includes(String(b.deliveryFeeStatus || "").toLowerCase());
      const matchesQuickFilter =
        quickFilter === "paid_revenue"
          ? ["approved", "booked_confirmed", "confirmed", "completed"].includes(normalizedStatus)
          : quickFilter === "upcoming"
          ? Boolean(isUpcoming)
          : quickFilter === "route_review"
          ? Boolean(needsRouteReview)
          : true;

      return matchesSearch && matchesStatus && matchesSource && matchesQuickFilter;
    });
  }, [bookings, quickFilter, search, statusFilter, sourceFilter]);

  const tabCounts = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    const baseMatches = (b) => {
      const matchesSearch =
        !keyword ||
        String(b.fullName || "").toLowerCase().includes(keyword) ||
        String(b.email || "").toLowerCase().includes(keyword) ||
        String(b.contact || "").toLowerCase().includes(keyword) ||
        String(b.purposeOfTravel || "").toLowerCase().includes(keyword) ||
        String(b.destination || "").toLowerCase().includes(keyword) ||
        String(b.pickupLocation || "").toLowerCase().includes(keyword) ||
        String(b.bookingReference || "").toLowerCase().includes(keyword) ||
        String(b.invoiceReference || "").toLowerCase().includes(keyword) ||
        String(b.vehicleId?.make || "").toLowerCase().includes(keyword) ||
        String(b.vehicleId?.model || "").toLowerCase().includes(keyword) ||
        String(b.vehicleId?.plateNo || "").toLowerCase().includes(keyword) ||
        String(b.vehicleId?.plateNumber || "").toLowerCase().includes(keyword) ||
        String(b.vehicleId?.unitCode || "").toLowerCase().includes(keyword) ||
        String(b.vehicleId?.code || "").toLowerCase().includes(keyword) ||
        String(b.vehicleId?.category || "").toLowerCase().includes(keyword);
      const source = String(b.source || "online").toLowerCase();
      const matchesSource = sourceFilter === "all" || source === sourceFilter;
      const normalizedStatus = String(b.status || "").toLowerCase();
      const startDate = b.startDate ? new Date(b.startDate) : null;
      const isUpcoming =
        startDate &&
        !Number.isNaN(startDate.getTime()) &&
        startDate.getTime() >= Date.now() &&
        ["approved", "booked_confirmed", "confirmed"].includes(normalizedStatus);
      const needsRouteReview =
        String(b.routeEstimateSource || b.routeTravelTimeSource || "").toLowerCase() ===
          "inter_island_manual" ||
        /special|manual|inter.?island|ferry|barge/i.test(String(b.routeNote || "")) ||
        ["manual_review", "unavailable"].includes(String(b.deliveryFeeStatus || "").toLowerCase());
      const matchesQuickFilter =
        quickFilter === "paid_revenue"
          ? ["approved", "booked_confirmed", "confirmed", "completed"].includes(normalizedStatus)
          : quickFilter === "upcoming"
          ? Boolean(isUpcoming)
          : quickFilter === "route_review"
          ? Boolean(needsRouteReview)
          : true;
      return matchesSearch && matchesSource && matchesQuickFilter;
    };

    return bookingStatusFilters.reduce((counts, filter) => {
      counts[filter.key] = bookings.filter((booking) => {
        if (!baseMatches(booking)) return false;
        const isArchived = Boolean(booking.isArchived);
        if (filter.key === "archived") return isArchived;
        if (isArchived) return false;
        if (filter.key === "all") return true;
        if (filter.key === "extension_requests") return hasPendingExtensionRequest(booking);
        if (filter.key === "additional_payments") return hasAdditionalPaymentActionRequired(booking);
        if (filter.key === "active") return isActiveRental(booking);
        return getBookingStatusMeta(booking.status || "pending_approval").key === filter.key;
      }).length;
      return counts;
    }, {});
  }, [bookingStatusFilters, bookings, quickFilter, search, sourceFilter]);

  // Reset to page 1 whenever filter/search/source changes
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, search, sourceFilter, quickFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredBookings.length / rowsPerPage));
  const paginationStart = filteredBookings.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const paginationEnd = Math.min(currentPage * rowsPerPage, filteredBookings.length);
  const paginatedBookings = filteredBookings.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const selectedManualVehicle = useMemo(() => {
    return manualVehicles.find((vehicle) => vehicle._id === manualForm.vehicleId) || null;
  }, [manualVehicles, manualForm.vehicleId]);
  const manualPassengerLimit = getVehiclePassengerLimit(
    selectedManualVehicle,
    manualForm.rentalType
  );
  const manualPassengerCount = Number(manualForm.numberOfPax || 0);
  const manualPassengerError =
    manualPassengerLimit > 0 && manualPassengerCount > manualPassengerLimit
      ? `Passenger count cannot exceed ${manualPassengerLimit} for this rental type.`
      : "";
  const selectedManualVehicleUnavailable =
    selectedManualVehicle &&
    !["available", ""].includes(String(selectedManualVehicle.availabilityStatus || "").toLowerCase());

  const manualDays = useMemo(
    () => calculateDays(manualForm.startDate, manualForm.endDate),
    [manualForm.startDate, manualForm.endDate]
  );

  const manualRentalPricing = useMemo(
    () =>
      calculateRentalPricing({
        vehicle: selectedManualVehicle,
        pickupDateTime: buildLocalDateTime(manualForm.startDate, manualForm.startTime || "00:00"),
        returnDateTime: buildLocalDateTime(manualForm.endDate, manualForm.endTime || "00:00"),
        rentalType: manualForm.rentalType,
        paymentOption: manualForm.paymentOption,
      }),
    [
      selectedManualVehicle,
      manualForm.startDate,
      manualForm.endDate,
      manualForm.startTime,
      manualForm.endTime,
      manualForm.rentalType,
      manualForm.paymentOption,
    ]
  );

  const manualPreviewPricing = manualPreview?.valid ? manualPreview.pricing || {} : {};
  const manualPreviewExtraCharges = manualPreview?.valid ? manualPreview.extraCharges || {} : {};
  const manualBasePrice = Number(manualPreviewPricing.rentalSubtotal ?? manualRentalPricing.subtotal);
  const manualDiscount = Math.max(0, Number(manualForm.discount || 0));
  const manualDeliveryFee = Math.max(
    0,
    Number(manualPreviewExtraCharges.deliveryFee ?? (manualForm.deliveryFee || 0))
  );
  const manualExtraFee = Math.max(0, Number(manualForm.extraFee || 0));
  const manualTotalPrice = Number(
    manualPreviewPricing.totalPrice ??
      Math.max(0, manualBasePrice - manualDiscount + manualDeliveryFee + manualExtraFee)
  );
  const manualInvoiceAmount =
    Number(manualPreviewPricing.invoiceAmount) ||
    (manualForm.paymentOption === "full_payment"
      ? manualTotalPrice
      : Math.round(manualTotalPrice * 0.5 * 100) / 100);
  const manualAmountPaid = Math.max(0, Number(manualForm.amountPaid || 0));
  const manualRemainingBalance = Math.max(0, manualTotalPrice - manualAmountPaid);
  const isManualWithDriver = manualForm.rentalType !== "self_drive";
  const manualVehiclePickupLocation = getManualVehiclePickupLocation(selectedManualVehicle);
  const manualBookingPickupLocation = isManualWithDriver
    ? manualForm.pickupLocation
    : manualForm.vehicleHandoffOption === "delivery"
    ? manualForm.deliveryAddress
    : manualVehiclePickupLocation;
  const manualBookingPickupCoords = isManualWithDriver
    ? manualForm.pickupCoords
    : manualForm.vehicleHandoffOption === "delivery"
    ? manualForm.deliveryCoords
    : null;
  const manualBookingPickupPlaceId = isManualWithDriver
    ? manualForm.pickupPlaceId
    : manualForm.vehicleHandoffOption === "delivery"
    ? manualForm.deliveryPlaceId
    : "";
  const selectedManualContactCountry =
    COUNTRY_CODE_OPTIONS.find((option) => option.code === manualForm.contactCountryCode) ||
    COUNTRY_CODE_OPTIONS[0];
  const manualContactError = getContactNumberError(
    manualForm.contactCountryCode,
    manualForm.contactNumber
  );
  const manualBookingContact =
    combineInternationalContact(manualForm.contactCountryCode, manualForm.contactNumber) ||
    manualForm.contact;

  const buildManualBookingPayload = useCallback(() => ({
    customer: {
      fullName: manualForm.fullName,
      email: manualForm.email,
      contact: manualBookingContact,
      contactCountryCode: manualForm.contactCountryCode,
      contactNumber: manualForm.contactNumber,
      address: manualForm.address,
    },
    vehicleId: manualForm.vehicleId,
    startDate: manualForm.startDate,
    endDate: manualForm.endDate,
    startTime: manualForm.startTime,
    endTime: manualForm.endTime,
    pickupLocation: manualBookingPickupLocation,
    pickupCoords: manualBookingPickupCoords || null,
    pickupPlaceId: manualBookingPickupPlaceId || "",
    vehicleHandoffOption: isManualWithDriver ? "" : manualForm.vehicleHandoffOption,
    deliveryAddress:
      !isManualWithDriver && manualForm.vehicleHandoffOption === "delivery"
        ? manualForm.deliveryAddress
        : "",
    deliveryCoords:
      !isManualWithDriver && manualForm.vehicleHandoffOption === "delivery"
        ? manualForm.deliveryCoords || null
        : null,
    deliveryPlaceId:
      !isManualWithDriver && manualForm.vehicleHandoffOption === "delivery"
        ? manualForm.deliveryPlaceId || ""
        : "",
    destination: manualForm.destination,
    destinationCoords: manualForm.destinationCoords || null,
    destinationPlaceId: manualForm.destinationPlaceId || "",
    purposeOfTravel: manualForm.purposeOfTravel,
    rentalType: manualForm.rentalType,
    numberOfPax: manualForm.numberOfPax,
    luggageBags: Number(manualForm.luggageBags || 0),
    luggageSize: manualForm.luggageSize || "",
    luggageWeightKg:
      manualForm.luggageWeightKg === "" ? "" : Number(manualForm.luggageWeightKg),
    notes: manualForm.notes,
    discount: manualForm.discount,
    deliveryFee: manualForm.deliveryFee,
    extraFee: manualForm.extraFee,
    extraChargeRemarks: manualForm.extraChargeRemarks,
    adjustmentReason: manualForm.adjustmentReason,
    paymentMethod: manualForm.paymentMethod,
    paymentOption: manualForm.paymentOption,
    paymentStatus: manualForm.paymentStatus,
    amountPaid: manualForm.amountPaid,
    paymentReference: manualForm.paymentReference,
  }), [
    manualForm.fullName,
    manualForm.email,
    manualForm.contactCountryCode,
    manualForm.contactNumber,
    manualForm.address,
    manualForm.vehicleId,
    manualForm.startDate,
    manualForm.endDate,
    manualForm.startTime,
    manualForm.endTime,
    manualForm.vehicleHandoffOption,
    manualForm.deliveryAddress,
    manualForm.deliveryCoords,
    manualForm.deliveryPlaceId,
    manualForm.destination,
    manualForm.destinationCoords,
    manualForm.destinationPlaceId,
    manualForm.purposeOfTravel,
    manualForm.rentalType,
    manualForm.numberOfPax,
    manualForm.luggageBags,
    manualForm.luggageSize,
    manualForm.luggageWeightKg,
    manualForm.notes,
    manualForm.discount,
    manualForm.deliveryFee,
    manualForm.extraFee,
    manualForm.extraChargeRemarks,
    manualForm.adjustmentReason,
    manualForm.paymentMethod,
    manualForm.paymentOption,
    manualForm.paymentStatus,
    manualForm.amountPaid,
    manualForm.paymentReference,
    manualBookingContact,
    manualBookingPickupLocation,
    manualBookingPickupCoords,
    manualBookingPickupPlaceId,
    isManualWithDriver,
  ]);

  useEffect(() => {
    if (!showManualWizard || !manualForm.vehicleId || !manualForm.startDate || !manualForm.endDate) {
      setManualPreview(null);
      setManualPreviewError("");
      setManualPreviewLoading(false);
      return undefined;
    }

    const hasRouteFields =
      manualForm.destination.trim() &&
      ((!isManualWithDriver && manualForm.vehicleHandoffOption === "pickup") ||
        manualBookingPickupLocation.trim());

    if (!hasRouteFields || !Number(manualForm.numberOfPax || 0)) {
      setManualPreview(null);
      setManualPreviewError("");
      setManualPreviewLoading(false);
      return undefined;
    }

    const timer = window.setTimeout(async () => {
      try {
        setManualPreviewLoading(true);
        const res = await api.post("/bookings/manual/preview", buildManualBookingPayload());
        setManualPreview(res.data || null);
        setManualPreviewError(res.data?.valid ? "" : res.data?.message || "");
      } catch (e) {
        setManualPreview(null);
        setManualPreviewError(
          e?.response?.data?.message || "Unable to preview manual booking rules."
        );
      } finally {
        setManualPreviewLoading(false);
      }
    }, 450);

    return () => window.clearTimeout(timer);
  }, [
    showManualWizard,
    manualForm.vehicleId,
    manualForm.startDate,
    manualForm.endDate,
    manualForm.startTime,
    manualForm.endTime,
    manualForm.pickupLocation,
    manualForm.deliveryAddress,
    manualForm.vehicleHandoffOption,
    manualForm.destination,
    manualForm.rentalType,
    manualForm.numberOfPax,
    manualForm.discount,
    manualForm.deliveryFee,
    manualForm.extraFee,
    manualForm.paymentOption,
    manualBookingPickupLocation,
    isManualWithDriver,
    buildManualBookingPayload,
  ]);

  const updateManualForm = (key, value) => {
    setManualForm((prev) => {
      const next = {
        ...prev,
        [key]: value,
      };
      if (key === "contactCountryCode") {
        next.contactNumber = normalizeLocalPhoneNumberForCountry(prev.contactNumber, value);
        next.contact = combineInternationalContact(value, next.contactNumber);
      }
      if (key === "contactNumber") {
        next.contactNumber = normalizeLocalPhoneNumberForCountry(value, prev.contactCountryCode);
        next.contact = combineInternationalContact(prev.contactCountryCode, next.contactNumber);
      }
      if (key === "rentalType") {
        next.vehicleHandoffOption = value === "self_drive" ? prev.vehicleHandoffOption || "pickup" : "pickup";
        if (value !== "self_drive") next.deliveryFee = "";
      }
      if (key === "vehicleHandoffOption" && value !== "delivery") {
        next.deliveryFee = "";
      }
      if (key === "pickupLocation") {
        next.pickupCoords = null;
        next.pickupPlaceId = "";
      }
      if (key === "deliveryAddress") {
        next.deliveryCoords = null;
        next.deliveryPlaceId = "";
      }
      if (key === "destination") {
        next.destinationCoords = null;
        next.destinationPlaceId = "";
      }
      return next;
    });
  };

  const selectManualPlace = (field, coordsField, placeIdField) => ({ address, coords, placeId }) => {
    setManualForm((prev) => ({
      ...prev,
      [field]: address,
      [coordsField]: coords,
      [placeIdField]: placeId || "",
    }));
  };

  const selectManualCustomer = (customer) => {
    const contactParts = inferContactParts(customer.contact || "");
    setManualForm((prev) => ({
      ...prev,
      selectedCustomerId: customer._id,
      fullName: customer.name || "",
      email: customer.email || "",
      contact: combineInternationalContact(
        contactParts.contactCountryCode,
        contactParts.contactNumber
      ),
      contactCountryCode: contactParts.contactCountryCode,
      contactNumber: contactParts.contactNumber,
      address: customer.address || "",
      selectedCustomerVerificationStatus:
        customer.verificationStatus ||
        (customer.isVerified ? "verified" : "not_submitted"),
      selectedCustomerIsVerified: Boolean(customer.isVerified),
      selectedCustomerAccountSetupRequired: Boolean(customer.accountSetupRequired),
      customerSearch: customer.email || customer.name || "",
    }));
  };

  const openManualWizard = () => {
    setManualForm(initialManualForm);
    setManualStep(0);
    setManualCustomerResults([]);
    setManualVehicles([]);
    setShowManualWizard(true);
    setMsg("");
  };

  const closeManualWizard = () => {
    if (manualSubmitting) return;
    setShowManualWizard(false);
    setManualStep(0);
    setManualForm(initialManualForm);
  };

  const manualStepIsValid = () => {
    if (manualStep === 0) {
      return Boolean(
        manualForm.fullName.trim() &&
          manualForm.email.trim() &&
          !manualContactError
      );
    }
    if (manualStep === 1) {
      const schedErrs = validateAdminSchedule({
        startDate: manualForm.startDate,
        startTime: manualForm.startTime,
        endDate: manualForm.endDate,
        endTime: manualForm.endTime,
      });
      const scheduleOk = Object.keys(schedErrs).length === 0;
      return Boolean(
        scheduleOk &&
          manualForm.vehicleId &&
          !selectedManualVehicleUnavailable
      );
    }
    if (manualStep === 2) {
      return Boolean(
        (!isManualWithDriver || manualForm.pickupLocation.trim()) &&
          (isManualWithDriver ||
            manualForm.vehicleHandoffOption !== "delivery" ||
            manualForm.deliveryAddress.trim()) &&
          manualForm.destination.trim() &&
          manualForm.purposeOfTravel.trim() &&
          Number(manualForm.numberOfPax) > 0 &&
          !manualPassengerError &&
          !manualPreviewError
      );
    }
    if (manualStep === 3) {
      return manualDays > 0 && manualTotalPrice >= 0 && !manualPreviewLoading && !manualPreviewError;
    }
    if (manualStep === 4) {
      return manualAmountPaid <= manualTotalPrice;
    }
    return true;
  };

  const submitManualBooking = async () => {
    setMsg("");
    setManualSubmitting(true);

    try {
      const res = await api.post("/bookings/manual", buildManualBookingPayload());

      if (res.data.booking) {
        setBookings((prev) => [res.data.booking, ...prev]);
      } else {
        await load();
      }
      await load({ silent: true });
      window.dispatchEvent(new Event("notifications-refresh"));
      setMsg(res.data.message || "Manual booking created.");
      setShowManualWizard(false);
      setManualStep(0);
      setManualForm(initialManualForm);
    } catch (e) {
      console.error("Manual booking submit error:", e?.response?.data?.message || e.message);
      setMsg(e?.response?.data?.message || "Failed to create manual booking.");
    } finally {
      setManualSubmitting(false);
    }
  };

  const openDetailsModal = (booking, options = {}) => {
    if (options.focusExtension) {
      openExtensionReview(booking);
      return;
    }
    consumeBookingNavigation();
    setSelectedBooking(booking);
    setActiveBookingDetailsTab(options.focusPayment ? "payment" : "overview");
    setFocusAdditionalPayment(Boolean(options.focusPayment && hasReviewableAdditionalPayment(booking)));
    setHighlightExtensionSection(Boolean(options.focusExtension));
    setNoteDraft(booking?.notes || "");
    setAdminRemarksDraft(booking?.adminRemarks || "");
    setRefundRemarksDraft(booking?.refundRemarks || "");
    setDriverAssignmentMode(false);
    setSelectedDriverId("");
    if (booking?.withDriver) {
      loadAvailableDrivers(booking);
    }
    setShowDetailsModal(true);
  };

  const closeDetailsModal = () => {
    if (extensionReviewInFlightRef.current || verificationInFlightRef.current || confirmDialog?.type === "verify_additional") return false;
    setExtensionReviewTarget(null);
    consumeBookingNavigation();
    setFocusAdditionalPayment(false);
    setSelectedBooking(null);
    setNoteDraft("");
    setAdminRemarksDraft("");
    setRefundRemarksDraft("");
    setAvailableDrivers([]);
    setActiveBookingDetailsTab("overview");
    setHighlightExtensionSection(false);
    setDriverAssignmentMode(false);
    setSelectedDriverId("");
    setShowDetailsModal(false);
    setShowMoreActionsMenu(false);
    setConfirmDialog(null);
    setConfirmReason("");
    setDeleteConfirmText("");
    return true;
  };

  const changeStatusFilter = (filterKey) => {
    if (closeDetailsModal() === false) return;
    setQuickFilter("");
    setStatusFilter(filterKey);
  };

  const changeBookingPage = (page) => {
    consumeBookingNavigation();
    setCurrentPage(page);
  };

  const openConfirm = (opts) => {
    setConfirmError("");
    setConfirmReason("");
    setDeleteConfirmText("");
    setShowMoreActionsMenu(false);
    setConfirmDialog(opts);
  };

  const closeConfirm = () => {
    if (verificationInFlightRef.current) return;
    setConfirmError("");
    setConfirmDialog(null);
    setConfirmReason("");
    setDeleteConfirmText("");
  };

  useEffect(() => {
    if (!showDetailsModal || activeBookingDetailsTab !== "payment" || !focusAdditionalPayment) return;
    const frame = window.requestAnimationFrame(() => {
      const section = additionalPaymentSectionRef.current;
      if (section) {
        section.scrollIntoView({ block: "start", behavior: "auto" });
        section.focus({ preventScroll: true });
        setFocusAdditionalPayment(false);
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [showDetailsModal, activeBookingDetailsTab, focusAdditionalPayment, selectedBooking?._id]);

  useEffect(() => {
    if (confirmDialog?.type !== "verify_additional") return;
    const previousFocus = document.activeElement;
    const box = confirmationBoxRef.current;
    box?.querySelector("button")?.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        if (!verificationInFlightRef.current) setConfirmDialog(null);
      }
      if (event.key !== "Tab") return;
      const buttons = [...(box?.querySelectorAll("button:not(:disabled)") || [])];
      if (!buttons.length) { event.preventDefault(); box?.focus(); return; }
      const first = buttons[0];
      const last = buttons[buttons.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [confirmDialog]);

  const submitAdditionalPaymentConfirmation = async () => {
    if (verificationInFlightRef.current || isSubmittingAction || confirmDialog?.type !== "verify_additional") return;
    verificationInFlightRef.current = true;
    setConfirmError("");
    try {
      const result = await confirmDialog.onConfirm();
      if (result?.ok) setConfirmDialog(null);
      else setConfirmError(result?.message || "Failed to verify additional payment. Please try again.");
    } catch (error) {
      setConfirmError(error?.response?.data?.message || "Failed to verify additional payment. Please try again.");
    } finally {
      verificationInFlightRef.current = false;
    }
  };

  const selectedPaymentProofUrl = getBookingPaymentProofUrl(selectedBooking);
  const selectedRouteDataReadiness = getBookingRouteDataReadiness(selectedBooking);
  const assessmentBalanceValue = selectedBooking
    ? getFinancialSummaryForBooking(selectedBooking)?.finalBalance
    : null;
  const assessmentRemainingBalance = (
    typeof assessmentBalanceValue === "number" ||
    (typeof assessmentBalanceValue === "string" && assessmentBalanceValue.trim() !== "")
  ) &&
    Number.isFinite(Number(assessmentBalanceValue)) &&
    Number(assessmentBalanceValue) >= 0
      ? Number(assessmentBalanceValue)
      : null;

  if (!canAccessPage) {
    return null;
  }

  const bookingsLoadingTitle = returnAssessmentSubmitting
    ? "Processing return assessment..."
    : isSubmittingAction
    ? showChangeVehicleModal
      ? "Changing vehicle..."
      : "Updating booking..."
    : "";

  return (
    <AdminShell
      eyebrow="BOOKINGS"
      title="Booking Management"
      subtitle="Review trip requests, invoices, payment proofs, extensions, and completion actions."
      pageClassName="admin-shell--bookings"
      actions={
        <div className="booking-head-actions">
          <button type="button" className="btn btn-primary" onClick={openManualWizard}>
            New Manual Booking
          </button>
        </div>
      }
    >
      <AdminLoadingOverlay
        open={Boolean(bookingsLoadingTitle)}
        title={bookingsLoadingTitle}
        message="Please wait while FleetX updates the booking record."
      />
      {msg && <div className="dashboard-alert">{msg}</div>}

      <div className="booking-toolbar-modern-exact">
        <div className="booking-toolbar-controls">
          <input
            className="input booking-search-exact"
            placeholder="Search customer, vehicle, plate, or invoice"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <select
            className="input booking-source-filter"
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
          >
            <option value="all">All Sources</option>
            <option value="online">Online</option>
            <option value="manual">Manual</option>
          </select>
        </div>

        <div className="booking-status-tabs" aria-label="Filter bookings by status">
          {bookingStatusFilters.map((filter) => {
            const count = tabCounts[filter.key] || 0;
            const needsAttention = ["payment_verification", "extension_requests", "additional_payments"].includes(filter.key) && count > 0;
            return (
              <button
                key={filter.key}
                type="button"
                className={`booking-status-tab ${
                  statusFilter === filter.key ? "is-active" : ""
                } ${needsAttention ? "has-attention" : ""}`}
                data-tone={filter.tone}
                onClick={() => changeStatusFilter(filter.key)}
              >
                <span>{filter.label}</span>
                {count > 0 ? (
                  <b className={`booking-status-tab__count ${needsAttention ? "is-attention" : ""}`}>
                    {count}
                  </b>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      <div className="booking-exact-panel">
        <div className="dashboard-table-wrap">
          <table className="booking-exact-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Vehicle</th>
                <th>Dates</th>
                <th>Billing Document</th>
                <th>Payment</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {filteredBookings.length === 0 ? (
                <tr>
                  <td colSpan="7" className="dashboard-empty-cell">
                    {getFilterEmptyMessage(statusFilter)}
                  </td>
                </tr>
              ) : (
                paginatedBookings.map((b) => {
                  const bStatus = String(b.status || "").toLowerCase();
                  const statusKey = getBookingStatusMeta(bStatus).key;
                  const isArchivedBooking = Boolean(b.isArchived);
                  const isPaymentVerification = statusKey === "payment_verification";
                  const canReviewSubmittedPayment = isPaymentVerificationActionable(b);
                  const isAwaitingPayment = statusKey === "awaiting_payment";
                  const isConfirmed = statusKey === "confirmed";
                  const isActiveOngoing = statusKey === "active";
                  const isTerminal = ["completed", "cancelled"].includes(statusKey);
                  const billingDocument = getBookingManagementDocument(b);

                  return (
                    <tr key={b._id} className="booking-exact-row">
                      <td>
                        <div className="booking-exact-customer">
                          <div className="booking-exact-name booking-exact-name--inline">
                            {b.fullName || "N/A"}
                            {String(b.source || "online").toLowerCase() === "manual" ? (
                              <span className="booking-source-badge">Manual</span>
                            ) : null}
                          </div>
                          <div className="booking-exact-sub">{b.contact || "No contact"}</div>
                          {statusFilter === "archived" ? (
                            <div className="booking-exact-sub">{b.bookingReference || "No reference"}</div>
                          ) : null}
                        </div>
                      </td>

                      <td>
                        {b.vehicleId ? (
                          <>
                            <div className="booking-exact-name">
                              {getVehicleName(b.vehicleId)}
                            </div>
                            {getVehicleIdentifierText(b.vehicleId) ? (
                              <div className="booking-exact-sub booking-vehicle-identifier">
                                {getVehicleIdentifierText(b.vehicleId)}
                              </div>
                            ) : null}
                            {getVehicleDetailLine(b.vehicleId) ? (
                              <div className="booking-exact-sub">
                                {getVehicleDetailLine(b.vehicleId)}
                              </div>
                            ) : null}
                          </>
                        ) : (
                          <div className="booking-exact-sub">No vehicle</div>
                        )}
                      </td>

                      <td>
                        <div className="booking-cell-stack booking-cell-stack--dates">
                          <strong>{formatDate(b.startDate)}</strong>
                          <span>
                            {statusFilter === "extension_requests" && hasPendingExtensionRequest(b)
                              ? `Requested: ${formatDate(
                                  (b.extensionRequests || []).find(
                                    (request) => String(request.status || "").toLowerCase() === "pending"
                                  )?.requestedEndDate || b.endDate
                                )}`
                              : formatDate(b.endDate)}
                          </span>
                        </div>
                      </td>

                      <td>
                        <div className="booking-cell-stack">
                          <strong>{billingDocument.number}</strong>
                          <span>{billingDocument.type} {formatPeso(billingDocument.amount)}</span>
                        </div>
                      </td>

                      <td>
                        <div className="booking-cell-stack">
                          <strong>{prettyStatus(b.paymentStatus || "not_submitted")}</strong>
                          <span>
                            {formatDateTime(
                              b.paymentDueAt ||
                                (String(b.source || 'online').toLowerCase() === 'manual'
                                  ? b.createdAt
                                  : null)
                            )}
                          </span>
                          <PaymentDeadlineBadge booking={b} now={clockNow} compact />
                        </div>
                      </td>

                      <td className="booking-status-cell">
                        {isArchivedBooking ? (
                          <>
                            <span className="booking-status booking-status--cancelled">Archived</span>
                            <div className="booking-exact-sub">
                              Original: {getBookingStatusLabel(String(b.originalStatus || b.status || "pending_approval"))}
                            </div>
                            <div className="booking-exact-sub">
                              Archived: {formatDateTime(b.archivedAt)}
                            </div>
                            <div className="booking-exact-sub">
                              By: {getArchivedByLabel(b)}
                            </div>
                            {b.archiveReason ? (
                              <div className="booking-exact-sub">Reason: {b.archiveReason}</div>
                            ) : null}
                          </>
                        ) : (
                          <span className={getBookingStatusClass(String(b.status || "pending_approval"))}>
                            {getBookingStatusLabel(String(b.status || "pending_approval"))}
                          </span>
                        )}
                        {!isArchivedBooking && isPaymentVerification ? (
                          <div className="booking-exact-sub">Temporarily reserved</div>
                        ) : null}
                        <div className="booking-row-badges">
                          {bStatus !== "completed" && hasPendingExtensionRequest(b) ? (
                            <span className="booking-row-badge booking-row-badge--attention">Extension Pending</span>
                          ) : null}
                          {hasPaymentAdjustmentRequired(b) ? (
                            <span className="booking-row-badge booking-row-badge--warning">Adjustment Required</span>
                          ) : null}
                          {hasAdditionalPaymentActionRequired(b) || hasCompletedOutstandingBalance(b) ? (
                            <span className="booking-row-badge booking-row-badge--attention">
                              {getAdditionalPaymentStatus(b) === "under_review"
                                ? "Additional Payment Review"
                                : "Additional Payment Required"}
                            </span>
                          ) : null}
                        </div>
                      </td>

                      <td>
                        <div className="booking-exact-actions">
                          {/*
                            One primary action per row. "View Details" is only shown when no
                            contextual modal-opener already covers the same destination.
                            Rules:
                              canReviewSubmittedPayment → "Review Payment" (focusPayment) — no View
                              isConfirmed || isActiveOngoing → "Manage" (focusExtension if pending) — no View
                              pending extension tab → "Review Extension" (focusExtension) — no View
                              additional payment under_review → "Review Payment" (focusPayment) — no View
                              pending_approval → "View Booking" + "Issue Invoice" (Issue Invoice is a direct API call)
                              awaiting_payment → "View Invoice" + optional Cancel
                              terminal / other → "View Details"
                          */}

                          {/* Payment Verification: only Review Payment — covers both view and payment review */}
                          {isArchivedBooking ? (
                            <button
                              type="button"
                              className="booking-action-btn booking-action-btn--secondary"
                              onClick={() => openDetailsModal(b, {})}
                              title="View archived booking"
                            >
                              View Archive
                            </button>
                          ) : canReviewSubmittedPayment ? (
                            <button
                              type="button"
                              className="booking-action-btn booking-action-btn--primary"
                              onClick={() => openDetailsModal(b, { focusPayment: true })}
                              title="Review submitted payment proof"
                              disabled={isSubmittingAction}
                            >
                              Review Payment
                            </button>
                          ) : hasReviewableAdditionalPayment(b) ? (
                            <button
                              type="button"
                              className="booking-action-btn booking-action-btn--primary"
                              onClick={() => openDetailsModal(b, { focusPayment: true })}
                              title="Review additional payment proof"
                              disabled={isSubmittingAction}
                            >
                              Review Payment
                            </button>
                          ) : hasCompletedOutstandingBalance(b) ? (
                            <button
                              type="button"
                              className="booking-action-btn booking-action-btn--primary"
                              onClick={() => openSettlementModal(b)}
                              title="Record a final payment"
                              disabled={isSubmittingAction}
                            >
                              Settle Payment
                            </button>
                          ) : (isConfirmed || isActiveOngoing) ? (
                            /* Confirmed / Active: Manage opens modal with extension focus if needed */
                            <button
                              type="button"
                              className="booking-action-btn booking-action-btn--primary"
                              onClick={() =>
                                openDetailsModal(b, {
                                  focusExtension: hasPendingExtensionRequest(b),
                                })
                              }
                              title="Manage booking"
                              disabled={isSubmittingAction}
                            >
                              Manage
                            </button>
                          ) : (statusFilter === "extension_requests" || hasPendingExtensionRequest(b)) && !isTerminal ? (
                            /* Extension pending: open the review modal for the pending request */
                            <button
                              type="button"
                              className="booking-action-btn booking-action-btn--primary"
                              onClick={() => openDetailsModal(b, { focusExtension: true })}
                              title="Review extension request"
                              disabled={isSubmittingAction}
                            >
                              Review Extension
                            </button>
                          ) : bStatus === "pending_approval" ? (
                            /* Pending approval: View Booking for details + Issue Invoice as direct API action */
                            <button
                              type="button"
                              className="booking-action-btn booking-action-btn--secondary"
                              onClick={() => openDetailsModal(b, {})}
                              title="View booking details"
                            >
                              View Booking
                            </button>
                          ) : isAwaitingPayment ? (
                            /* Awaiting payment: View Invoice for details */
                            <button
                              type="button"
                              className="booking-action-btn booking-action-btn--secondary"
                              onClick={() => openDetailsModal(b, {})}
                              title="View invoice and booking details"
                            >
                              View Invoice
                            </button>
                          ) : (
                            /* Terminal (completed/cancelled) and all other statuses */
                            <button
                              type="button"
                              className="booking-action-btn booking-action-btn--secondary"
                              onClick={() => openDetailsModal(b, {})}
                              title="View booking details"
                            >
                              View Details
                            </button>
                          )}

                          {/* Booking review (pending_approval): Issue Invoice — direct API call, kept as secondary action */}
                          {!isArchivedBooking && bStatus === "pending_approval" ? (
                            <button
                              type="button"
                              className="booking-action-btn booking-action-btn--success"
                              onClick={() =>
                                patchBooking(
                                  `/bookings/${b._id}/status`,
                                  { status: "awaiting_payment", notes: b.notes || "", adminRemarks: b.adminRemarks || "" },
                                  "Invoice issued successfully."
                                )
                              }
                              title="Approve and issue invoice"
                              disabled={isSubmittingAction}
                            >
                              Issue Invoice
                            </button>
                          ) : null}

                          {/* Awaiting Payment: Cancel — kept as secondary destructive action */}
                          {!isArchivedBooking && isAwaitingPayment && canRejectBooking(b.status) ? (
                            <button
                              type="button"
                              className="booking-action-btn booking-action-btn--danger"
                              onClick={() =>
                                patchBooking(
                                  `/bookings/${b._id}/status`,
                                  { status: "cancelled", notes: b.notes || "", adminRemarks: b.adminRemarks || "" },
                                  "Booking cancelled."
                                )
                              }
                              title="Cancel booking"
                              disabled={isSubmittingAction}
                            >
                              Cancel
                            </button>
                          ) : null}

                          {/* Non-payment-verification, non-special statuses: generic Reject if applicable */}
                          {!isArchivedBooking && !isPaymentVerification && !isAwaitingPayment && !isConfirmed && !isActiveOngoing && !isTerminal && bStatus !== "pending_approval" && canRejectBooking(b.status) ? (
                            <button
                              type="button"
                              className="booking-action-btn booking-action-btn--danger"
                              onClick={() =>
                                patchBooking(
                                  `/bookings/${b._id}/status`,
                                  { status: "rejected", notes: b.notes || "", adminRemarks: b.adminRemarks || "" },
                                  "Booking rejected."
                                )
                              }
                              title="Reject booking"
                              disabled={isSubmittingAction}
                            >
                              Reject
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {filteredBookings.length > 0 && (
          <div className="booking-pagination">
            <div className="booking-pagination__info">
              Showing {paginationStart}–{paginationEnd} of {filteredBookings.length} results
            </div>

            <div className="booking-pagination__controls">
              <select
                className="booking-pagination__rows-select"
                value={rowsPerPage}
                aria-label="Rows per page"
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  changeBookingPage(1);
                }}
              >
                <option value={10}>10 / page</option>
                <option value={20}>20 / page</option>
                <option value={50}>50 / page</option>
              </select>

              <button
                type="button"
                className="booking-pagination__btn"
                onClick={() => changeBookingPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                aria-label="Previous page"
              >
                ← Prev
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((page) => {
                  if (totalPages <= 7) return true;
                  if (page === 1 || page === totalPages) return true;
                  if (Math.abs(page - currentPage) <= 1) return true;
                  return false;
                })
                .reduce((acc, page, idx, arr) => {
                  if (idx > 0 && page - arr[idx - 1] > 1) {
                    acc.push("ellipsis-" + page);
                  }
                  acc.push(page);
                  return acc;
                }, [])
                .map((item) =>
                  String(item).startsWith("ellipsis") ? (
                    <span key={item} className="booking-pagination__ellipsis">…</span>
                  ) : (
                    <button
                      key={item}
                      type="button"
                      className={`booking-pagination__btn${currentPage === item ? " is-active" : ""}`}
                      onClick={() => changeBookingPage(item)}
                      aria-label={`Page ${item}`}
                      aria-current={currentPage === item ? "page" : undefined}
                    >
                      {item}
                    </button>
                  )
                )}

              <button
                type="button"
                className="booking-pagination__btn"
                onClick={() => changeBookingPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                aria-label="Next page"
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>

      {showDetailsModal && selectedBooking ? (
        <div className="modal-overlay booking-modal-overlay" onClick={closeDetailsModal}>
          <div className="modal-box booking-details-modal-exact" onClick={(e) => e.stopPropagation()}>
            <div className="booking-details-modal__head">
              <div>
                <div className="booking-details-modal__title">Booking Details</div>
                <div className="booking-details-modal__reference">
                  {selectedBooking.bookingReference || selectedBooking._id?.slice(-6) || "Booking"}
                </div>
                <div className="booking-details-modal__badges">
                  <span className={getBookingStatusClass(selectedBooking.status)}>
                    {getBookingStatusLabel(selectedBooking.status)}
                  </span>
                  <span className={paymentBadgeClass(selectedBooking.paymentStatus)}>
                    {prettyStatus(selectedBooking.paymentStatus || "not_submitted")}
                  </span>
                  {selectedBooking.source ? (
                    <span className="booking-source-badge">
                      {getBookingSourceLabel(selectedBooking)}
                    </span>
                  ) : null}
                </div>
              </div>
              <button
                type="button"
                className="booking-details-modal__close"
                onClick={closeDetailsModal}
                aria-label="Close"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M2 2L14 14M14 2L2 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>

            <div className="booking-details-summary-strip">
              <div><span>Customer</span><strong>{selectedBooking.fullName || "N/A"}</strong></div>
              <div>
                <span>Vehicle</span>
                <strong>{getCompactVehicleLabel(selectedBooking.vehicleId) || getAdminVehicleName(selectedBooking)}</strong>
                {getVehicleDetailLine(selectedBooking.vehicleId) ? (
                  <small className="booking-summary-meta">{getVehicleDetailLine(selectedBooking.vehicleId)}</small>
                ) : null}
              </div>
              <div><span>Trip dates</span><strong>{formatDate(selectedBooking.startDate)} to {formatDate(selectedBooking.endDate)}</strong></div>
              <div><span>Total amount</span><strong>{formatPeso(selectedBooking.totalPrice)}</strong></div>
              <div><span>Payment</span><strong>{getPaymentOptionLabel(selectedBooking.paymentOption)} · {prettyStatus(selectedBooking.paymentStatus || "not_submitted")}</strong></div>
            </div>

            {selectedBooking.isArchived ? (
              <div className="booking-archive-banner">
                <strong>This booking is archived.</strong>
                <span>
                  Original status: {getBookingStatusLabel(selectedBooking.originalStatus || selectedBooking.status)}.
                  {" "}Archived {formatDateTime(selectedBooking.archivedAt)} by {getArchivedByLabel(selectedBooking)}.
                </span>
                {selectedBooking.archiveReason ? <small>{selectedBooking.archiveReason}</small> : null}
              </div>
            ) : null}

            {hasPendingExtensionRequest(selectedBooking) ? (
              <div className="booking-extension-alert">
                <div>
                  <strong>Extension request pending</strong>
                  <span>
                    Requested return: {formatDate(pendingExtensionRequest?.requestedEndDate)}{" "}
                    {pendingExtensionRequest?.requestedEndTime || ""}
                    {Number(pendingExtensionRequest?.additionalAmount || 0) > 0
                      ? ` · Additional amount ${formatPeso(pendingExtensionRequest.additionalAmount)}`
                      : ""}
                  </span>
                  {pendingExtensionRequest?.reason ? <small>{pendingExtensionRequest.reason}</small> : null}
                </div>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => openExtensionReview(selectedBooking)}
                >
                  Review Extension
                </button>
              </div>
            ) : null}

            {hasAdditionalPaymentActionRequired(selectedBooking) ? (
              <div className="booking-extension-alert">
                <div>
                  <strong>
                    {getAdditionalPaymentStatus(selectedBooking) === "under_review"
                      ? "Additional payment proof under review"
                      : "Additional payment required"}
                  </strong>
                  <span>
                    {getAdditionalPaymentReasonLabel(selectedBooking.additionalPayment?.reason)} ·{" "}
                    {formatPeso(getAdditionalPaymentAmounts(selectedBooking).totalAmountDue)}
                  </span>
                  {selectedBooking.additionalPayment?.rejectionRemarks ? (
                    <small>{selectedBooking.additionalPayment.rejectionRemarks}</small>
                  ) : null}
                </div>
              </div>
            ) : null}

            <div className="booking-details-tabs" role="tablist" aria-label="Booking details sections">
              {[
                ["overview", "Overview"],
                ["payment", "Billing"],
                ["notes", "Notes"],
                ["activity", "Activity"],
              ].map(([tabKey, label]) => (
                <button
                  key={tabKey}
                  type="button"
                  role="tab"
                  className={`booking-details-tab ${activeBookingDetailsTab === tabKey ? "is-active" : ""}`}
                  onClick={() => setActiveBookingDetailsTab(tabKey)}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="booking-details-modal__body" onClick={() => setShowMoreActionsMenu(false)}>
              {activeBookingDetailsTab === "overview" ? (
              <>
                <section className="booking-details-section">
                  <div className="booking-details-section__header">
                    <span>A</span>
                    <h4>Customer Info</h4>
                  </div>
                  <div className="booking-details-compact-grid">
                    <div><span>Customer name</span><strong>{selectedBooking.fullName || "N/A"}</strong></div>
                    <div><span>Email</span><strong>{selectedBooking.email || "N/A"}</strong></div>
                    {hasDetailValue(selectedBooking.contact) ? (
                      <div><span>Contact number</span><strong>{selectedBooking.contact}</strong></div>
                    ) : null}
                  </div>
                </section>

                <section className="booking-details-section">
                  <div className="booking-details-section__header">
                    <span>B</span>
                    <h4>Vehicle &amp; Rental</h4>
                  </div>
                  <div className="booking-details-compact-grid">
                    <div>
                      <span>Vehicle</span>
                      <strong>{getCompactVehicleLabel(selectedBooking.vehicleId) || getAdminVehicleName(selectedBooking)}</strong>
                      {getVehicleDetailLine(selectedBooking.vehicleId) ? (
                        <small className="booking-summary-meta">{getVehicleDetailLine(selectedBooking.vehicleId)}</small>
                      ) : null}
                    </div>
                    <div><span>Rental type</span><strong>{selectedBooking.withDriver ? "With Driver" : "Self Drive"}</strong></div>
                    <div><span>Handoff option</span><strong>{selectedBooking.vehicleHandoffOption || (selectedBooking.withDriver ? "N/A" : "pickup")}</strong></div>
                    <div><span>Passengers</span><strong>{selectedBooking.numberOfPax || "Not provided"}</strong></div>
                    <div>
                      <span>Luggage</span>
                      <strong>
                        {getLuggageDetailLines(selectedBooking).map((line, index, lines) => (
                          <span key={`${line}-${index}`}>
                            {line}
                            {index < lines.length - 1 ? <br /> : null}
                          </span>
                        ))}
                      </strong>
                    </div>
                  </div>
                </section>

                <section className="booking-details-section">
                  <div className="booking-details-section__header">
                    <span>C</span>
                    <h4>Trip Information</h4>
                  </div>
                  <div className="booking-details-compact-grid">
                    <div><span>Trip dates</span><strong>{formatDate(selectedBooking.startDate)} – {formatDate(selectedBooking.endDate)}</strong></div>
                    {hasDetailValue(selectedBooking.destination) ? (
                      <div><span>Destination</span><strong>{selectedBooking.destination}</strong></div>
                    ) : null}
                    {hasDetailValue(selectedBooking.pickupLocation) ? (
                      <div><span>Pickup location</span><strong>{selectedBooking.pickupLocation}</strong></div>
                    ) : null}
                    {selectedBooking.vehicleHandoffOption === "delivery" ? (
                      <div><span>Delivery address</span><strong>{selectedBooking.deliveryAddress || "Not specified"}</strong></div>
                    ) : null}
                    <div><span>Return arrangement</span><strong>{getReturnArrangementLabel(selectedBooking)}</strong></div>
                    {hasDetailValue(selectedBooking.returnPickupAddress) ? (
                      <div><span>Return pickup address</span><strong>{selectedBooking.returnPickupAddress}</strong></div>
                    ) : null}
                    {Number(selectedBooking.returnPickupFee || 0) > 0 ? (
                      <div><span>Return pickup fee</span><strong>{formatPeso(selectedBooking.returnPickupFee)}</strong></div>
                    ) : null}
                    {getReturnPickupFeeStatusLabel(selectedBooking) ? (
                      <div><span>Return pickup fee</span><strong>{getReturnPickupFeeStatusLabel(selectedBooking)}</strong></div>
                    ) : null}
                    {hasDetailValue(selectedBooking.returnNotes) ? (
                      <div><span>Return notes</span><strong>{selectedBooking.returnNotes}</strong></div>
                    ) : null}
                  </div>
                </section>

                <section className="booking-details-section">
                  <div className="booking-details-section__header">
                    <span>D</span>
                    <h4>Route Monitoring</h4>
                  </div>
                  <div className="booking-details-compact-grid">
                    <div>
                      <span>Origin</span>
                      <strong>{selectedRouteDataReadiness.originConfirmed ? "Confirmed location" : "Location selection required"}</strong>
                    </div>
                    <div>
                      <span>Destination</span>
                      <strong>{selectedRouteDataReadiness.destinationConfirmed ? "Confirmed location" : "Location selection required"}</strong>
                    </div>
                    <div>
                      <span>Status</span>
                      <strong>
                        {selectedRouteDataReadiness.ready
                          ? "Location data ready for future route planning"
                          : "Route monitoring unavailable"}
                      </strong>
                      <small className="booking-summary-meta">
                        {selectedRouteDataReadiness.ready
                          ? "Physical tracker and booking eligibility will be checked before activation."
                          : "Select missing locations from the location search. Existing booking operations remain available."}
                      </small>
                    </div>
                  </div>
                </section>

                <section className="booking-details-section">
                  <div className="booking-details-section__header">
                    <span>E</span>
                    <h4>Additional Information</h4>
                  </div>
                  <div className="booking-details-compact-grid">
                    <div><span>Booking amount</span><strong>{formatPeso(selectedBooking.totalPrice)}</strong></div>
                    <div><span>Payment option</span><strong>{getPaymentOptionLabel(selectedBooking.paymentOption)}</strong></div>
                    <div><span>Payment status</span><strong>{prettyStatus(selectedBooking.paymentStatus || "not_submitted")}</strong></div>
                    {selectedBooking.invoiceReference ? (
                      <div><span>Invoice ref</span><strong>{selectedBooking.invoiceReference}</strong></div>
                    ) : null}
                    {selectedBooking.source ? (
                      <div><span>Source</span><strong><span className="booking-source-badge">{getBookingSourceLabel(selectedBooking)}</span></strong></div>
                    ) : null}
                    {selectedBooking.createdBy ? (
                      <div><span>Created by</span><strong>{selectedBooking.createdBy.name || selectedBooking.createdBy.email} ({selectedBooking.createdBy.role || "staff"})</strong></div>
                    ) : null}
                  </div>
                </section>
              </>
              ) : null}

              {activeBookingDetailsTab === "payment" ? (() => {
                const financialSummary = getFinancialSummaryForBooking(selectedBooking);
                const billingDocuments = getBillingDocumentsForBooking(selectedBooking);
                const chargeRows = getBillingChargeRows(selectedBooking, financialSummary);
                const paymentRows = getBillingPaymentRows(selectedBooking, financialSummary);
                const grandTotal = getFinancialAmount(financialSummary, "grandTotal", selectedBooking.totalPrice);
                const returnChargesTotal = getFinancialAmount(financialSummary, "returnChargesTotal", 0);
                const paymentCycles = new Map();
                (selectedBooking.extensionRequests || []).forEach((request) => {
                  if (request.payment) paymentCycles.set(String(request._id), request.payment);
                });
                const currentPayment = selectedBooking.additionalPayment;
                if (currentPayment && !currentPayment.extensionRequestId) paymentCycles.set("current", currentPayment);
                else if (currentPayment?.extensionRequestId) paymentCycles.set(String(currentPayment.extensionRequestId), currentPayment);
                const underReviewAmount = [...paymentCycles.values()]
                  .filter((payment) => payment?.status === "under_review")
                  .reduce((total, payment) => total + Number(payment.submittedAmount || payment.totalAmountDue || payment.extensionAmount || 0), 0);
                const finalBalance = getFinancialAmount(financialSummary, "finalBalance", selectedBooking.remainingBalance || 0);
                const contractAccepted = Boolean(selectedBooking.contractAccepted);
                const contractAvailable = contractAccepted;
                return (
                <>
                  <section className="booking-details-section billing-redesign-section">
                    <div className="booking-details-section__header"><h4>Summary</h4></div>
                    <div className="billing-redesign-summary">
                      <article><span>Grand Total</span><strong>{formatPeso(grandTotal + returnChargesTotal)}</strong></article>
                      <article className="is-paid"><span>Paid</span><strong>{formatPeso(getFinancialAmount(financialSummary, "totalPaid", selectedBooking.amountPaid))}</strong></article>
                      <article className="is-review"><span>Under Review</span><strong>{formatPeso(underReviewAmount)}</strong></article>
                      <article className={finalBalance > 0 ? "is-due" : "is-paid"}><span>Remaining Balance</span><strong>{formatPeso(finalBalance)}</strong></article>
                    </div>
                  </section>

                  <section className="booking-details-section billing-redesign-section">
                    <div className="booking-details-section__header"><h4>Charges</h4></div>
                    <div className="billing-line-list">
                      <div className="billing-line-head"><span aria-hidden="true" /><strong>Charge</strong><strong>Description</strong><strong>Amount</strong></div>
                      {chargeRows.map((row, index) => <div className="billing-line-row" key={row.key}><span className="billing-line-index">{index + 1}</span><strong>{row.label}</strong><span>{row.description}</span><strong>{formatPeso(row.amount)}</strong></div>)}
                      <div className="billing-line-total"><strong>Grand Total</strong><strong>{formatPeso(grandTotal + returnChargesTotal)}</strong></div>
                    </div>
                  </section>

                  <section ref={additionalPaymentSectionRef} tabIndex={-1} aria-labelledby="billing-payments-title" className="booking-details-section billing-redesign-section additional-payment-review-section">
                    <div className="booking-details-section__header"><h4 id="billing-payments-title">Payments</h4></div>
                    <div className="billing-payment-list">
                      <div className="billing-payment-head"><strong>Payment</strong><strong>Status</strong><strong>Method</strong><strong>Date</strong><strong>Amount</strong></div>
                      {paymentRows.length ? paymentRows.map((payment, index) => <div className="billing-payment-row" key={`${payment.label}-${index}`}>
                        <strong>{payment.label}</strong><span className={`billing-status-pill ${payment.status.toLowerCase().replace(/\s+/g, "-")}`}>{payment.status}</span><span>{payment.method}</span><span>{payment.date ? formatDateTime(payment.date) : "—"}</span><strong>{formatPeso(payment.amount)}</strong>
                      </div>) : <div className="pmt-proof-empty">No payments have been submitted.</div>}
                    </div>
                    {underReviewAmount > 0 ? <div className="billing-review-notice">Additional payment proof is under review. We’ll confirm this payment after verifying the submitted proof.</div> : null}
                    {selectedBooking.additionalPayment?.proofUrl ? <div className="billing-proof-link"><span>{getPaymentProofTypeLabel(selectedBooking)}{selectedBooking.additionalPayment.paymentReference ? ` · Ref ${selectedBooking.additionalPayment.paymentReference}` : ""}</span><button type="button" className="pmt-proof-open-btn" onClick={() => window.open(getUploadUrl(selectedBooking.additionalPayment.proofUrl), "_blank")}>View proof</button></div> : null}
                    {selectedPaymentProofUrl ? <div className="billing-proof-link"><span>{getPaymentProofTypeLabel(selectedBooking)}{selectedBooking.paymentReference ? ` · Ref ${selectedBooking.paymentReference}` : ""}</span><button type="button" className="pmt-proof-open-btn" onClick={() => window.open(selectedPaymentProofUrl, "_blank")}>View proof</button></div> : null}
                    {selectedBooking.additionalPayment?.rejectionRemarks ? <div className="pmt-rejection-note"><span>Rejection remarks:</span> {selectedBooking.additionalPayment.rejectionRemarks}</div> : null}
                    {selectedBooking.returnAssessment?.settlementStatus ? <div className="billing-settlement-meta">Settlement: {settlementStatusLabel(selectedBooking.returnAssessment.settlementStatus)}{selectedBooking.returnAssessment.settlementMethod ? ` · ${settlementMethodLabel(selectedBooking.returnAssessment.settlementMethod)}` : ""}{Number(selectedBooking.returnAssessment.settlementAmount || 0) > 0 ? ` · ${formatPeso(selectedBooking.returnAssessment.settlementAmount)}` : ""}{selectedBooking.returnAssessment.settlementReference ? ` · Ref ${selectedBooking.returnAssessment.settlementReference}` : ""}</div> : null}
                    {finalBalance > 0 ? <div className="billing-settlement-meta">Final settlement is still due. Use “Record Settlement” to record payment.</div> : null}
                  </section>

                  <section className="booking-details-section billing-redesign-section">
                    <div className="booking-details-section__header"><h4>Documents</h4></div>
                  {billingDocuments.length ? (
                    <div className="billing-doc-list">
                      {billingDocuments.map((document) => {
                        const documentKey = document._uiKey || document.documentNumber || document.type;
                        const type = String(document.type || "").toLowerCase();
                        const storedPdfUrl = getStoredDocumentPdfUrl(document);
                        const canUseGenericDocument = canUseGenericDocumentEndpoint(document, selectedBooking);
                        const canViewDocument =
                          Boolean(document.html) ||
                          canUseLegacyDocumentEndpoint(document, selectedBooking) ||
                          canUseGenericDocument;
                        const canDownloadInvoice =
                          type === "booking_invoice" &&
                          canUseLegacyDocumentEndpoint(document, selectedBooking);
                        const canDownloadReceipt =
                          type === "payment_receipt" &&
                          Boolean(selectedBooking.receiptNumber) &&
                          canUseLegacyDocumentEndpoint(document, selectedBooking);
                        const canDownloadGeneric =
                          canUseGenericDocument &&
                          !canDownloadInvoice &&
                          !canDownloadReceipt &&
                          !storedPdfUrl;
                        return (
                          <article className="billing-doc-card" key={documentKey}>
                            <div className="billing-doc-card__main">
                              <span className={`billing-doc-type billing-doc-type--${type}`}>
                                {getDocumentTypeLabel(type)}
                              </span>
                              <strong>{safeDisplay(document.title, getDocumentTypeLabel(type))}</strong>
                              <small>{safeDisplay(document.documentNumber, "No document number")}</small>
                            </div>
                            <div className="billing-doc-card__meta">
                              <div><span>Issued</span><strong>{formatDateTime(document.issuedAt)}</strong></div>
                              <div><span>Amount</span><strong>{formatPeso(document.amount)}</strong></div>
                              {document.paymentType ? (
                                <div><span>Payment Type</span><strong>{getDocumentPaymentTypeLabel(document.paymentType)}</strong></div>
                              ) : null}
                              <div><span>Status</span><strong>{prettyStatus(document.status || "issued")}</strong></div>
                            </div>
                            <div className="billing-doc-card__actions">
                              {canViewDocument ? (
                                <button
                                  type="button"
                                  className="btn btn-soft pmt-doc-btn"
                                  onClick={() => handleViewBillingDocument(document, selectedBooking)}
                                  disabled={viewingDocumentId === documentKey}
                                >
                                  {viewingDocumentId === documentKey ? "Opening..." : "View"}
                                </button>
                              ) : null}
                              {canDownloadInvoice ? (
                                <button
                                  type="button"
                                  className="btn btn-soft pmt-doc-btn"
                                  onClick={() => handleDownloadInvoice(selectedBooking._id)}
                                  disabled={downloadingInvoiceId === selectedBooking._id}
                                >
                                  {downloadingInvoiceId === selectedBooking._id ? "Downloading..." : "Download PDF"}
                                </button>
                              ) : null}
                              {canDownloadReceipt ? (
                                <button
                                  type="button"
                                  className="btn btn-soft pmt-doc-btn"
                                  onClick={() => handleDownloadReceipt(selectedBooking._id)}
                                  disabled={downloadingReceiptId === selectedBooking._id}
                                >
                                  {downloadingReceiptId === selectedBooking._id ? "Downloading..." : "Download PDF"}
                                </button>
                              ) : null}
                              {canDownloadGeneric ? (
                                <button
                                  type="button"
                                  className="btn btn-soft pmt-doc-btn"
                                  onClick={() => handleDownloadBillingDocument(document, selectedBooking)}
                                  disabled={downloadingReceiptId === documentKey}
                                >
                                  {downloadingReceiptId === documentKey ? "Downloading..." : "Download PDF"}
                                </button>
                              ) : null}
                              {!canDownloadInvoice && !canDownloadReceipt && storedPdfUrl ? (
                                <a
                                  className="btn btn-soft pmt-doc-btn"
                                  href={storedPdfUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  Download PDF
                                </a>
                              ) : null}
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="pmt-proof-empty">No billing documents are available yet.</div>
                  )}

                  {selectedBooking.additionalPayment?.required || selectedBooking.additionalPayment?.invoiceReference ? (
                    <article className="billing-doc-card billing-additional-invoice-card">
                      <div className="billing-doc-card__main"><strong>Additional Invoice</strong><small>{safeDisplay(selectedBooking.additionalPayment.invoiceReference, "Additional payment invoice")}</small></div>
                      <div className="billing-doc-card__actions"><button type="button" className="btn btn-soft pmt-doc-btn" onClick={() => handleViewAdditionalInvoice(selectedBooking._id)} disabled={viewingDocumentId === `additional-${selectedBooking._id}`}>{viewingDocumentId === `additional-${selectedBooking._id}` ? "Opening..." : "View"}</button><button type="button" className="btn btn-soft pmt-doc-btn" onClick={() => handleDownloadAdditionalInvoice(selectedBooking._id)} disabled={downloadingInvoiceId === `additional-${selectedBooking._id}`}>{downloadingInvoiceId === `additional-${selectedBooking._id}` ? "Downloading..." : "Download"}</button></div>
                    </article>
                  ) : null}

                  <div className="pmt-doc-actions pmt-doc-actions--contract">
                    {contractAvailable ? (
                      <button
                        type="button"
                        className="btn btn-soft pmt-doc-btn"
                        onClick={() => handleDownloadContract(selectedBooking)}
                        disabled={downloadingContractId === selectedBooking._id}
                      >
                        {downloadingContractId === selectedBooking._id ? "Downloading..." : "Download Contract"}
                      </button>
                    ) : (
                      <span className="pmt-doc-pending">
                        Contract — Pending customer acceptance
                      </span>
                    )}
                  </div>

                  {/* Contract acceptance details */}
                  <div className="pmt-contract-status">
                    {contractAccepted ? (
                      <>
                        <span className="pmt-contract-chip pmt-contract-chip--accepted">&#10003; Accepted</span>
                        <span className="pmt-contract-meta">
                          {selectedBooking.contractAcceptedName ? `By ${selectedBooking.contractAcceptedName}` : ""}
                          {selectedBooking.contractAcceptedAt ? ` · ${formatDateTime(selectedBooking.contractAcceptedAt)}` : ""}
                          {selectedBooking.contractSignatureUrl
                            ? " · Drawn signature provided"
                            : " · Typed signature"}
                        </span>
                        {selectedBooking.contractSignatureUrl ? (
                          <div className="pmt-sig-preview">
                            <img
                              src={selectedBooking.contractSignatureUrl}
                              alt="Customer signature"
                              className="pmt-sig-img"
                              onClick={() => window.open(selectedBooking.contractSignatureUrl, "_blank")}
                              title="Click to open full signature"
                              style={{ cursor: "pointer" }}
                            />
                          </div>
                        ) : null}
                      </>
                    ) : (
                      <span className="pmt-contract-chip pmt-contract-chip--pending">Pending customer acceptance</span>
                    )}
                  </div>
                </section>

                </>
                );
              })() : null}

              {activeBookingDetailsTab === "activity" ? (
              <section className="booking-details-section">
                <div className="booking-details-section__header">
                  <span>E</span>
                  <h4>Activity &amp; Metadata</h4>
                </div>
                  <div className="booking-details-more__panel">
                    {hasMoreBookingDetails(selectedBooking) ? (
                    <div className="booking-details-compact-grid">
                      <div><span>Source</span><strong><span className="booking-source-badge">{getBookingSourceLabel(selectedBooking)}</span></strong></div>
                      {selectedBooking.createdBy ? (
                        <div><span>Created by</span><strong>{selectedBooking.createdBy.name || selectedBooking.createdBy.email} ({selectedBooking.createdBy.role || "staff"})</strong></div>
                      ) : null}
                      {selectedBooking.accountSetupRequired || selectedBooking.userId?.accountSetupRequired ? (
                        <div><span>Account setup</span><strong>Required</strong></div>
                      ) : null}
                      {selectedBooking.invoiceReference ? (
                        <div><span>Invoice ref</span><strong>{selectedBooking.invoiceReference}</strong></div>
                      ) : null}
                      {hasDetailValue(selectedBooking.paymentReference) ? (
                        <div><span>Payment ref</span><strong>{selectedBooking.paymentReference}</strong></div>
                      ) : null}
                      {hasDetailValue(selectedBooking.selectedPaymentCategory) ? (
                        <div><span>Payment category</span><strong>{selectedBooking.selectedPaymentCategory}</strong></div>
                      ) : null}
                      {hasPositiveAmount(selectedBooking.amountPaid) ? (
                        <div><span>Amount paid</span><strong>{formatPeso(selectedBooking.amountPaid)}</strong></div>
                      ) : null}
                      {(hasPositiveAmount(selectedBooking.remainingBalance) || String(selectedBooking.paymentOption || "").includes("down")) ? (
                        <div><span>Remaining balance</span><strong>{formatPeso(selectedBooking.remainingBalance)}</strong></div>
                      ) : null}
                      {isRefundRelevant(selectedBooking) ? (
                        <div><span>Refund status</span><strong>{prettyStatus(selectedBooking.refundStatus)}</strong></div>
                      ) : null}
                      {hasDetailValue(selectedBooking.adjustmentReason) ? (
                        <div className="booking-details-compact-grid__wide"><span>Adjustment reason</span><strong>{selectedBooking.adjustmentReason}</strong></div>
                      ) : null}
                      {hasDetailValue(selectedBooking.paymentAdjustmentStatus) && selectedBooking.paymentAdjustmentStatus !== "none" ? (
                        <div><span>Payment adjustment</span><strong>{prettyStatus(selectedBooking.paymentAdjustmentStatus)} ({formatPeso(selectedBooking.paymentAdjustmentAmount)})</strong></div>
                      ) : null}
                      {hasDetailValue(selectedBooking.paymentAdjustmentNote) ? (
                        <div className="booking-details-compact-grid__wide"><span>Adjustment note</span><strong>{selectedBooking.paymentAdjustmentNote}</strong></div>
                      ) : null}
                      {selectedBooking.modificationLogs?.length ? (
                        <div className="booking-details-compact-grid__wide">
                          <span>Last modified</span>
                          <strong>
                            {formatDateTime(selectedBooking.modificationLogs[selectedBooking.modificationLogs.length - 1]?.modifiedAt)} by{" "}
                            {selectedBooking.modificationLogs[selectedBooking.modificationLogs.length - 1]?.modifiedBy?.name ||
                              selectedBooking.modificationLogs[selectedBooking.modificationLogs.length - 1]?.modifiedBy?.email ||
                              "Admin"}{" "}
                            - {selectedBooking.modificationLogs[selectedBooking.modificationLogs.length - 1]?.reason}
                          </strong>
                        </div>
                      ) : null}
                      {hasDetailValue(selectedBooking.invoiceNotes) ? (
                        <div className="booking-details-compact-grid__wide"><span>Invoice notes</span><strong>{selectedBooking.invoiceNotes}</strong></div>
                      ) : null}
                      {hasDetailValue(selectedBooking.notes) ? (
                        <div className="booking-details-compact-grid__wide"><span>Customer notes</span><strong>{selectedBooking.notes}</strong></div>
                      ) : null}
                    </div>
                    ) : (
                      <div className="booking-details-more__empty">No extra booking details for this request.</div>
                    )}
                  </div>
              </section>
              ) : null}

              {activeBookingDetailsTab === "overview" && selectedBooking.withDriver ? (
                <div className="driver-assignment-card">
                  <div className="driver-assignment-card__head">
                    <div>
                      <span className="booking-source-badge">With Driver</span>
                      <h4>Driver Assignment</h4>
                      <div className="vehicle-exact-meta">
                        Availability checked for {formatDate(selectedBooking.startDate)} to{" "}
                        {formatDate(selectedBooking.endDate)}
                      </div>
                    </div>
                  </div>

                  {selectedBooking.driverId ? (
                    <div className="driver-assignment-profile">
                      {selectedBooking.driverId.profilePhoto ? (
                        <img
                          className="driver-assignment-photo"
                          src={selectedBooking.driverId.profilePhoto}
                          alt={selectedBooking.driverId.fullName}
                        />
                      ) : (
                        <div className="driver-assignment-photoFallback">
                          {(selectedBooking.driverId.fullName || "D").charAt(0).toUpperCase()}
                        </div>
                      )}

                      <div>
                        <div className="vehicle-exact-name">
                          {selectedBooking.driverId.fullName || "Assigned Driver"}
                        </div>
                        <div className="vehicle-exact-meta">
                          Assigned {formatDateTime(selectedBooking.assignedAt)}
                        </div>
                        <span className={driverStatusBadgeClass(selectedBooking.driverId.status)}>
                          {prettyStatus(selectedBooking.driverId.status || "assigned")}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <p className="driver-assignment-empty">
                      <strong>No driver assigned yet</strong>
                      <br />
                      Driver will be assigned before the trip.
                    </p>
                  )}

                  <div className="driver-assignment-actions">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => {
                        setDriverAssignmentMode(true);
                        loadAvailableDrivers();
                      }}
                    >
                      {selectedBooking.driverId ? "Change Driver" : "Assign Driver"}
                    </button>

                    {selectedBooking.driverId ? (
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={removeAssignedDriver}
                        disabled={isSubmittingAction}
                      >
                        Remove Driver
                      </button>
                    ) : null}
                  </div>

                  {driverAssignmentMode ? (
                    <div className="driver-assignment-workspace">
                      <div className="driver-assignment-left">
                        <div className="driver-assignment-panel-head">
                          <div>
                            <strong>Available Drivers</strong>
                            <span>{availableDrivers.length} valid match(es)</span>
                          </div>
                          <button type="button" className="btn btn-primary" onClick={smartAssignDriver}>
                            Smart Assign Driver
                          </button>
                        </div>

                        <div className="driver-dnd-list">
                          {assignmentDrivers.map((driver) => {
                            const issue = validateDriverCandidate(driver);
                            const valid = !issue;
                            return (
                              <article
                                key={driver._id}
                                className={`driver-dnd-card ${valid ? "is-valid" : "is-invalid"}`}
                                draggable={valid}
                                onDragStart={(event) => {
                                  event.dataTransfer.setData("text/driver-id", driver._id);
                                  event.dataTransfer.effectAllowed = "move";
                                  setDraggedDriverId(driver._id);
                                }}
                                onDragEnd={() => {
                                  setDraggedDriverId("");
                                  setDropActive(false);
                                }}
                                onClick={() => requestDriverAssignment(driver)}
                              >
                                {driver.profilePhoto ? (
                                  <img src={getUploadUrl(driver.profilePhoto)} alt={driver.fullName} />
                                ) : (
                                  <div>{(driver.fullName || "D").charAt(0).toUpperCase()}</div>
                                )}
                                <section>
                                  <strong>{driver.fullName}</strong>
                                  <span>{valid ? `Available ${formatDate(selectedBooking.startDate)}` : issue}</span>
                                  <small>{driver.contactNumber || "No contact number"} · {driver.assignmentCount || 0} assignment(s)</small>
                                  <div className="driver-mini-calendar" aria-label="Driver schedule preview">
                                    {[0, 1, 2, 3, 4, 5, 6].map((day) => (
                                      <i key={day} className={valid ? "" : "is-booked"} />
                                    ))}
                                  </div>
                                </section>
                                <em>{valid ? "Drag" : "Blocked"}</em>
                              </article>
                            );
                          })}
                          {assignmentDrivers.length === 0 ? (
                            <div className="dashboard-empty-cell">No drivers loaded.</div>
                          ) : null}
                        </div>
                      </div>

                      <div className="driver-assignment-right">
                        <div className="driver-booking-drop-details">
                          <strong>{selectedBooking.bookingReference || "Booking"}</strong>
                          <span>{selectedBooking.fullName}</span>
                          <span>{formatDate(selectedBooking.startDate)} to {formatDate(selectedBooking.endDate)}</span>
                          <span>
                            {getCompactVehicleLabel(selectedBooking.vehicleId) ||
                              getAdminVehicleName(selectedBooking)}
                          </span>
                        </div>

                        <div
                          className={`driver-drop-zone ${dropActive ? "is-active" : ""}`}
                          onDragOver={(event) => {
                            event.preventDefault();
                            event.dataTransfer.dropEffect = "move";
                            setDropActive(true);
                          }}
                          onDragLeave={() => setDropActive(false)}
                          onDrop={(event) => {
                            event.preventDefault();
                            const droppedId = event.dataTransfer.getData("text/driver-id") || draggedDriverId;
                            const driver = assignmentDrivers.find((item) => String(item._id) === String(droppedId));
                            setDropActive(false);
                            setDraggedDriverId("");
                            requestDriverAssignment(driver);
                          }}
                        >
                          <strong>Drop driver here</strong>
                          <span>We will validate license, status, and overlapping schedules before assigning.</span>
                        </div>

                        <button
                          type="button"
                          className="btn btn-soft"
                          onClick={() => {
                            setDriverAssignmentMode(false);
                            setSelectedDriverId("");
                          }}
                          disabled={isSubmittingAction}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {activeBookingDetailsTab === "overview" && canManageExtension(selectedBooking) ? (
                <section
                  className={`booking-details-section booking-extension-panel ${
                    highlightExtensionSection || hasPendingExtensionRequest(selectedBooking) ? "is-highlighted" : ""
                  }`}
                >
                  <div className="booking-details-section__header">
                    <span>E</span>
                    <h4>Booking Extension</h4>
                  </div>
                  <div className="booking-details-compact-grid">
                    <div>
                      <span>Status</span>
                      <strong>{prettyStatus(selectedBooking.extensionStatus || "none")}</strong>
                    </div>
                    <div>
                      <span>Current return</span>
                      <strong>
                        {formatDate(selectedBooking.endDate)} {selectedBooking.endTime || ""}
                      </strong>
                    </div>
                    {latestExtensionRequest ? (
                      <>
                        <div>
                          <span>Requested return</span>
                          <strong>
                            {formatDate(latestExtensionRequest.requestedEndDate)}{" "}
                            {latestExtensionRequest.requestedEndTime || ""}
                          </strong>
                        </div>
                        <div>
                          <span>Additional amount</span>
                          <strong>{formatPeso(latestExtensionRequest.additionalAmount)}</strong>
                        </div>
                        <div className="booking-details-compact-grid__wide">
                          <span>Reason / remarks</span>
                          <strong>
                            {latestExtensionRequest.reason || latestExtensionRequest.adminRemarks || "No remarks"}
                          </strong>
                        </div>
                      </>
                    ) : (
                      <div className="booking-details-compact-grid__wide">
                        <span>Request</span>
                        <strong>No extension request yet.</strong>
                      </div>
                    )}
                  </div>

                  {pendingExtensionRequest ? (
                    <div className="booking-extension-review">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => openExtensionReview(selectedBooking)}
                        disabled={isSubmittingAction}
                      >
                        Review Extension
                      </button>
                    </div>
                  ) : null}
                </section>
              ) : null}

              {activeBookingDetailsTab === "overview" && selectedBooking.returnAssessment?.assessedAt ? (
                <section className="booking-details-section booking-return-assessment-panel">
                  <div className="booking-details-section__header">
                    <span>R</span>
                    <h4>Return Assessment</h4>
                  </div>
                  <ReturnConditionSummary assessment={selectedBooking.returnAssessment} />
                  <div className="booking-details-compact-grid">
                    <div>
                      <span>Status</span>
                      <strong>{selectedBooking.returnAssessment.isLate ? "Late return" : "On time"}</strong>
                    </div>
                    <div>
                      <span>Expected return</span>
                      <strong>{formatDateTime(selectedBooking.returnAssessment.expectedReturnAt)}</strong>
                    </div>
                    <div>
                      <span>Actual return</span>
                      <strong>{formatDateTime(selectedBooking.returnAssessment.actualReturnAt)}</strong>
                    </div>
                    <div>
                      <span>Grace period</span>
                      <strong>{formatHours(selectedBooking.returnAssessment.gracePeriodHours)}</strong>
                    </div>
                    <div>
                      <span>Extra duration</span>
                      <strong>{formatHours(selectedBooking.returnAssessment.extraHours)}</strong>
                    </div>
                    <div>
                      <span>Billable late days</span>
                      <strong>{selectedBooking.returnAssessment.lateDays || 0}</strong>
                    </div>
                    <div>
                      <span>Additional rental</span>
                      <strong>{formatPeso(selectedBooking.returnAssessment.additionalRentalAmount)}</strong>
                    </div>
                    {Number(selectedBooking.returnAssessment.damageFee || 0) > 0 ? (
                      <div>
                        <span>Damage fee</span>
                        <strong>{formatPeso(selectedBooking.returnAssessment.damageFee)}</strong>
                      </div>
                    ) : null}
                    {Number(selectedBooking.returnAssessment.fuelAdjustment || 0) > 0 ? (
                      <div>
                        <span>Fuel adjustment</span>
                        <strong>{formatPeso(selectedBooking.returnAssessment.fuelAdjustment)}</strong>
                      </div>
                    ) : null}
                    {Number(selectedBooking.returnAssessment.cleaningFee || 0) > 0 ? (
                      <div>
                        <span>Cleaning fee</span>
                        <strong>{formatPeso(selectedBooking.returnAssessment.cleaningFee)}</strong>
                      </div>
                    ) : null}
                    {Number(selectedBooking.returnAssessment.otherFee || 0) > 0 ? (
                      <div>
                        <span>Other fee</span>
                        <strong>{formatPeso(selectedBooking.returnAssessment.otherFee)}</strong>
                      </div>
                    ) : null}
                    <div>
                      <span>Late penalty</span>
                      <strong>{formatPeso(selectedBooking.returnAssessment.penaltyAmount)}</strong>
                    </div>
                    <div>
                      <span>Total additional due</span>
                      <strong>{formatPeso(selectedBooking.returnAssessment.totalAdditionalAmount)}</strong>
                    </div>
                    <div>
                      <span>Assessed by</span>
                      <strong>
                        {selectedBooking.returnAssessment.assessedBy?.name ||
                          selectedBooking.returnAssessment.assessedBy?.email ||
                          "Admin"}{" "}
                        on {formatDateTime(selectedBooking.returnAssessment.assessedAt)}
                      </strong>
                    </div>
                    {hasDetailValue(selectedBooking.returnAssessment.penaltyOverrideRemarks) ? (
                      <div className="booking-details-compact-grid__wide">
                        <span>Remarks</span>
                        <strong>{selectedBooking.returnAssessment.penaltyOverrideRemarks}</strong>
                      </div>
                    ) : null}
                  </div>
                  {getFinancialAmount(getFinancialSummaryForBooking(selectedBooking), "finalBalance", selectedBooking.remainingBalance) > 0 ? (
                    <div style={{ marginTop: "16px" }}>
                      <div className="booking-details-section__subheader" style={{ fontWeight: 800, fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 8 }}>
                        Final Settlement
                      </div>
                      <div className="booking-details-compact-grid">
                        <div>
                          <span>Settlement Status</span>
                          <strong>
                            {selectedBooking.returnAssessment.settlementStatus
                              ? settlementStatusLabel(selectedBooking.returnAssessment.settlementStatus)
                              : <span style={{ color: "#f97316" }}>Not recorded</span>}
                          </strong>
                        </div>
                        {selectedBooking.returnAssessment.settlementMethod ? (
                          <div>
                            <span>Method</span>
                            <strong>{settlementMethodLabel(selectedBooking.returnAssessment.settlementMethod)}</strong>
                          </div>
                        ) : null}
                        {Number(selectedBooking.returnAssessment.settlementAmount || 0) > 0 ? (
                          <div>
                            <span>Amount</span>
                            <strong>{formatPeso(selectedBooking.returnAssessment.settlementAmount)}</strong>
                          </div>
                        ) : null}
                        {selectedBooking.returnAssessment.settlementReference ? (
                          <div>
                            <span>Reference</span>
                            <strong>{selectedBooking.returnAssessment.settlementReference}</strong>
                          </div>
                        ) : null}
                        {selectedBooking.returnAssessment.settlementRecordedAt ? (
                          <div>
                            <span>Recorded at</span>
                            <strong>{formatDateTime(selectedBooking.returnAssessment.settlementRecordedAt)}</strong>
                          </div>
                        ) : null}
                        {selectedBooking.returnAssessment.settlementNotes ? (
                          <div className="booking-details-compact-grid__wide">
                            <span>Notes</span>
                            <strong>{selectedBooking.returnAssessment.settlementNotes}</strong>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                </section>
              ) : null}


              {activeBookingDetailsTab === "notes" ? (
              <section className="booking-details-section">
                <div className="booking-details-section__header">
                  <span>D</span>
                  <h4>Notes & Admin Remarks</h4>
                </div>

                <div className="booking-details-notes-grid">
                  <div className="booking-details-notes">
                    <label className="booking-details-notes__label">Booking notes</label>
                    <textarea
                      className="booking-details-notes__textarea"
                      placeholder="Add booking notes..."
                      value={noteDraft}
                      onChange={(e) => setNoteDraft(e.target.value)}
                    />
                  </div>

                  <div className="booking-details-notes">
                    <label className="booking-details-notes__label">Admin remarks</label>
                    <textarea
                      className="booking-details-notes__textarea"
                      placeholder="Add admin remarks..."
                      value={adminRemarksDraft}
                      onChange={(e) => setAdminRemarksDraft(e.target.value)}
                    />
                  </div>
                </div>

                {isRefundRelevant(selectedBooking) ? (
                  <div className="booking-details-notes">
                    <label className="booking-details-notes__label">Refund remarks</label>
                    <textarea
                      className="booking-details-notes__textarea"
                      placeholder="Add refund remarks..."
                      value={refundRemarksDraft}
                      onChange={(e) => setRefundRemarksDraft(e.target.value)}
                    />
                  </div>
                ) : null}
                <button
                  type="button"
                  className="btn btn-soft booking-details-notes-save"
                  onClick={saveNoteOnly}
                  disabled={isSubmittingAction}
                >
                  Save Admin Notes
                </button>
              </section>
              ) : null}

            </div>

            <section className="booking-details-section booking-details-section--actions">
                <div className="booking-details-actions">
                  <div className="booking-details-actions__meta">
                    {selectedBooking.createdAt ? (
                      <span>Created {formatDateTime(selectedBooking.createdAt)}</span>
                    ) : null}
                    {selectedBooking.updatedAt ? (
                      <span>Updated {formatDateTime(selectedBooking.updatedAt)}</span>
                    ) : null}
                  </div>

                  <div className="booking-details-actions__buttons">
                    {/* Always visible: Close */}
                    <button
                      type="button"
                      className="btn btn-soft"
                      onClick={closeDetailsModal}
                      disabled={isSubmittingAction}
                    >
                      Close
                    </button>

                    {/* Booking review: Reject Request + Approve & Issue Invoice */}
                    {canApproveBooking(selectedBooking.status) ? (
                      <>
                        {canRejectBooking(selectedBooking.status) ? (
                          <button
                            type="button"
                            className="btn btn-danger-outline"
                            disabled={isSubmittingAction}
                            onClick={() => openConfirm({
                              type: "reject_request",
                              title: "Reject Booking Request",
                              message: "This will reject the booking and notify the customer.",
                              requireReason: true,
                              reasonLabel: "Reason for rejection",
                              reasonPlaceholder: "Add a short reason so the customer knows what happened.",
                              confirmLabel: "Reject Request",
                              confirmClass: "btn btn-danger",
                              loadingLabel: "Rejecting...",
                              onConfirm: (reason) => patchBooking(
                                `/bookings/${selectedBooking._id}/status`,
                                { status: "rejected", notes: noteDraft, adminRemarks: reason || adminRemarksDraft },
                                "Booking rejected."
                              ),
                            })}
                          >
                            Reject Request
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="btn btn-success booking-details-action-primary"
                          disabled={isSubmittingAction}
                          onClick={() => openConfirm({
                            type: "approve_invoice",
                            title: "Approve Booking & Issue Invoice",
                            message: "This will approve the booking and send an invoice to the customer.",
                            requireReason: false,
                            confirmLabel: "Approve & Issue Invoice",
                            confirmClass: "btn btn-success",
                            loadingLabel: "Approving...",
                            onConfirm: () => patchBooking(
                              `/bookings/${selectedBooking._id}/status`,
                              { status: "awaiting_payment", notes: noteDraft, adminRemarks: adminRemarksDraft },
                              "Invoice issued successfully."
                            ),
                          })}
                        >
                          Approve &amp; Issue Invoice
                        </button>
                      </>
                    ) : null}

                    {/* Payment verification: Request New Proof · Reject Proof · Verify Payment */}
                    {isPaymentVerificationActionable(selectedBooking) ? (
                      <>
                        <button
                          type="button"
                          className="btn btn-warning-outline"
                          disabled={isSubmittingAction}
                          onClick={() => openConfirm({
                            type: "request_reupload",
                            title: "Request New Proof",
                            message: "The customer will be asked to re-upload their payment proof.",
                            requireReason: true,
                            reasonLabel: "What should the customer fix?",
                            reasonPlaceholder: "Add a short reason so the customer knows what to fix.",
                            confirmLabel: "Send Request",
                            confirmClass: "btn btn-warning",
                            loadingLabel: "Sending request...",
                            onConfirm: (reason) => patchBooking(
                              `/bookings/${selectedBooking._id}/payment/request-reupload`,
                              { adminRemarks: reason || adminRemarksDraft },
                              "Customer asked to re-upload payment proof."
                            ),
                          })}
                        >
                          Request New Proof
                        </button>
                        <button
                          type="button"
                          className="btn btn-danger-outline"
                          disabled={isSubmittingAction}
                          onClick={() => openConfirm({
                            type: "reject_payment",
                            title: "Reject payment proof?",
                            message: "Please provide a reason so the customer knows what to correct before re-uploading.",
                            requireReason: true,
                            reasonLabel: "Rejection reason",
                            reasonPlaceholder: "Add a short reason so the customer knows what to fix.",
                            confirmLabel: "Reject Proof",
                            confirmClass: "btn btn-danger",
                            loadingLabel: "Rejecting...",
                            onConfirm: (reason) => patchBooking(
                              `/bookings/${selectedBooking._id}/payment/reject`,
                              { adminRemarks: reason },
                              "Payment rejected."
                            ),
                          })}
                        >
                          Reject Proof
                        </button>
                        <button
                          type="button"
                          className="btn btn-success booking-details-action-primary"
                          disabled={isSubmittingAction}
                          onClick={() => openConfirm({
                            type: "verify_payment",
                            title: "Approve payment proof?",
                            message: "You are about to approve this payment proof. This will mark the payment as verified, confirm the booking, and notify the customer if notifications are enabled.",
                            requireReason: false,
                            confirmLabel: "Approve Payment",
                            confirmClass: "btn btn-success",
                            loadingLabel: "Approving...",
                            onConfirm: () => patchBooking(
                              `/bookings/${selectedBooking._id}/payment/verify`,
                              { adminRemarks: adminRemarksDraft },
                              "Payment verified."
                            ),
                          })}
                        >
                          Approve Payment
                        </button>
                      </>
                    ) : null}

                    {/* Refund */}
                    {selectedBooking.refundStatus === "refund_pending" ? (
                      <button
                        type="button"
                        className="btn btn-success"
                        disabled={isSubmittingAction}
                        onClick={() => openConfirm({
                          type: "mark_refunded",
                          title: "Mark as Refunded",
                          message: "Confirm that the refund has been processed and sent to the customer.",
                          requireReason: false,
                          confirmLabel: "Mark Refunded",
                          confirmClass: "btn btn-success",
                          loadingLabel: "Marking...",
                          onConfirm: () => patchBooking(
                            `/bookings/${selectedBooking._id}/refund`,
                            { refundStatus: "refunded", refundRemarks: refundRemarksDraft },
                            "Refund marked as completed."
                          ),
                        })}
                      >
                        Mark Refunded
                      </button>
                    ) : null}

                    {/* Additional payment review */}
                    {getAdditionalPaymentStatus(selectedBooking) === "under_review" ? (
                      <>
                        <button
                          type="button"
                          className="btn btn-danger-outline"
                          disabled={isSubmittingAction}
                          onClick={() => openConfirm({
                            type: "reject_additional",
                            title: "Reject Additional Payment",
                            message: "The additional payment proof will be rejected.",
                            requireReason: true,
                            reasonLabel: "Reason for rejection",
                            reasonPlaceholder: "Add a short reason so the customer knows what to fix.",
                            confirmLabel: "Reject",
                            confirmClass: "btn btn-danger",
                            loadingLabel: "Rejecting...",
                            onConfirm: (reason) => patchBooking(
                              `/bookings/${selectedBooking._id}/additional-payment/reject`,
                              { adminRemarks: reason || adminRemarksDraft, ...(selectedBooking.additionalPayment?.extensionRequestId ? {
                                extensionRequestId: selectedBooking.additionalPayment.extensionRequestId,
                                invoiceReference: selectedBooking.additionalPayment.invoiceReference,
                              } : {}) },
                              "Additional payment rejected."
                            ),
                          })}
                        >
                          Reject Additional Payment
                        </button>
                        <button
                          type="button"
                          className="btn btn-success booking-details-action-primary"
                          disabled={isSubmittingAction}
                          onClick={() => openConfirm({
                            type: "verify_additional",
                            title: "Verify additional payment?",
                            message: "Confirm that you have reviewed the submitted payment proof and that the payment details are correct.",
                            summary: [
                              ["Customer", selectedBooking.fullName || selectedBooking.customerName || "Customer"],
                              ["Booking Reference", selectedBooking.bookingReference || selectedBooking.reference || selectedBooking._id],
                              ["Amount Submitted", formatPeso(Number(selectedBooking.additionalPayment.submittedAmount) > 0
                                ? selectedBooking.additionalPayment.submittedAmount
                                : getAdditionalPaymentAmounts(selectedBooking).totalAmountDue)],
                              ["Payment Method", selectedBooking.paymentMethod || "Not specified"],
                              ...(selectedBooking.additionalPayment.paymentReference
                                ? [["Reference Number", selectedBooking.additionalPayment.paymentReference]] : []),
                              ["Reason / settlement type", getAdditionalPaymentReasonLabel(selectedBooking.additionalPayment.reason)],
                              ...(selectedBooking.additionalPayment.reason === "extension" && selectedBooking.additionalPayment.extensionRequestId
                                ? [["Extension request", String(selectedBooking.additionalPayment.extensionRequestId)],
                                   ["Invoice", selectedBooking.additionalPayment.invoiceReference || "Not specified"]] : []),
                            ],
                            requireReason: false,
                            confirmLabel: "Verify Payment",
                            confirmClass: "btn btn-success",
                            loadingLabel: "Verifying...",
                            onConfirm: () => patchBooking(
                              `/bookings/${selectedBooking._id}/additional-payment/verify`,
                              { adminRemarks: adminRemarksDraft, ...(selectedBooking.additionalPayment?.extensionRequestId ? {
                                extensionRequestId: selectedBooking.additionalPayment.extensionRequestId,
                                invoiceReference: selectedBooking.additionalPayment.invoiceReference,
                              } : {}) },
                              "Additional payment verified."
                            ),
                          })}
                        >
                          Verify Additional Payment
                        </button>
                      </>
                    ) : null}

                    {/* Assess return */}
                    {canAssessReturn(selectedBooking) ? (
                      <button
                        type="button"
                        className="btn btn-warning"
                        onClick={openReturnAssessmentModal}
                        disabled={isSubmittingAction}
                      >
                        Assess Return
                      </button>
                    ) : null}

                    {/* Record settlement — shown whenever assessment done AND final balance still due */}
                    {selectedBooking.returnAssessment?.assessedAt &&
                     hasCompletedOutstandingBalance(selectedBooking) &&
                     !hasReviewableAdditionalPayment(selectedBooking) ? (
                      <button
                        type="button"
                        className="btn btn-warning"
                        onClick={() => openSettlementModal()}
                        disabled={isSubmittingAction}
                      >
                        Settle Payment
                      </button>
                    ) : null}

                    {/* More Actions dropdown */}
                    <div className="booking-details-more-actions">
                      <button
                        type="button"
                        className="btn btn-soft"
                        onClick={() => setShowMoreActionsMenu((v) => !v)}
                        disabled={isSubmittingAction}
                      >
                        More Actions
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" style={{marginLeft: 4}}>
                          <path d="M3 5L7 9L11 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>

                      {showMoreActionsMenu ? (
                        <div className="booking-details-more-actions__menu" onClick={(e) => e.stopPropagation()}>
                          {canModifyBooking(selectedBooking) ? (
                            <button
                              type="button"
                              className="booking-details-more-actions__item"
                              onClick={() => { setShowMoreActionsMenu(false); openModifyBooking(selectedBooking); }}
                              disabled={isSubmittingAction}
                            >
                              Edit Booking
                            </button>
                          ) : null}

                          {canManageExtension(selectedBooking) ? (
                            <button
                              type="button"
                              className="booking-details-more-actions__item"
                              onClick={() => { setShowMoreActionsMenu(false); openExtensionModal(); }}
                              disabled={isSubmittingAction || Boolean(pendingExtensionRequest)}
                            >
                              Request / Manage Extension
                            </button>
                          ) : null}

                          {/* Reject request available for non-payment-verification statuses */}
                          {canRejectBooking(selectedBooking.status) &&
                           !canApproveBooking(selectedBooking.status) &&
                           !["payment_submitted", "under_review"].includes(String(selectedBooking.status || "").toLowerCase()) ? (
                            <button
                              type="button"
                              className="booking-details-more-actions__item booking-details-more-actions__item--danger"
                              disabled={isSubmittingAction}
                              onClick={() => {
                                setShowMoreActionsMenu(false);
                                openConfirm({
                                  type: "reject_request",
                                  title: "Reject / Cancel Booking",
                                  message: "This will cancel the booking. The customer will be notified.",
                                  requireReason: true,
                                  reasonLabel: "Reason for cancellation",
                                  reasonPlaceholder: "Add a short reason so the customer knows what happened.",
                                  confirmLabel: "Cancel Booking",
                                  confirmClass: "btn btn-danger",
                                  loadingLabel: "Cancelling...",
                                  onConfirm: (reason) => patchBooking(
                                    `/bookings/${selectedBooking._id}/status`,
                                    { status: "rejected", notes: noteDraft, adminRemarks: reason || adminRemarksDraft },
                                    "Booking cancelled."
                                  ),
                                });
                              }}
                            >
                              Cancel Booking
                            </button>
                          ) : null}

                          <button
                            type="button"
                            className="booking-details-more-actions__item"
                            onClick={() => { setShowMoreActionsMenu(false); saveNoteOnly(); }}
                            disabled={isSubmittingAction}
                          >
                            Save Admin Notes
                          </button>

                          {selectedBooking.invoiceReference || selectedBooking.invoiceNumber ? (
                            <button
                              type="button"
                              className="booking-details-more-actions__item"
                              onClick={() => { setShowMoreActionsMenu(false); handleDownloadInvoice(selectedBooking._id); }}
                              disabled={downloadingInvoiceId === selectedBooking._id}
                            >
                              {downloadingInvoiceId === selectedBooking._id ? "Downloading..." : "Download Invoice PDF"}
                            </button>
                          ) : null}

                          {selectedBooking.receiptNumber ? (
                            <button
                              type="button"
                              className="booking-details-more-actions__item"
                              onClick={() => { setShowMoreActionsMenu(false); handleDownloadReceipt(selectedBooking._id); }}
                              disabled={downloadingReceiptId === selectedBooking._id}
                            >
                              {downloadingReceiptId === selectedBooking._id ? "Downloading..." : "Download Receipt PDF"}
                            </button>
                          ) : null}

                          {isAdmin && !selectedBooking.isArchived ? (
                            <>
                              <div className="booking-details-more-actions__divider" />
                              <button
                                type="button"
                                className="booking-details-more-actions__item booking-details-more-actions__item--danger"
                                disabled={isSubmittingAction}
                                onClick={() => {
                                  setShowMoreActionsMenu(false);
                                  openConfirm({
                                    type: "archive",
                                    title: "Archive this booking?",
                                    message: "This booking will be hidden from active rental lists but kept for records, reports, contracts, invoices, and receipts.",
                                    requireReason: true,
                                    reasonLabel: "Archive reason",
                                    reasonPlaceholder: "Duplicate test booking, Cancelled by customer, Created during testing, Administrative cleanup, or Other",
                                    confirmLabel: "Archive Booking",
                                    confirmClass: "btn btn-danger",
                                    loadingLabel: "Archiving...",
                                    onConfirm: (reason) => archiveBooking(selectedBooking._id, reason),
                                  });
                                }}
                              >
                                Archive Booking
                              </button>
                            </>
                          ) : null}
                          {isAdmin && selectedBooking.isArchived ? (
                            <>
                              <div className="booking-details-more-actions__divider" />
                              <button
                                type="button"
                                className="booking-details-more-actions__item"
                                disabled={isSubmittingAction}
                                onClick={() => {
                                  setShowMoreActionsMenu(false);
                                  openConfirm({
                                    type: "restore",
                                    title: "Restore this booking?",
                                    message: "This booking will return to the normal rental management lists. Historical records will remain unchanged.",
                                    requireReason: false,
                                    confirmLabel: "Restore Booking",
                                    confirmClass: "btn btn-primary",
                                    loadingLabel: "Restoring...",
                                    onConfirm: () => restoreBooking(selectedBooking._id),
                                  });
                                }}
                              >
                                Restore Booking
                              </button>
                            </>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
            </section>

            {/* Confirmation dialog — rendered inside modal-box so it inherits z-context */}
            {confirmDialog ? (
              <div className="bdc-confirm-overlay" onClick={closeConfirm}>
                <div ref={confirmationBoxRef} className={"bdc-confirm-box" + (confirmDialog.type === "verify_additional" ? " bdc-confirm-box--additional-payment" : "")} role="dialog" aria-modal="true" aria-labelledby="booking-confirm-title" aria-describedby="booking-confirm-message" aria-busy={isSubmittingAction} tabIndex={-1} onClick={(e) => e.stopPropagation()}>
                  <div className="bdc-confirm-box__icon">
                    {confirmDialog.type === "delete" ? (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : confirmDialog.confirmClass?.includes("danger") ? (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <circle cx="12" cy="12" r="10" stroke="#dc2626" strokeWidth="2"/>
                        <path d="M12 8v4M12 16h.01" stroke="#dc2626" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    ) : (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <circle cx="12" cy="12" r="10" stroke="#16a34a" strokeWidth="2"/>
                        <path d="M9 12l2 2 4-4" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>
                  <h4 id="booking-confirm-title" className="bdc-confirm-box__title">{confirmDialog.title}</h4>
                  <p id="booking-confirm-message" className="bdc-confirm-box__message">{confirmDialog.message}</p>
                  {confirmDialog.type === "verify_additional" ? (
                    <>
                      <dl className="bdc-confirm-summary">
                        {confirmDialog.summary.map(([label, value]) => (
                          <div key={label}><dt>{label}</dt><dd>{value}</dd></div>
                        ))}
                      </dl>
                      <p className="bdc-confirm-box__message">
                        This action will apply the verified amount to the booking balance.
                      </p>
                      {confirmError ? <p className="bdc-confirm-error" role="alert">{confirmError}</p> : null}
                    </>
                  ) : null}

                  {confirmDialog.requireReason ? (
                    <div className="bdc-confirm-box__reason">
                      <label className="bdc-confirm-box__reason-label">{confirmDialog.reasonLabel}</label>
                      <textarea
                        className="bdc-confirm-box__reason-input"
                        placeholder={confirmDialog.reasonPlaceholder}
                        value={confirmReason}
                        onChange={(e) => setConfirmReason(e.target.value)}
                        rows={3}
                      />
                    </div>
                  ) : null}

                  {confirmDialog.requireDeleteConfirm ? (
                    <div className="bdc-confirm-box__reason">
                      <label className="bdc-confirm-box__reason-label">
                        Type <strong>DELETE</strong> to confirm permanent deletion
                      </label>
                      <input
                        type="text"
                        className="bdc-confirm-box__delete-input"
                        placeholder="DELETE"
                        value={deleteConfirmText}
                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                        autoComplete="off"
                      />
                    </div>
                  ) : null}

                  <div className="bdc-confirm-box__buttons">
                    <button
                      type="button"
                      className="btn btn-soft"
                      onClick={closeConfirm}
                      disabled={isSubmittingAction}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className={confirmDialog.confirmClass}
                      disabled={
                        isSubmittingAction ||
                        (confirmDialog.requireReason && !confirmReason.trim()) ||
                        (confirmDialog.requireDeleteConfirm && deleteConfirmText !== "DELETE")
                      }
                      onClick={() => {
                        if (confirmDialog.type === "verify_additional") {
                          submitAdditionalPaymentConfirmation();
                          return;
                        }
                        if (confirmDialog.requireReason && !confirmReason.trim()) return;
                        confirmDialog.onConfirm(confirmReason.trim());
                        closeConfirm();
                      }}
                    >
                      {isSubmittingAction ? (confirmDialog.loadingLabel || "Processing...") : confirmDialog.confirmLabel}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {extensionReviewTarget && selectedBooking && reviewedExtensionRequest ? (
        <ExtensionReviewModal
          key={extensionReviewTarget.bookingId + ":" + extensionReviewTarget.requestId}
          booking={selectedBooking}
          request={reviewedExtensionRequest}
          onClose={closeExtensionReview}
          onDecision={(action, remarks, pricingKey) => reviewExtensionRequest(extensionReviewTarget.requestId, action, remarks, pricingKey)}
        />
      ) : null}

      {showExtensionModal && selectedBooking ? (
        <div className="modal-overlay booking-change-vehicle-overlay" onClick={closeExtensionModal}>
          <form
            className="modal-box booking-extension-modal"
            onSubmit={createAdminExtensionRequest}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="booking-change-vehicle-head">
              <div>
                <span className="manual-booking-eyebrow">Rental Extension</span>
                <h2>Request Extension</h2>
                <p>Create an extension request for FleetX review and approval.</p>
              </div>
              <button type="button" className="btn btn-soft" onClick={closeExtensionModal}>
                Cancel
              </button>
            </div>
            <div className="booking-extension-modal__body">
              {extensionError ? <div className="alert alert-danger">{extensionError}</div> : null}
              <div className="booking-extension-current">
                Current return: <strong>{formatDate(selectedBooking.endDate)} {selectedBooking.endTime || ""}</strong>
              </div>
              <div className="manual-form-grid">
                <label className="field-label">
                  New Return Date
                  <input
                    className="input"
                    type="date"
                    value={extensionForm.newEndDate}
                    min={toDateInput(selectedBooking.endDate) || undefined}
                    onChange={(e) =>
                      setExtensionForm((prev) => ({ ...prev, newEndDate: e.target.value }))
                    }
                    required
                  />
                </label>
                <label className="field-label">
                  New Return Time
                  <input
                    className="input"
                    type="time"
                    value={extensionForm.newEndTime}
                    onChange={(e) =>
                      setExtensionForm((prev) => ({ ...prev, newEndTime: e.target.value }))
                    }
                    required
                  />
                </label>
              </div>
              <label className="field-label">
                Reason / Message
                <textarea
                  className="textarea"
                  value={extensionForm.reason}
                  onChange={(e) =>
                    setExtensionForm((prev) => ({ ...prev, reason: e.target.value }))
                  }
                  placeholder="Customer requested more rental time."
                />
              </label>
            </div>
            <div className="manual-booking-footer">
              <span>Backend will check vehicle, driver, route, restricted areas, and pricing.</span>
              <button type="submit" className="btn btn-primary" disabled={extensionSubmitting}>
                {extensionSubmitting ? "Submitting..." : "Submit Extension Request"}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {showReturnAssessmentModal && selectedBooking ? (
        <div className="modal-overlay booking-change-vehicle-overlay" onClick={closeReturnAssessmentModal}>
          <form
            className="modal-box booking-return-assessment-modal booking-return-assessment-modal--assess"
            onSubmit={requestReturnAssessmentCompletion}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="booking-return-assessment-head">
              <div>
                <span className="manual-booking-eyebrow">RETURN ASSESSMENT</span>
                <h2>Assess Return</h2>
                <p>Record actual return details, document findings, and apply charges when needed.</p>
              </div>
              <button
                type="button"
                className="driver-profile-modal__close"
                onClick={closeReturnAssessmentModal}
                aria-label="Close"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            </div>

            <div className="booking-return-assessment-body">
              {returnAssessmentError ? (
                <div className="alert alert-danger">{returnAssessmentError}</div>
              ) : null}

              <div className="booking-return-assessment-summary">
                <div><span>Customer</span><strong>{selectedBooking.fullName || "N/A"}</strong></div>
                <div>
                  <span>Vehicle</span>
                  <strong>{getCompactVehicleLabel(selectedBooking.vehicleId) || getAdminVehicleName(selectedBooking)}</strong>
                  {getVehicleDetailLine(selectedBooking.vehicleId) ? (
                    <small className="booking-summary-meta">{getVehicleDetailLine(selectedBooking.vehicleId)}</small>
                  ) : null}
                </div>
                <div>
                  <span>Expected return</span>
                  <strong>{formatDateTime(getAssessmentExpectedReturn(selectedBooking))}</strong>
                </div>
                <div>
                  <span>Pricing basis</span>
                  <strong>{formatPeso(getVehicleRate24Hr(selectedBooking.vehicleId))} / day</strong>
                </div>
                <div><span>Current total</span><strong>{formatPeso(selectedBooking.totalPrice)}</strong></div>
                <div>
                  <span>Remaining balance</span>
                  <strong>{assessmentRemainingBalance === null ? "N/A" : formatPeso(assessmentRemainingBalance)}</strong>
                </div>
                {selectedBooking.extensionApproved ? (
                  <div>
                    <span>Approved extension</span>
                    <strong>
                      Until {formatDate(selectedBooking.extensionNewEndDate)}{" "}
                      {selectedBooking.extensionNewEndTime || ""}
                    </strong>
                  </div>
                ) : null}
              </div>

              <section className="booking-return-section">
                <div className="booking-return-section__head">
                  <span className="booking-return-section__number">1</span>
                  <div>
                    <h3>Actual Return</h3>
                    <p>Record the actual return date and time, and indicate if penalties apply.</p>
                  </div>
                </div>
                <div className="booking-return-fields booking-return-fields--actual">
                  <label className="field-label">
                    Actual Return Date
                    <input
                      className="input"
                      type="date"
                      value={returnAssessmentForm.actualReturnDate}
                      onChange={(e) => updateReturnAssessmentForm("actualReturnDate", e.target.value)}
                      required
                    />
                  </label>
                  <label className="field-label">
                    Actual Return Time
                    <input
                      className="input"
                      type="time"
                      value={returnAssessmentForm.actualReturnTime}
                      onChange={(e) => updateReturnAssessmentForm("actualReturnTime", e.target.value)}
                      required
                    />
                  </label>
                  <label className="field-label">
                    Penalty Decision
                    <select
                      className="select"
                      value={returnAssessmentForm.penaltyOverrideType}
                      onChange={(e) => updateReturnAssessmentForm("penaltyOverrideType", e.target.value)}
                    >
                      <option value="apply">Apply computed penalty if late</option>
                      <option value="waive">Waive penalty</option>
                      <option value="adjust">Adjust penalty amount</option>
                    </select>
                  </label>
                  {returnAssessmentForm.penaltyOverrideType === "adjust" ? (
                    <label className="field-label">
                      Adjusted Penalty Amount
                      <input
                        className="input"
                        type="number"
                        min="0"
                        value={returnAssessmentForm.penaltyOverrideAmount}
                        onChange={(e) => updateReturnAssessmentForm("penaltyOverrideAmount", e.target.value)}
                      />
                    </label>
                  ) : null}
                </div>
              </section>

              <ReturnConditionEditor form={returnAssessmentForm} onChange={updateReturnAssessmentForm} disabled={returnAssessmentSubmitting} />

              <section className="booking-return-section booking-return-section--charges">
                <div className="booking-return-section__head">
                  <span className="booking-return-section__number">3</span>
                  <div>
                    <h3>Itemized Return Charges</h3>
                    <p>Enter applicable charges for the issues identified above.</p>
                  </div>
                </div>
                {visibleIssueCharges.length ? (
                  <div className="booking-return-fields booking-return-fields--charges return-issue-charge-grid">
                    {visibleIssueCharges.map(({ field, label }) => <label className="field-label" key={field}>
                      {label}
                      <div className="booking-return-money-field"><span>PHP</span>
                        <input className="input" type="number" min="0" value={returnAssessmentForm[field]}
                          onChange={(e) => updateReturnAssessmentForm(field, e.target.value)} />
                      </div>
                    </label>)}
                  </div>
                ) : <p className="return-condition-hint">No issue-related charges to enter.</p>}
              </section>
              <section className="booking-return-section">
                <div className="booking-return-section__head"><span className="booking-return-section__number">4</span><div><h3>Settlement Summary</h3><p>Calculated from the return details and current booking balance.</p></div></div>
                <ReturnSettlementSummary summary={returnPreviewSummary} loading={returnPreviewLoading} error={returnPreview.error} formatMoney={formatPeso} />
              {(() => {
                const showSettlement = Number(returnPreviewSummary?.finalBalance || 0) > 0;
                if (!showSettlement) return null;
                return (
                  <div className="booking-return-charge-entry">
                    <div>
                      <strong>Final Settlement</strong>
                      <span>How was or will the final balance be settled?</span>
                    </div>
                    <div className="manual-form-grid">
                      <label className="field-label">
                        Settlement Status
                        <select
                          className="select"
                          value={returnAssessmentForm.settlementStatus}
                          onChange={(e) => updateReturnAssessmentForm("settlementStatus", e.target.value)}
                        >
                          <option value="not_now">Not recording now — skip</option>
                          <option value="paid_to_admin">Paid to admin / face-to-face — verified immediately</option>
                          <option value="paid_online_pending_verification">Paid online, pending verification</option>
                          <option value="unpaid">Not paid yet</option>
                        </select>
                      </label>
                      {returnAssessmentForm.settlementStatus === "paid_to_admin" ? (
                        <label className="field-label">
                          Payment Method
                          <select
                            className="select"
                            value={returnAssessmentForm.settlementMethod}
                            onChange={(e) => updateReturnAssessmentForm("settlementMethod", e.target.value)}
                          >
                            <option value="cash">Cash at office / Face-to-face</option>
                            <option value="gcash">GCash</option>
                            <option value="bank_transfer">Bank Transfer</option>
                            <option value="online_banking">Online Banking</option>
                            <option value="other">Other</option>
                          </select>
                        </label>
                      ) : null}
                    </div>
                    {returnAssessmentForm.settlementStatus === "paid_to_admin" ? (
                      <div className="manual-form-grid">
                        <label className="field-label">
                          Settlement Amount
                          <input
                            className="input"
                            type="number"
                            min="0.01"
                            step="0.01"
                            placeholder="Enter the amount actually received"
                            value={returnAssessmentForm.settlementAmount}
                            onChange={(e) => updateReturnAssessmentForm("settlementAmount", e.target.value)}
                          />
                        </label>
                        <label className="field-label">
                          Reference / Receipt No.
                          <input
                            className="input"
                            type="text"
                            placeholder="Optional — GCash ref, bank ref, etc."
                            value={returnAssessmentForm.settlementReference}
                            onChange={(e) => updateReturnAssessmentForm("settlementReference", e.target.value)}
                          />
                        </label>
                      </div>
                    ) : null}
                    {returnAssessmentForm.settlementStatus !== "not_now" ? (
                      <label className="field-label">
                        Settlement Notes
                        <input
                          className="input"
                          type="text"
                          placeholder="e.g. Customer paid cash at office upon return."
                          value={returnAssessmentForm.settlementNotes}
                          onChange={(e) => updateReturnAssessmentForm("settlementNotes", e.target.value)}
                        />
                      </label>
                    ) : null}
                    {returnAssessmentForm.settlementStatus === "paid_to_admin" ? (
                      <div className="billing-settlement-note billing-settlement-note--info">
                        Only the amount entered above will be recorded as received. Any remaining balance stays due.
                      </div>
                    ) : returnAssessmentForm.settlementStatus === "paid_online_pending_verification" ? (
                      <div className="billing-settlement-note billing-settlement-note--due">
                        No receipt until admin verifies the uploaded proof. Customer sees &ldquo;Payment under review.&rdquo;
                      </div>
                    ) : returnAssessmentForm.settlementStatus === "unpaid" ? (
                      <div className="billing-settlement-note billing-settlement-note--due">
                        Customer still owes the final balance. Use &ldquo;Record Settlement&rdquo; after they pay.
                      </div>
                    ) : null}
                  </div>
                );
              })()}
              </section>
              <section className="booking-return-section">
                <div className="booking-return-section__head">
                  <span className="booking-return-section__number">5</span>
                  <div>
                    <h3>Assessment Notes</h3>
                    <p>Document the vehicle condition and return issues before applying additional charges.</p>
                  </div>
                </div>
                <div className="booking-return-fields booking-return-fields--notes">
              <label className="field-label">
                Vehicle Return Notes
                <textarea
                  className="textarea"
                  value={returnAssessmentForm.vehicleReturnNotes}
                  onChange={(e) => updateReturnAssessmentForm("vehicleReturnNotes", e.target.value)}
                  placeholder="Returned at office, keys received, odometer checked."
                />
              </label>
                <label className="field-label">
                  Vehicle Condition Notes
                  <textarea
                    className="textarea"
                    value={returnAssessmentForm.vehicleConditionNotes}
                    onChange={(e) => updateReturnAssessmentForm("vehicleConditionNotes", e.target.value)}
                    placeholder="Optional condition notes"
                  />
                </label>
                <label className="field-label">
                  Fuel / Cleanliness Notes
                  <textarea
                    className="textarea"
                    value={returnAssessmentForm.fuelCleanlinessNotes}
                    onChange={(e) => updateReturnAssessmentForm("fuelCleanlinessNotes", e.target.value)}
                    placeholder="Optional fuel or cleanliness notes"
                  />
                </label>
              <label className="field-label">
                Admin Remarks
                <textarea
                  className="textarea"
                  value={returnAssessmentForm.penaltyOverrideRemarks}
                  onChange={(e) => updateReturnAssessmentForm("penaltyOverrideRemarks", e.target.value)}
                  placeholder="Required when applying, waiving, or adjusting a late return penalty."
                />
              </label>
                </div>
              </section>


            </div>
            <div className="manual-booking-footer booking-return-assessment-footer">
              <span className="booking-return-assessment-footer__note">
                <span aria-hidden="true">i</span>
                Backend will apply grace period, approved extension, and penalty settings.
              </span>
              <button
                type="submit"
                className="btn btn-primary booking-return-assessment-submit"
                disabled={returnAssessmentSubmitting}
              >
                {returnAssessmentSubmitting ? "Completing..." : "Review Completion"}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {showReturnAssessmentModal && pendingIssueChargeChange ? (
        <ReturnChargeConfirmation change={pendingIssueChargeChange} formatMoney={formatPeso}
          onCancel={() => setPendingIssueChargeChange(null)}
          onConfirm={() => {
            setReturnAssessmentForm(pendingIssueChargeChange.next);
            setPendingIssueChargeChange(null);
            setReturnAssessmentError("");
          }} />
      ) : null}

      {showReturnCompletionConfirm && showReturnAssessmentModal && selectedBooking ? (
        <div
          className="modal-overlay booking-return-completion-confirm-overlay"
          onClick={() => !returnAssessmentSubmitting && setShowReturnCompletionConfirm(false)}
        >
          <div
            className="modal-box booking-return-completion-confirm"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="booking-return-completion-title"
            onClick={(event) => event.stopPropagation()}
          >
            <span className="manual-booking-eyebrow">RETURN ASSESSMENT</span>
            <h2 id="booking-return-completion-title">Complete Booking?</h2>
            <p>Are you sure you want to complete this booking? The return assessment will be finalized and the vehicle will be removed from active monitoring.</p>
            <ReturnConditionSummary assessment={returnAssessmentForm} />
            {visibleIssueCharges.length ? <dl className="return-review-charges">
              {visibleIssueCharges.map(({ field, label }) => <div key={field}><dt>{label}</dt><dd>{formatPeso(returnAssessmentForm[field])}</dd></div>)}
            </dl> : null}
            <ReturnSettlementSummary summary={returnPreviewSummary} loading={returnPreviewLoading} error={returnPreview.error} formatMoney={formatPeso} />
            <div className="booking-return-completion-confirm__actions">
              <button type="button" className="btn btn-soft" disabled={returnAssessmentSubmitting} onClick={() => setShowReturnCompletionConfirm(false)}>Cancel</button>
              <button type="button" className="btn btn-primary" disabled={returnAssessmentSubmitting || returnPreviewLoading || !returnPreviewSummary} onClick={submitReturnAssessment}>
                {returnAssessmentSubmitting ? "Completing..." : "Complete Booking"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showSettlementModal && selectedBooking ? (
        <div className="modal-overlay booking-change-vehicle-overlay" onClick={closeSettlementModal}>
          <form
            className="modal-box booking-return-assessment-modal"
            onSubmit={submitSettlement}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="booking-change-vehicle-head">
              <div>
                <span className="manual-booking-eyebrow">Final Settlement</span>
                <h2>Record Settlement</h2>
                <p>Record how the final balance after return charges was settled. Face-to-face payments are verified immediately.</p>
              </div>
              <button type="button" className="btn btn-soft" onClick={closeSettlementModal} disabled={settlementSubmitting}>
                Cancel
              </button>
            </div>
            <div className="booking-return-assessment-body">
              {settlementError ? (
                <div className="alert alert-danger">{settlementError}</div>
              ) : null}
              <div className="manual-form-grid">
                <div><span>Booking</span><strong>{selectedBooking.bookingReference || selectedBooking._id}</strong></div>
                <div><span>Total amount</span><strong>{formatPeso(getFinancialAmount(getFinancialSummaryForBooking(selectedBooking), "grandTotal", selectedBooking.totalPrice))}</strong></div>
                <div><span>Already paid</span><strong>{formatPeso(getFinancialAmount(getFinancialSummaryForBooking(selectedBooking), "totalPaid", selectedBooking.amountPaid))}</strong></div>
                <div><span>Remaining balance</span><strong>{formatPeso(getFinancialAmount(getFinancialSummaryForBooking(selectedBooking), "finalBalance", selectedBooking.remainingBalance))}</strong></div>
              </div>
              <div className="manual-form-grid">
                <label className="field-label">
                  Settlement Status
                  <select
                    className="select"
                    value={settlementForm.settlementStatus}
                    onChange={(e) => updateSettlementForm("settlementStatus", e.target.value)}
                    required
                  >
                    <option value="unpaid">Not paid yet</option>
                    <option value="paid_to_admin">Paid to admin / face-to-face — verified immediately</option>
                    <option value="paid_online_pending_verification">Paid online, pending verification</option>
                  </select>
                </label>
                {settlementForm.settlementStatus === "paid_to_admin" ? (
                  <label className="field-label">
                    Payment Method
                    <select
                      className="select"
                      value={settlementForm.settlementMethod}
                      onChange={(e) => updateSettlementForm("settlementMethod", e.target.value)}
                      required
                    >
                      <option value="cash">Cash at office / Face-to-face</option>
                      <option value="gcash">GCash</option>
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="online_banking">Online Banking</option>
                      <option value="other">Other</option>
                    </select>
                  </label>
                ) : null}
              </div>
              {settlementForm.settlementStatus === "paid_to_admin" ? (
                <div className="manual-form-grid">
                  <label className="field-label">
                    Settlement Amount
                    <input
                      className="input"
                      type="number"
                      min="0.01"
                      step="0.01"
                      placeholder="Enter the amount actually received"
                      value={settlementForm.settlementAmount}
                      onChange={(e) => updateSettlementForm("settlementAmount", e.target.value)}
                    />
                  </label>
                  <label className="field-label">
                    Reference / Receipt No.
                    <input
                      className="input"
                      type="text"
                      placeholder="Optional reference or receipt number"
                      value={settlementForm.settlementReference}
                      onChange={(e) => updateSettlementForm("settlementReference", e.target.value)}
                    />
                  </label>
                </div>
              ) : null}
              <label className="field-label">
                Settlement Notes
                <textarea
                  className="textarea"
                  value={settlementForm.settlementNotes}
                  onChange={(e) => updateSettlementForm("settlementNotes", e.target.value)}
                  placeholder="Optional notes about this settlement."
                />
              </label>
              {settlementForm.settlementStatus === "paid_to_admin" ? (
                <div className="billing-settlement-note billing-settlement-note--info">
                  Face-to-face payment will be verified immediately. A Payment Receipt will be generated automatically.
                </div>
              ) : settlementForm.settlementStatus === "paid_online_pending_verification" ? (
                <div className="billing-settlement-note billing-settlement-note--due">
                  Online payment will remain pending until proof is verified. No receipt will be generated until then.
                </div>
              ) : null}
            </div>
            <div className="manual-booking-footer">
              <span>Settlement type determines whether a receipt is generated immediately.</span>
              <button type="submit" className="btn btn-primary" disabled={settlementSubmitting}>
                {settlementSubmitting ? "Recording..." : "Save Settlement"}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {showModifyModal && selectedBooking ? (
        <div className="modal-overlay manual-booking-overlay" onClick={closeModifyBooking}>
          <form className="manual-booking-modal booking-modify-modal" onSubmit={submitModifyBooking} onClick={(e) => e.stopPropagation()}>
            <div className="manual-booking-head">
              <div>
                <span className="manual-booking-eyebrow">Booking Modification</span>
                <h2>Modify Booking</h2>
                <p>Revalidates schedule, vehicle, driver, route rules, restricted areas, and pricing before saving.</p>
              </div>
              <button type="button" className="btn btn-soft" onClick={closeModifyBooking} disabled={modifySubmitting}>
                Close
              </button>
            </div>

            {modifyError ? <div className="alert alert-danger">{modifyError}</div> : null}

            <div className="manual-panel booking-modify-body">
              <div className="booking-modify-selected-vehicle">
                <div className="booking-modify-selected-vehicle__media">
                  {getVehicleImageSrc(selectedModifyVehicle) ? (
                    <img
                      src={getVehicleImageSrc(selectedModifyVehicle)}
                      alt={getVehicleDisplayName(selectedModifyVehicle)}
                    />
                  ) : (
                    <span>{getVehicleDisplayName(selectedModifyVehicle).charAt(0)}</span>
                  )}
                </div>
                <div>
                  <small>
                    {String(selectedBooking.vehicleId?._id || selectedBooking.vehicleId) ===
                    String(modifyForm.vehicleId)
                      ? "Current vehicle"
                      : "Selected vehicle"}
                  </small>
                  <strong>
                    {getCompactVehicleLabel(selectedModifyVehicle) ||
                      getVehicleDisplayName(selectedModifyVehicle)}
                  </strong>
                  <em>
                    {getVehicleDetailLine(selectedModifyVehicle) || "Vehicle details unavailable"}
                  </em>
                  {modifyVehicleNotice ? <p>{modifyVehicleNotice}</p> : null}
                </div>
                <button
                  type="button"
                  className="btn btn-soft"
                  onClick={openChangeVehicleModal}
                  disabled={modifySubmitting}
                >
                  Change Vehicle
                </button>
              </div>

              <div className="manual-form-grid">
                <label className="field-label">
                  Pick-up Date
                  <input
                    className="input"
                    type="date"
                    min={getTodayString()}
                    value={modifyForm.startDate}
                    onChange={(e) => {
                      updateModifyForm("startDate", e.target.value);
                      setModifyError("");
                    }}
                    onBlur={() => loadModifyOptions()}
                  />
                </label>
                <label className="field-label">
                  Pick-up Time
                  <input
                    className="input"
                    type="time"
                    min={getMinTimeForDate(modifyForm.startDate) || undefined}
                    value={modifyForm.startTime}
                    onChange={(e) => {
                      updateModifyForm("startTime", e.target.value);
                      setModifyError("");
                    }}
                  />
                </label>
                <label className="field-label">
                  Return Date
                  <input
                    className="input"
                    type="date"
                    min={modifyForm.startDate || getTodayString()}
                    value={modifyForm.endDate}
                    onChange={(e) => {
                      updateModifyForm("endDate", e.target.value);
                      setModifyError("");
                    }}
                    onBlur={() => loadModifyOptions()}
                  />
                </label>
                <label className="field-label">
                  Return Time
                  <input
                    className="input"
                    type="time"
                    value={modifyForm.endTime}
                    onChange={(e) => {
                      updateModifyForm("endTime", e.target.value);
                      setModifyError("");
                    }}
                  />
                </label>
                <label className="field-label">
                  Trip Type
                  <select
                    className="select"
                    value={modifyForm.rentalType}
                    onChange={(e) => {
                      const nextRentalType = e.target.value;
                      updateModifyForm("rentalType", nextRentalType);
                      loadModifyOptions({ ...modifyForm, rentalType: nextRentalType });
                    }}
                  >
                    <option value="with_driver">With Driver</option>
                    <option value="self_drive">Self Drive</option>
                  </select>
                </label>
                <label className="field-label">
                  Passengers
                  <input
                    className="input"
                    type="number"
                    min="1"
                    max={modifyPassengerMax || undefined}
                    value={modifyForm.numberOfPax}
                    onChange={(e) => updateModifyForm("numberOfPax", e.target.value)}
                  />
                  {modifyPassengerMax ? (
                    <small className="booking-modify-helper">
                      Vehicle supports up to {modifyPassengerMax} passengers for this rental type.
                    </small>
                  ) : null}
                </label>
                <label className="field-label">
                  Luggage Bags
                  <input className="input" type="number" min="0" value={modifyForm.luggageBags} onChange={(e) => updateModifyForm("luggageBags", e.target.value)} />
                </label>
                <label className="field-label">
                  Luggage Size
                  <select className="select" value={modifyForm.luggageSize} onChange={(e) => updateModifyForm("luggageSize", e.target.value)}>
                    <option value="">Optional</option>
                    {LUGGAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                  <small className="booking-modify-helper">{LUGGAGE_HELPER_TEXT}</small>
                </label>
                <label className="field-label">
                  Estimated total luggage weight (optional)
                  <input
                    className="input"
                    type="number"
                    min="0"
                    inputMode="decimal"
                    placeholder="kg"
                    value={modifyForm.luggageWeightKg}
                    onChange={(e) => updateModifyForm("luggageWeightKg", e.target.value)}
                  />
                </label>
                <label className="field-label">
                  Purpose
                  <input className="input" value={modifyForm.purposeOfTravel} onChange={(e) => updateModifyForm("purposeOfTravel", e.target.value)} />
                </label>
              </div>

              <div className="manual-form-grid">
                <label className="field-label">
                  Pickup Location
                  <GooglePlacesAutocomplete
                    value={modifyForm.pickupLocation}
                    placeholder="Enter pickup location"
                    className="admin-places-input"
                    onChange={(value) => updateModifyForm("pickupLocation", value)}
                    onPlaceSelect={(place) => {
                      updateModifyForm("pickupLocation", place?.address || place?.description || "");
                      updateModifyForm("pickupCoords", place?.coords || null);
                      updateModifyForm("pickupPlaceId", place?.placeId || "");
                    }}
                  />
                </label>
                <label className="field-label">
                  Destination / Drop-off
                  <GooglePlacesAutocomplete
                    value={modifyForm.destination}
                    placeholder="Enter destination"
                    className="admin-places-input"
                    onChange={(value) => updateModifyForm("destination", value)}
                    onPlaceSelect={(place) => {
                      updateModifyForm("destination", place?.address || place?.description || "");
                      updateModifyForm("destinationCoords", place?.coords || null);
                      updateModifyForm("destinationPlaceId", place?.placeId || "");
                    }}
                  />
                </label>
                {modifyForm.rentalType === "self_drive" ? (
                  <label className="field-label">
                    Handoff Option
                    <select className="select" value={modifyForm.vehicleHandoffOption} onChange={(e) => updateModifyForm("vehicleHandoffOption", e.target.value)}>
                      <option value="pickup">Customer picks up vehicle</option>
                      <option value="delivery">Deliver vehicle</option>
                    </select>
                  </label>
                ) : null}
                {modifyForm.rentalType === "self_drive" && modifyForm.vehicleHandoffOption === "delivery" ? (
                  <label className="field-label">
                    Delivery Address
                    <GooglePlacesAutocomplete
                      value={modifyForm.deliveryAddress}
                      placeholder="Enter delivery address"
                      className="admin-places-input"
                      onChange={(value) => updateModifyForm("deliveryAddress", value)}
                      onPlaceSelect={(place) => {
                        updateModifyForm("deliveryAddress", place?.address || place?.description || "");
                        updateModifyForm("deliveryCoords", place?.coords || null);
                        updateModifyForm("deliveryPlaceId", place?.placeId || "");
                      }}
                    />
                  </label>
                ) : null}
                {modifyForm.rentalType === "with_driver" ? (
                  <label className="field-label">
                    Assigned Driver
                    <select className="select" value={modifyForm.driverId} onChange={(e) => updateModifyForm("driverId", e.target.value)}>
                      <option value="">No driver assigned yet</option>
                      {selectedBooking.driverId ? (
                        <option value={selectedBooking.driverId._id || selectedBooking.driverId}>
                          Current: {selectedBooking.driverId.fullName || "Assigned Driver"}
                        </option>
                      ) : null}
                      {modifyDrivers.map((driver) => (
                        <option key={driver._id} value={driver._id}>
                          {driver.fullName} ({driver.assignmentCount || 0} assignment(s))
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
              </div>

              <div className="manual-grid">
                <label className="field-label">
                  Delivery Fee Optional Override
                  <input
                    className="input"
                    type="number"
                    min="0"
                    value={modifyForm.deliveryFee}
                    onChange={(e) => updateModifyForm("deliveryFee", e.target.value)}
                    placeholder="Leave blank for rules"
                  />
                </label>
                <label className="field-label">
                  Other Extra Charges
                  <input
                    className="input"
                    type="number"
                    min="0"
                    value={modifyForm.extraFee}
                    onChange={(e) => updateModifyForm("extraFee", e.target.value)}
                  />
                </label>
                <label className="field-label manual-field--full">
                  Extra Charge Remarks
                  <textarea
                    className="textarea"
                    value={modifyForm.extraChargeRemarks}
                    onChange={(e) => updateModifyForm("extraChargeRemarks", e.target.value)}
                  />
                </label>
              </div>

              <div className="booking-modify-price-box">
                <div><span>Old total</span><strong>{formatPeso(selectedBooking.totalPrice)}</strong></div>
                <div><span>Delivery fee</span><strong>{modifyForm.deliveryFee === "" ? "Computed by FleetX" : formatPeso(modifyForm.deliveryFee)}</strong></div>
                <div><span>Other extra charges</span><strong>{formatPeso(modifyForm.extraFee)}</strong></div>
                <div><span>New estimated total</span><strong>{formatPeso(modifyPricing.total)}</strong></div>
                <div><span>Adjustment</span><strong>{formatPeso(modifyPricing.total - Number(selectedBooking.totalPrice || 0))}</strong></div>
              </div>

              <label className={`field-label booking-modify-reason ${modifyReasonError ? "is-invalid" : ""}`}>
                <span>
                  Modification Reason <b aria-hidden="true">*</b>
                </span>
                <textarea
                  ref={modifyReasonRef}
                  className={`textarea ${modifyReasonError ? "input-error" : ""}`}
                  value={modifyForm.reason}
                  onChange={(e) => updateModifyForm("reason", e.target.value)}
                  placeholder="Customer requested reschedule during office visit."
                  aria-invalid={Boolean(modifyReasonError)}
                  aria-describedby={modifyReasonError ? "modify-reason-error" : undefined}
                />
                {modifyReasonError ? (
                  <small id="modify-reason-error" className="booking-modify-inline-error">
                    {modifyReasonError}
                  </small>
                ) : (
                  <small className="booking-modify-helper">Required. A short note is okay.</small>
                )}
              </label>
            </div>

            <div className="manual-booking-footer">
              <span>{modifyLoadingOptions ? "Loading availability options..." : "Backend will revalidate before saving."}</span>
              <div>
                <button type="button" className="btn btn-soft" onClick={closeModifyBooking} disabled={modifySubmitting}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={modifySubmitting}>
                  {modifySubmitting ? "Saving..." : "Save Modification"}
                </button>
              </div>
            </div>
          </form>
        </div>
      ) : null}

      {showChangeVehicleModal && selectedBooking ? (
        <div className="modal-overlay booking-change-vehicle-overlay" onClick={() => setShowChangeVehicleModal(false)}>
          <div className="modal-box booking-change-vehicle-modal" onClick={(e) => e.stopPropagation()}>
            <div className="booking-change-vehicle-head">
              <div>
                <span className="manual-booking-eyebrow">Vehicle Availability</span>
                <h2>Change Vehicle</h2>
                <p>Available vehicles for the modified schedule and passenger count.</p>
              </div>
              <button type="button" className="btn btn-soft" onClick={() => setShowChangeVehicleModal(false)}>
                Cancel
              </button>
            </div>

            <div className="booking-change-vehicle-toolbar">
              <input
                className="input"
                value={vehiclePickerSearch}
                onChange={(e) => setVehiclePickerSearch(e.target.value)}
                placeholder="Search by vehicle name, plate, unit, category, or location"
              />
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => loadAvailableModifyVehicles(vehiclePickerSearch)}
                disabled={vehiclePickerLoading}
              >
                {vehiclePickerLoading ? "Loading..." : "Search"}
              </button>
            </div>

            {vehiclePickerError ? (
              <div className="alert alert-danger">{vehiclePickerError}</div>
            ) : null}

            {!vehiclePickerError && vehiclePickerLoading ? (
              <div className="booking-change-vehicle-empty">Loading available vehicles...</div>
            ) : null}

            {!vehiclePickerError && !vehiclePickerLoading && modifyVehicles.length === 0 ? (
              <div className="booking-change-vehicle-empty">
                No available vehicles match the selected schedule and passenger count.
              </div>
            ) : null}

            {!vehiclePickerError && !vehiclePickerLoading && modifyVehicles.length ? (
              <div className="booking-change-vehicle-grid">
                {modifyVehicles.map((vehicle) => {
                  const imageSrc = getVehicleImageSrc(vehicle);
                  const passengerLimit = getVehiclePassengerLimit(vehicle, modifyForm.rentalType);
                  const isSelected = String(vehicle._id) === String(modifyForm.vehicleId);
                  const detailItems = getVehicleDetailLine(vehicle).split(" - ").filter(Boolean);
                  return (
                    <article
                      key={vehicle._id}
                      className={`booking-change-vehicle-card ${isSelected ? "is-selected" : ""}`}
                    >
                      <div className="booking-change-vehicle-card__image">
                        {imageSrc ? (
                          <img src={imageSrc} alt={getVehicleDisplayName(vehicle)} />
                        ) : (
                          <span>{getVehicleDisplayName(vehicle).charAt(0)}</span>
                        )}
                      </div>
                      <div className="booking-change-vehicle-card__body">
                        <div className="booking-change-vehicle-title">
                          <strong>{getVehicleDisplayName(vehicle)}</strong>
                          {getVehicleIdentifierText(vehicle) ? (
                            <small>{getVehicleIdentifierText(vehicle)}</small>
                          ) : null}
                        </div>
                        <div className="booking-change-vehicle-meta">
                          {detailItems.map((item) => (
                            <span key={item}>{item}</span>
                          ))}
                          <span>{passengerLimit ? `${passengerLimit} pax max` : "Capacity N/A"}</span>
                        </div>
                        <p className="booking-change-vehicle-rate">
                          Daily rate: <strong>{formatPeso(getVehicleRate24Hr(vehicle))}</strong>
                        </p>
                        <em>{vehicle.availabilityStatus || "available"}</em>
                      </div>
                      <button
                        type="button"
                        className={isSelected ? "btn btn-soft" : "btn btn-primary"}
                        onClick={() => selectModifyVehicle(vehicle)}
                      >
                        {isSelected ? "Selected" : "Select"}
                      </button>
                    </article>
                  );
                })}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {showManualWizard ? (
        <div className="modal-overlay manual-booking-overlay" onClick={closeManualWizard}>
          <div className="manual-booking-modal" onClick={(e) => e.stopPropagation()}>
            <div className="manual-booking-head">
              <div>
                <span className="manual-booking-eyebrow">Rental Management</span>
                <h2>New Manual Booking</h2>
                <p>Guided booking creation for walk-in and assisted customers.</p>
              </div>
              <button type="button" className="btn btn-soft" onClick={closeManualWizard}>
                Close
              </button>
            </div>

            <div className="manual-booking-steps">
              {manualSteps.map((step, index) => (
                <button
                  key={step}
                  type="button"
                  className={`manual-step ${manualStep === index ? "is-active" : ""} ${
                    manualStep > index ? "is-complete" : ""
                  }`}
                  onClick={() => setManualStep(index)}
                >
                  <span>{index + 1}</span>
                  {step}
                </button>
              ))}
            </div>

            <div className="manual-booking-body">
              {manualStep === 0 ? (
                <section className="manual-panel">
                  <div className="manual-panel-title">
                    <h3>Customer Details</h3>
                    <p>Find an existing customer or create a linked walk-in account.</p>
                  </div>

                  <label className="manual-field manual-field--full">
                    <span>Search Customer</span>
                    <input
                      className="input"
                      placeholder="Search by email, contact, or name"
                      value={manualForm.customerSearch}
                      onChange={(e) => updateManualForm("customerSearch", e.target.value)}
                    />
                  </label>

                  {manualForm.customerSearch ? (
                    <div className="manual-search-results">
                      {manualLoadingCustomers ? (
                        <p>Searching customers...</p>
                      ) : manualCustomerResults.length > 0 ? (
                        manualCustomerResults.map((customer) => (
                          <button
                            key={customer._id}
                            type="button"
                            className="manual-result-row"
                            onClick={() => selectManualCustomer(customer)}
                          >
                            <strong>{customer.name}</strong>
                            <span>{customer.email} · {customer.contact || "No contact"}</span>
                          </button>
                        ))
                      ) : (
                        <p>No existing customer found. Fill the details below to create one.</p>
                      )}
                    </div>
                  ) : null}

                  <div className="manual-grid">
                    <label className="manual-field">
                      <span>Full Name</span>
                      <input
                        className="input"
                        value={manualForm.fullName}
                        onChange={(e) => updateManualForm("fullName", e.target.value)}
                      />
                    </label>
                    <label className="manual-field">
                      <span>Email Address</span>
                      <input
                        className="input"
                        type="email"
                        value={manualForm.email}
                        onChange={(e) => updateManualForm("email", e.target.value)}
                      />
                    </label>
                    <label className="manual-field">
                      <span>Contact Number</span>
                      <div className={`admin-phone-row ${manualContactError ? "is-invalid" : ""}`}>
                        <select
                          className="select admin-phone-row__code"
                          value={manualForm.contactCountryCode}
                          onChange={(e) => updateManualForm("contactCountryCode", e.target.value)}
                          aria-label="Country code"
                        >
                          {COUNTRY_CODE_OPTIONS.map((option) => (
                            <option
                              key={`${option.country}-${option.code}`}
                              value={option.code}
                            >
                              {getCountryOptionLabel(option)}
                            </option>
                          ))}
                        </select>
                        <input
                          className="input admin-phone-row__number"
                          type="tel"
                          inputMode="tel"
                          placeholder={selectedManualContactCountry.placeholder || "Enter contact number"}
                          value={manualForm.contactNumber}
                          onChange={(e) => updateManualForm("contactNumber", e.target.value)}
                          maxLength={getMaxLocalPhoneDigits(
                            manualForm.contactCountryCode,
                            manualForm.contactNumber
                          )}
                        />
                      </div>
                      {manualContactError ? (
                        <small className="field-error">{manualContactError}</small>
                      ) : (
                        <small>Choose a country code, then enter the mobile number.</small>
                      )}
                    </label>
                    <label className="manual-field">
                      <span>Address Optional</span>
                      <input
                        className="input"
                        value={manualForm.address}
                        onChange={(e) => updateManualForm("address", e.target.value)}
                      />
                    </label>
                  </div>
                  {manualForm.selectedCustomerId ? (
                    <div className="manual-inline-status">
                      <strong>Existing customer selected</strong>
                      <span>
                        Verification: {prettyStatus(manualForm.selectedCustomerVerificationStatus || "not_submitted")}
                      </span>
                      <span>
                        Contact: {manualBookingContact || "No contact"}
                      </span>
                      {manualForm.selectedCustomerAccountSetupRequired ? (
                        <span>Account setup is still required.</span>
                      ) : null}
                    </div>
                  ) : null}
                  {manualForm.rentalType === "self_drive" && !manualForm.selectedCustomerIsVerified ? (
                    <div className="manual-warning-note">
                      Self-drive bookings may require customer verification before release.
                    </div>
                  ) : null}
                </section>
              ) : null}

              {manualStep === 1 ? (
                <section className="manual-panel">
                  <div className="manual-panel-title">
                    <h3>Select Vehicle</h3>
                    <p>Choose dates first to show vehicles available for that range.</p>
                  </div>

                  <div className="manual-grid">
                    <label className="manual-field">
                      <span>Pickup Date</span>
                      <input
                        className="input"
                        type="date"
                        min={getTodayString()}
                        value={manualForm.startDate}
                        onChange={(e) => {
                          const next = e.target.value;
                          updateManualForm("startDate", next);
                          // Clear a now-past startTime when date changes to today.
                          if (next === getTodayString() && manualForm.startTime) {
                            const minT = getMinTimeForDate(next);
                            if (minT && manualForm.startTime < minT) {
                              updateManualForm("startTime", "");
                            }
                          }
                          setManualScheduleErrors({});
                        }}
                      />
                      {manualScheduleErrors.startDate ? (
                        <small className="field-error">{manualScheduleErrors.startDate}</small>
                      ) : null}
                    </label>
                    <label className="manual-field">
                      <span>Pickup Time</span>
                      <input
                        className="input"
                        type="time"
                        min={getMinTimeForDate(manualForm.startDate) || undefined}
                        value={manualForm.startTime}
                        onChange={(e) => {
                          updateManualForm("startTime", e.target.value);
                          setManualScheduleErrors({});
                        }}
                      />
                      {manualScheduleErrors.startTime ? (
                        <small className="field-error">{manualScheduleErrors.startTime}</small>
                      ) : null}
                    </label>
                    <label className="manual-field">
                      <span>Return Date</span>
                      <input
                        className="input"
                        type="date"
                        min={manualForm.startDate || getTodayString()}
                        value={manualForm.endDate}
                        onChange={(e) => {
                          updateManualForm("endDate", e.target.value);
                          setManualScheduleErrors({});
                        }}
                      />
                      {manualScheduleErrors.endDate ? (
                        <small className="field-error">{manualScheduleErrors.endDate}</small>
                      ) : null}
                    </label>
                    <label className="manual-field">
                      <span>Return Time</span>
                      <input
                        className="input"
                        type="time"
                        value={manualForm.endTime}
                        onChange={(e) => {
                          updateManualForm("endTime", e.target.value);
                          setManualScheduleErrors({});
                        }}
                      />
                      {manualScheduleErrors.endTime ? (
                        <small className="field-error">{manualScheduleErrors.endTime}</small>
                      ) : null}
                    </label>
                    <label className="manual-field manual-field--full">
                      <span>Search Vehicle</span>
                      <input
                        className="input"
                        placeholder="Search make, model, plate, unit, category, location"
                        value={manualForm.vehicleSearch}
                        onChange={(e) => updateManualForm("vehicleSearch", e.target.value)}
                      />
                    </label>
                  </div>

                  <div className="manual-vehicle-grid">
                    {manualLoadingVehicles ? (
                      <p>Checking availability...</p>
                    ) : manualVehicles.length === 0 ? (
                      <p>No vehicles match the selected criteria.</p>
                    ) : (
                      manualVehicles.map((vehicle) => {
                        const availability = String(vehicle.availabilityStatus || "available").toLowerCase();
                        const unavailable = !["available", ""].includes(availability);
                        const passengerLimit = getVehiclePassengerLimit(vehicle, manualForm.rentalType);
                        const isSelected = manualForm.vehicleId === vehicle._id && !unavailable;
                        return (
                        <button
                          key={vehicle._id}
                          type="button"
                          className={`manual-vehicle-card ${
                            manualForm.vehicleId === vehicle._id ? "is-selected" : ""
                          } ${unavailable ? "is-disabled" : ""}`}
                          aria-pressed={isSelected}
                          onClick={() => {
                            if (!unavailable) updateManualForm("vehicleId", vehicle._id);
                          }}
                          disabled={unavailable}
                        >
                          {isSelected ? (
                            <span className='manual-vehicle-card__selected-indicator' aria-hidden='true'>
                              ✓
                            </span>
                          ) : null}
                          <strong>{getVehicleOptionLabel(vehicle)}</strong>
                          <span>{passengerLimit || "N/A"} passenger max for this rental type</span>
                          {getVehicleDetailLine(vehicle) ? (
                            <span>{getVehicleDetailLine(vehicle)}</span>
                          ) : null}
                          <em>{formatPeso(vehicle.rate24Hr || vehicle.dailyRate)} / day</em>
                          <small>{vehicle.availabilityMessage || prettyStatus(availability)}</small>
                        </button>
                        );
                      })
                    )}
                  </div>
                  {selectedManualVehicleUnavailable ? (
                    <small className="field-error">
                      Selected vehicle is not available for this schedule. Choose another vehicle.
                    </small>
                  ) : null}
                </section>
              ) : null}

              {manualStep === 2 ? (
                <section className="manual-panel">
                  <div className="manual-panel-title">
                    <h3>Trip Details</h3>
                    <p>Capture the itinerary and rental setup.</p>
                  </div>

                  <div className="manual-grid">
                    <label className="manual-field">
                      <span>Rental Type</span>
                      <select
                        className="input"
                        value={manualForm.rentalType}
                        onChange={(e) => updateManualForm("rentalType", e.target.value)}
                      >
                        <option value="with_driver">With Driver</option>
                        <option value="self_drive">Self Drive</option>
                      </select>
                    </label>
                    {isManualWithDriver ? (
                      <label className="manual-field">
                        <span>Pickup Location</span>
                        <GooglePlacesAutocomplete
                          inputClassName="input"
                          value={manualForm.pickupLocation}
                          placeholder="Search or type pickup address"
                          onChange={(value) => updateManualForm("pickupLocation", value)}
                          onPlaceSelect={selectManualPlace(
                            "pickupLocation",
                            "pickupCoords",
                            "pickupPlaceId"
                          )}
                        />
                      </label>
                    ) : null}
                    <label className="manual-field">
                      <span>{isManualWithDriver ? "Drop-off Location" : "Trip Destination / Area"}</span>
                      <GooglePlacesAutocomplete
                        inputClassName="input"
                        value={manualForm.destination}
                        placeholder={
                          isManualWithDriver
                            ? "Search or type drop-off location"
                            : "Search or type destination or travel area"
                        }
                        onChange={(value) => updateManualForm("destination", value)}
                        onPlaceSelect={selectManualPlace(
                          "destination",
                          "destinationCoords",
                          "destinationPlaceId"
                        )}
                      />
                    </label>
                    {!isManualWithDriver ? (
                      <div className="manual-field manual-field--full manual-handoff-section">
                        <div className="manual-handoff-section__head">
                          <span>Vehicle Handoff</span>
                          <small>Choose how the customer will receive the vehicle.</small>
                        </div>
                        <div className="manual-handoff-grid" role="radiogroup" aria-label="Vehicle handoff option">
                          <button
                            type="button"
                            className={`manual-handoff-card ${
                              manualForm.vehicleHandoffOption === "pickup" ? "is-selected" : ""
                            }`}
                            onClick={() => updateManualForm("vehicleHandoffOption", "pickup")}
                            aria-pressed={manualForm.vehicleHandoffOption === "pickup"}
                          >
                            <strong>Customer pickup</strong>
                            <small>Customer picks up the unit at the vehicle or FleetX location.</small>
                          </button>
                          <button
                            type="button"
                            className={`manual-handoff-card ${
                              manualForm.vehicleHandoffOption === "delivery" ? "is-selected" : ""
                            }`}
                            onClick={() => updateManualForm("vehicleHandoffOption", "delivery")}
                            aria-pressed={manualForm.vehicleHandoffOption === "delivery"}
                          >
                            <strong>Deliver vehicle</strong>
                            <small>FleetX may deliver the unit to a selected address.</small>
                          </button>
                        </div>

                        {manualForm.vehicleHandoffOption === "pickup" ? (
                          <div className="manual-handoff-note">
                            Pickup point: {manualVehiclePickupLocation}
                          </div>
                        ) : (
                          <label className="manual-field manual-field--full manual-field--nested">
                            <span>Delivery Address</span>
                            <GooglePlacesAutocomplete
                              inputClassName="input"
                              value={manualForm.deliveryAddress}
                              placeholder="Search or type delivery address"
                              onChange={(value) => updateManualForm("deliveryAddress", value)}
                              onPlaceSelect={selectManualPlace(
                                "deliveryAddress",
                                "deliveryCoords",
                                "deliveryPlaceId"
                              )}
                            />
                            <small>Additional delivery charges may apply and can be reviewed by FleetX.</small>
                          </label>
                        )}
                      </div>
                    ) : null}
                    <label className="manual-field">
                      <span>Purpose of Travel</span>
                      <input
                        className="input"
                        value={manualForm.purposeOfTravel}
                        onChange={(e) => updateManualForm("purposeOfTravel", e.target.value)}
                      />
                    </label>
                    <label className="manual-field">
                      <span>Passengers</span>
                      <input
                        className="input"
                        type="number"
                        min="1"
                        max={manualPassengerLimit || undefined}
                        value={manualForm.numberOfPax}
                        onChange={(e) => updateManualForm("numberOfPax", e.target.value)}
                      />
                      <small>
                        {manualPassengerLimit
                          ? `This vehicle supports up to ${manualPassengerLimit} passengers for this rental type.`
                          : "Select a vehicle to check passenger capacity."}
                      </small>
                      {manualPassengerError ? (
                        <small className="field-error">{manualPassengerError}</small>
                      ) : null}
                    </label>
                    <label className="manual-field">
                      <span>Luggage Bags</span>
                      <input
                        className="input"
                        type="number"
                        min="0"
                        value={manualForm.luggageBags}
                        onChange={(e) => updateManualForm("luggageBags", e.target.value)}
                      />
                    </label>
                    <label className="manual-field">
                      <span>Luggage Size Optional</span>
                      <select
                        className="select"
                        value={manualForm.luggageSize}
                        onChange={(e) => updateManualForm("luggageSize", e.target.value)}
                      >
                        <option value="">Optional</option>
                        {LUGGAGE_SIZE_OPTIONS.map((size) => (
                          <option key={size} value={size}>{size}</option>
                        ))}
                      </select>
                      <small>{LUGGAGE_HELPER_TEXT}</small>
                    </label>
                    <label className="manual-field">
                      <span>Estimated total luggage weight (optional)</span>
                      <input
                        className="input"
                        type="number"
                        min="0"
                        inputMode="decimal"
                        placeholder="kg"
                        value={manualForm.luggageWeightKg}
                        onChange={(e) => updateManualForm("luggageWeightKg", e.target.value)}
                      />
                    </label>
                    <label className="manual-field">
                      <span>Return Time</span>
                      <input
                        className="input"
                        type="time"
                        value={manualForm.endTime}
                        onChange={(e) => updateManualForm("endTime", e.target.value)}
                      />
                    </label>
                    <label className="manual-field manual-field--full">
                      <span>Notes Optional</span>
                      <textarea
                        className="input manual-textarea"
                        value={manualForm.notes}
                        onChange={(e) => updateManualForm("notes", e.target.value)}
                      />
                    </label>
                  </div>
                  {manualPreviewLoading ? (
                    <div className="manual-inline-status">
                      Calculating route, delivery fee, and total...
                    </div>
                  ) : null}
                  {manualPreviewError ? (
                    <div className="manual-warning-note">
                      {manualPreview?.restrictedArea
                        ? manualPreview.message
                        : manualPreview?.route
                        ? `Long-distance trip detected. ${manualPreview.message}`
                        : manualPreviewError}
                    </div>
                  ) : null}
                  {manualPreview?.valid && manualPreview.route?.routeMinimumRequiredHours ? (
                    <div className="manual-inline-status">
                      Route duration validated. Minimum: about{" "}
                      {Math.max(1, Math.ceil(Number(manualPreview.route.routeMinimumRequiredHours || 0) / 24))} day(s).
                    </div>
                  ) : null}
                </section>
              ) : null}

              {manualStep === 3 ? (
                <section className="manual-panel">
                  <div className="manual-panel-title">
                    <h3>Pricing</h3>
                    <p>Base price is calculated from the selected vehicle and trip dates.</p>
                  </div>

                  <div className="manual-price-summary">
                    {manualPreviewLoading ? (
                      <div className="manual-driver-note">Calculating total...</div>
                    ) : null}
                    <div><span>Daily Rate</span><strong>{formatPeso(getVehicleRate24Hr(selectedManualVehicle))}</strong></div>
                    <div><span>Billing Duration</span><strong>{manualRentalPricing.billingLabel}</strong></div>
                    <div><span>Rental Subtotal</span><strong>{formatPeso(manualBasePrice)}</strong></div>
                    <div><span>Vehicle Base Price</span><strong>{formatPeso(manualPreviewPricing.vehicleBaseAmount ?? manualRentalPricing.vehicleBaseAmount)}</strong></div>
                    {manualRentalPricing.vehicleExtensionType !== "none" ? (
                      <div><span>Vehicle Overtime / Extension</span><strong>{formatPeso(manualPreviewPricing.vehicleOvertimeAmount ?? manualRentalPricing.vehicleOvertimeAmount)}</strong></div>
                    ) : null}
                    {manualRentalPricing.driverTotal > 0 ? (
                      <>
                        <div><span>Driver Service</span><strong>{formatPeso(manualPreviewPricing.driverBaseAmount ?? manualRentalPricing.driverBaseAmount)}</strong></div>
                        {manualRentalPricing.driverOvertimeAmount > 0 ? (
                          <div><span>Driver Overtime / Extension</span><strong>{formatPeso(manualPreviewPricing.driverOvertimeAmount ?? manualRentalPricing.driverOvertimeAmount)}</strong></div>
                        ) : null}
                        <div><span>Driver Total</span><strong>{formatPeso(manualPreviewPricing.driverTotal ?? manualRentalPricing.driverTotal)}</strong></div>
                      </>
                    ) : null}
                    {!isManualWithDriver && manualForm.vehicleHandoffOption === "delivery" ? (
                      <>
                        <div><span>Delivery Distance</span><strong>{manualPreviewExtraCharges.deliveryDistanceKm ? `${manualPreviewExtraCharges.deliveryDistanceKm} km` : manualPreviewLoading ? "Calculating delivery fee..." : "For FleetX review"}</strong></div>
                        <div><span>Base Delivery Fee</span><strong>{formatPeso(manualPreviewExtraCharges.baseDeliveryFee || 0)}</strong></div>
                        <div><span>Rate Per KM</span><strong>{formatPeso(manualPreviewExtraCharges.perKmDeliveryFee || 0)} / km</strong></div>
                      </>
                    ) : null}
                    <div><span>Delivery Fee</span><strong>{!isManualWithDriver && manualForm.vehicleHandoffOption === "delivery" ? formatPeso(manualDeliveryFee) : "PHP 0"}</strong></div>
                    <div><span>Promo Discount</span><strong>{manualDiscount > 0 ? `-${formatPeso(manualDiscount)}` : "Not applied"}</strong></div>
                    <div><span>Other Extra Charges</span><strong>{formatPeso(manualExtraFee)}</strong></div>
                    <div><span>Estimated / Final Total</span><strong>{formatPeso(manualTotalPrice)}</strong></div>
                    {manualRentalPricing.driverTotal > 0 ? (
                      <div className="manual-driver-note">
                        Driver meals/accommodation may be customer's responsibility when needed.
                      </div>
                    ) : null}
                    <div><span>Payment Option</span><strong>{getPaymentOptionLabel(manualForm.paymentOption)}</strong></div>
                    <div><span>Invoice Amount Due</span><strong>{formatPeso(manualInvoiceAmount)}</strong></div>
                  </div>
                  <div className="manual-inline-status">
                    Manual bookings do not auto-apply customer promos. Use the discount field only when FleetX approves an adjustment.
                  </div>
                  {manualPreviewError ? (
                    <div className="manual-warning-note">{manualPreviewError}</div>
                  ) : null}

                  <div className="manual-grid">
                    <label className="manual-field">
                      <span>Discount</span>
                      <input
                        className="input"
                        type="number"
                        min="0"
                        value={manualForm.discount}
                        onChange={(e) => updateManualForm("discount", e.target.value)}
                      />
                    </label>
                    {!isManualWithDriver && manualForm.vehicleHandoffOption === "delivery" ? (
                      <label className="manual-field">
                        <span>Delivery Fee Optional Override</span>
                        <input
                          className="input"
                          type="number"
                          min="0"
                          value={manualForm.deliveryFee}
                          onChange={(e) => updateManualForm("deliveryFee", e.target.value)}
                          placeholder="Leave blank for rules"
                        />
                        <small>Leave blank to use configured delivery fee rules.</small>
                      </label>
                    ) : null}
                    <label className="manual-field">
                      <span>Extra Fee</span>
                      <input
                        className="input"
                        type="number"
                        min="0"
                        value={manualForm.extraFee}
                        onChange={(e) => updateManualForm("extraFee", e.target.value)}
                      />
                    </label>
                    <label className="manual-field manual-field--full">
                      <span>Extra Charge Remarks</span>
                      <textarea
                        className="input manual-textarea"
                        value={manualForm.extraChargeRemarks}
                        onChange={(e) => updateManualForm("extraChargeRemarks", e.target.value)}
                      />
                    </label>
                    <label className="manual-field manual-field--full">
                      <span>Adjustment Notes / Reason</span>
                      <textarea
                        className="input manual-textarea"
                        value={manualForm.adjustmentReason}
                        onChange={(e) => updateManualForm("adjustmentReason", e.target.value)}
                      />
                    </label>
                  </div>
                </section>
              ) : null}

              {manualStep === 4 ? (
                <section className="manual-panel">
                  <div className="manual-panel-title">
                    <h3>Payment</h3>
                    <p>Record payment method, current status, and reference information.</p>
                  </div>

                  <div className="manual-grid">
                    <label className="manual-field">
                      <span>Payment Option</span>
                      <select
                        className="input"
                        value={manualForm.paymentOption}
                        onChange={(e) => updateManualForm("paymentOption", e.target.value)}
                      >
                        <option value="down_payment_50">50% Down Payment</option>
                        <option value="full_payment">Full Payment</option>
                      </select>
                    </label>
                    <label className="manual-field">
                      <span>Payment Method</span>
                      <select
                        className="input"
                        value={manualForm.paymentMethod}
                        onChange={(e) => updateManualForm("paymentMethod", e.target.value)}
                      >
                        <option>Cash</option>
                        <option>GCash</option>
                        <option>Online Banking / Bank Transfer</option>
                        <option>Bank Deposit</option>
                        <option>Other</option>
                      </select>
                    </label>
                    <label className="manual-field">
                      <span>Payment Status</span>
                      <select
                        className="input"
                        value={manualForm.paymentStatus}
                        onChange={(e) => updateManualForm("paymentStatus", e.target.value)}
                      >
                        <option value="unpaid">Unpaid</option>
                        <option value="downpayment_paid">Downpayment Paid</option>
                        <option value="fully_paid">Fully Paid</option>
                      </select>
                    </label>
                    <label className="manual-field">
                      <span>Amount Paid</span>
                      <input
                        className="input"
                        type="number"
                        min="0"
                        max={manualTotalPrice}
                        value={manualForm.amountPaid}
                        onChange={(e) => updateManualForm("amountPaid", e.target.value)}
                      />
                    </label>
                    <label className="manual-field">
                      <span>Remaining Balance</span>
                      <input className="input" value={formatPeso(manualRemainingBalance)} disabled />
                    </label>
                    <label className="manual-field manual-field--full">
                      <span>Proof / Reference Number Optional</span>
                      <input
                        className="input"
                        value={manualForm.paymentReference}
                        onChange={(e) => updateManualForm("paymentReference", e.target.value)}
                      />
                    </label>
                  </div>
                </section>
              ) : null}

              {manualStep === 5 ? (
                <section className="manual-panel">
                  <div className="manual-panel-title">
                    <h3>Confirm Booking</h3>
                    <p>Review the complete manual booking before creating it.</p>
                  </div>

                  <div className="manual-review-grid">
                    <div><span>Customer</span><strong>{manualForm.fullName}</strong></div>
                    <div><span>Email</span><strong>{manualForm.email}</strong></div>
                    <div><span>Contact</span><strong>{manualBookingContact}</strong></div>
                    <div>
                      <span>Vehicle</span>
                      <strong>{selectedManualVehicle ? getCompactVehicleLabel(selectedManualVehicle) : "N/A"}</strong>
                      {getVehicleDetailLine(selectedManualVehicle) ? (
                        <small className="booking-summary-meta">{getVehicleDetailLine(selectedManualVehicle)}</small>
                      ) : null}
                    </div>
                    <div><span>Dates</span><strong>{manualForm.startDate} {manualForm.startTime} to {manualForm.endDate} {manualForm.endTime}</strong></div>
                    <div><span>Rental Type</span><strong>{isManualWithDriver ? "With Driver" : "Self Drive"}</strong></div>
                    <div><span>Pickup / Handoff</span><strong>{manualBookingPickupLocation}</strong></div>
                    <div><span>Destination</span><strong>{manualForm.destination}</strong></div>
                    <div><span>Luggage</span><strong>{formatLuggageDetails(manualForm)}</strong></div>
                    <div><span>Payment Option</span><strong>{getPaymentOptionLabel(manualForm.paymentOption)}</strong></div>
                    <div><span>Payment</span><strong>{prettyStatus(manualForm.paymentStatus)}</strong></div>
                    <div><span>Rental Subtotal</span><strong>{formatPeso(manualBasePrice)}</strong></div>
                    <div><span>Promo / Discount</span><strong>{manualDiscount > 0 ? `-${formatPeso(manualDiscount)}` : "Not applied"}</strong></div>
                    <div><span>Delivery Fee</span><strong>{formatPeso(manualDeliveryFee)}</strong></div>
                    <div><span>Extra Charges</span><strong>{formatPeso(manualExtraFee)}</strong></div>
                    <div><span>Total</span><strong>{formatPeso(manualTotalPrice)}</strong></div>
                    <div><span>Remaining</span><strong>{formatPeso(manualRemainingBalance)}</strong></div>
                  </div>
                </section>
              ) : null}
            </div>

            <div className="manual-booking-footer">
              <button
                type="button"
                className="btn btn-soft"
                onClick={() => setManualStep((prev) => Math.max(0, prev - 1))}
                disabled={manualStep === 0 || manualSubmitting}
              >
                Previous
              </button>

              {manualStep < manualSteps.length - 1 ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    if (manualStep === 1) {
                      const errs = validateAdminSchedule({
                        startDate: manualForm.startDate,
                        startTime: manualForm.startTime,
                        endDate: manualForm.endDate,
                        endTime: manualForm.endTime,
                      });
                      if (Object.keys(errs).length > 0) {
                        setManualScheduleErrors(errs);
                        return;
                      }
                      setManualScheduleErrors({});
                    }
                    setManualStep((prev) => Math.min(manualSteps.length - 1, prev + 1));
                  }}
                  disabled={!manualStepIsValid() || manualSubmitting}
                >
                  Next
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={submitManualBooking}
                  disabled={!manualStepIsValid() || manualSubmitting}
                >
                  {manualSubmitting ? "Creating..." : "Create Manual Booking"}
                </button>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {assignmentConflict ? (
        <div className="modal-overlay assignment-feedback-overlay" onClick={() => setAssignmentConflict(null)}>
          <div className="assignment-feedback-modal assignment-feedback-modal--conflict" onClick={(event) => event.stopPropagation()}>
            <span className="assignment-feedback-kicker">Assignment blocked</span>
            <h3>Cannot Assign Driver</h3>
            <p>{assignmentConflict.message}</p>
            {assignmentConflict.driver ? (
              <div className="assignment-feedback-driver">
                <strong>{assignmentConflict.driver.fullName}</strong>
                <span>{assignmentConflict.driver.contactNumber || "No contact number"}</span>
              </div>
            ) : null}
            <div className="assignment-feedback-actions">
              <button type="button" className="btn btn-soft" onClick={() => setAssignmentConflict(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {assignmentConfirm ? (
        <div className="modal-overlay assignment-feedback-overlay" onClick={() => setAssignmentConfirm(null)}>
          <div className="assignment-feedback-modal" onClick={(event) => event.stopPropagation()}>
            <span className="assignment-feedback-kicker">
              {assignmentConfirm.smart ? "Smart assignment suggestion" : "Ready to assign"}
            </span>
            <h3>Confirm Driver Assignment</h3>
            <p>
              Assign <strong>{assignmentConfirm.driver.fullName}</strong> to{" "}
              <strong>{selectedBooking?.bookingReference || "this booking"}</strong> for{" "}
              {formatDate(selectedBooking?.startDate)} to {formatDate(selectedBooking?.endDate)}.
            </p>
            <div className="assignment-feedback-driver">
              <strong>{assignmentConfirm.driver.fullName}</strong>
              <span>
                Available for selected dates · {assignmentConfirm.driver.assignmentCount || 0} assignment(s)
              </span>
            </div>
            <div className="assignment-feedback-actions">
              <button
                type="button"
                className="btn btn-soft"
                onClick={() => setAssignmentConfirm(null)}
                disabled={isSubmittingAction}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={async () => {
                  const driverId = assignmentConfirm.driver._id;
                  setAssignmentConfirm(null);
                  await assignSelectedDriver(driverId);
                }}
                disabled={isSubmittingAction}
              >
                {isSubmittingAction ? "Assigning..." : "Confirm Assignment"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </AdminShell>
  );
}
